/**
 * ===========================================================================
 *  NUSANTARA WARRIOR — PROCEDURAL CLIP LIBRARY
 * ===========================================================================
 *
 * Every animation in the game is hand-authored HERE, as data, in degrees and
 * normalised time. Nothing is imported; there is no .fbx, no .glb, no baked
 * curve blob. A clip is a set of per-bone rotation tracks (plus optional
 * position tracks on `root`/`hips`) sampled with a Catmull-Rom spline, which
 * is what gives the poses their overshoot and follow-through for free.
 *
 * ---------------------------------------------------------------------------
 * TRACK FORMAT
 * ---------------------------------------------------------------------------
 *   R: { boneName: [ t, rx, ry, rz,   t, rx, ry, rz,  ... ] }
 *        t  = normalised time 0..1 (fraction of the clip's duration)
 *        rx = pitch  in DEGREES  (+ = bend forward / limb swings back)
 *        ry = twist  in DEGREES  (- = LEFT side forward, bladed stance)
 *        rz = roll   in DEGREES  (+ = lean right / LEFT limb abducts)
 *   T: { root|hips: [ t, x, y, z, ... ] }   METRES, rig-local, +Z forward
 *
 * Sign conventions come straight from `src/chars/rig.js` (identity bind pose,
 * arms hanging down, +Z forward):
 *     thigh.rx  < 0  leg swings FORWARD          shin.rx  > 0  knee flexes
 *     upperArm.rx < 0 arm swings FORWARD         foreArm.rx < 0 elbow flexes
 *     upperArm.rz > 0 LEFT arm abducts (use SIDE_SIGN to mirror)
 *     foot.rx   > 0  toes point down             head.rx  > 0  looks down
 *     chest.ry  < 0  right shoulder winds BACK (a right-handed wind-up)
 *
 * ---------------------------------------------------------------------------
 * EVENT SCHEMA  — consumed by combat / vfx / audio
 * ---------------------------------------------------------------------------
 * `world.anim.events(name)` returns a frozen array of markers, sorted by time,
 * with `t` already converted to SECONDS from the start of the clip:
 *
 *   { t, type:'hitboxOn',  arc, reach, height, power, dmg, launch?, gb? }
 *        Open a melee hitbox. `arc` = half-angle in degrees around the
 *        character's facing, `reach` = metres from the pelvis, `height` =
 *        centre height in metres, `power` = 0..1 impact strength for
 *        world.impact.hit(), `dmg` = damage multiplier, `launch` = launches,
 *        `gb` = guard-breaks.
 *   { t, type:'hitboxOff' }                  close the hitbox
 *   { t, type:'foot', side:'L'|'R' }         foot plant: dust + step sfx
 *   { t, type:'swoosh', power }              weapon whoosh + trail intensity
 *   { t, type:'voice', tag }                 'effort'|'shout'|'pain'|'death'
 *   { t, type:'sfx',   name }                one-shot synthesised sound
 *   { t, type:'vfx',   name, ... }           one-shot effect spawn
 *   { t, type:'cancel' }                     earliest frame this move may be
 *                                            cancelled into the next combo hit
 *   { t, type:'rootLock', on:0|1 }           freeze/unfreeze root motion
 *   { t, type:'ragdoll' }                    hand the body to physics
 *   { t, type:'land' }                       airborne clip touched down
 *
 * Anything unknown must be ignored, never crash. New types are appended, never
 * renamed.
 * ===========================================================================
 */
import { BONE_INDEX, BONE_COUNT, RIG } from '../chars/rig.js';
import { makeQ, makeM, eulerToQuat, poseToMatrices } from './pose.js';
import { compile, sampleChannel } from './curve.js';
import { legCycle, bob } from './gait.js';

export { sampleChannel };

const D = Math.PI / 180;

/* =========================================================================
 *  SHOULDER CLEARANCE
 * =========================================================================
 * The Dynasty Warriors torso is a WEDGE: the cuirass is wider at the chest
 * (half-width ~0.19 m on a 1.75 m grunt) than the shoulder JOINT is far from
 * the spine (~0.16 m). An upper arm authored hanging straight down therefore
 * already has two thirds of its diameter inside the breastplate, and this
 * library was written with the abduction sign inverted on top of that — every
 * `upperArm*` rz pulled the arm INTO the ribs by another 8-34 degrees. The
 * measured result was an arm whose outermost surface sat 2-7 cm INSIDE the
 * torso hull: the whole limb was swallowed, the character rendered with a
 * pauldron, a floating hand and nothing in between, and the cast shadow came
 * out as a splayed star instead of a human.
 *
 * Rather than re-author 68 hand-tuned keys (and lose the per-clip character
 * in them), the abduction is solved at sample time. It is a ONE-SIDED pull
 * towards ABD_TARGET: an arm tucked into the ribs is opened until the limb
 * clears the cuirass, an arm already thrown wide is not touched at all, and
 * ABD_RETAIN keeps a quarter of whatever tuck the animator wrote so the clips
 * still differ from one another. The correction also fades out as the arm
 * pitches away from the body, because an arm swung forward, back or overhead
 * is already clear of the torso in Z and must keep exactly the angle it was
 * given (attack arcs depend on it).
 *
 * Measured on a 1.75 m grunt, the outermost surface of the upper arm went from
 * 2-7 cm INSIDE the torso hull to 4.6-7.3 cm outside it across every idle and
 * locomotion clip. See tools/_tmp for the probes.
 *
 *   rz > 0 abducts the LEFT arm, rz < 0 abducts the RIGHT  (see rig.js)
 * ------------------------------------------------------------------------- */
const ABD_TARGET = 26 * D;     // abduction a hanging arm is pulled TOWARDS
const ABD_RETAIN = 0.25;       // how much of the authored tuck survives the pull
const ABD_MAX = 34 * D;        // hard cap: no pose may be rotated further than this
const ABD_HOLD = 38 * D;       // |pitch| below which the full correction applies
const ABD_FADE = 62 * D;       // ...falling to ABD_TAIL over this much more
const ABD_TAIL = 0.30;         // raised/swung arms keep 30% of it
const _ABD_I = [BONE_INDEX.upperArmL * 3, BONE_INDEX.upperArmR * 3];

/** Local rz the last shoulderClearance() added, per side [L, R]. Consumed by
 *  wristCompensate() — see below. Valid until the next sampleClip(). */
export const ABD_DELTA = new Float32Array(2);

/** In-place: guarantee both upper arms clear the cuirass. No allocation. */
export function shoulderClearance(e) {
  for (let s = 0; s < 2; s++) {
    const o = _ABD_I[s];
    const sign = s === 0 ? 1 : -1;
    const rx = e[o] < 0 ? -e[o] : e[o];
    let k = 1 - (rx - ABD_HOLD) / ABD_FADE;
    k = k > 1 ? 1 : (k < ABD_TAIL ? ABD_TAIL : k);
    let abd = e[o + 2] * sign;
    // One-sided pull. An arm the animator already threw wide — an attack that
    // opens the chest, a death that flings it out — is left exactly where it
    // was; only arms tucked in towards the ribs are moved, and never by more
    // than ABD_MAX, so a wind-up that crosses the body still crosses it.
    const target = ABD_TARGET * k;
    if (abd < target) {
      let d = (target - abd) * (1 - ABD_RETAIN);
      const cap = ABD_MAX * k;
      if (d > cap) d = cap;
      abd += d;
    }
    const rz = abd * sign;
    ABD_DELTA[s] = rz - e[o + 2];
    e[o + 2] = rz;
  }
}

/* ---------------------------------------------------------------- wrist ---
 * Abducting the shoulder swings everything below it, so the fist — and the
 * weapon rigid-bound to it — rotates by the same angle, and a field of upright
 * spears would lean outward by 30 degrees. The wrist absorbs it exactly.
 *
 * A local rz delta on the upper arm is a RIGHT multiply on its local rotation
 * (`Rx Ry Rz(rz+d) == Rx Ry Rz(rz) Rz(d)`), so the whole chain below picks up
 * one extra factor and the cancelling term on the hand is a conjugation:
 *
 *   handLocal' = foreArm^-1 * Rz(-d) * foreArm * handLocal
 *
 * which leaves the hand's WORLD orientation exactly as authored while its
 * position still travels outward with the arm. Runs on the quaternion buffer
 * because that is the only place the forearm's rotation exists as a rotation
 * rather than three Euler numbers. No allocation.
 *
 * Call it immediately after `eulerToQuat()` on a buffer that came from
 * `sampleClip()` — both the VAT bake and the hero pose do.
 */
const _WRIST = [
  [BONE_INDEX.foreArmL * 4, BONE_INDEX.handL * 4],
  [BONE_INDEX.foreArmR * 4, BONE_INDEX.handR * 4],
];
const _wq = new Float64Array(8);

export function wristCompensate(q) {
  for (let s = 0; s < 2; s++) {
    const d = ABD_DELTA[s];
    if (d < 1e-5 && d > -1e-5) continue;
    const fo = _WRIST[s][0], ho = _WRIST[s][1];
    const fx = q[fo], fy = q[fo + 1], fz = q[fo + 2], fw = q[fo + 3];
    // t = Rz(-d) * foreArm
    const sz = Math.sin(-d * 0.5), cz = Math.cos(d * 0.5);
    _wq[0] = cz * fx - sz * fy;
    _wq[1] = cz * fy + sz * fx;
    _wq[2] = cz * fz + sz * fw;
    _wq[3] = cz * fw - sz * fz;
    // c = foreArm^-1 * t   (foreArm is unit, so inverse is the conjugate)
    const ix = -fx, iy = -fy, iz = -fz, iw = fw;
    _wq[4] = iw * _wq[0] + ix * _wq[3] + iy * _wq[2] - iz * _wq[1];
    _wq[5] = iw * _wq[1] + iy * _wq[3] + iz * _wq[0] - ix * _wq[2];
    _wq[6] = iw * _wq[2] + iz * _wq[3] + ix * _wq[1] - iy * _wq[0];
    _wq[7] = iw * _wq[3] - ix * _wq[0] - iy * _wq[1] - iz * _wq[2];
    // hand = c * hand
    const cx = _wq[4], cy = _wq[5], czz = _wq[6], cw = _wq[7];
    const hx = q[ho], hy = q[ho + 1], hz = q[ho + 2], hw = q[ho + 3];
    q[ho] = cw * hx + cx * hw + cy * hz - czz * hy;
    q[ho + 1] = cw * hy + cy * hw + czz * hx - cx * hz;
    q[ho + 2] = cw * hz + czz * hw + cx * hy - cy * hx;
    q[ho + 3] = cw * hw - cx * hx - cy * hy - czz * hz;
  }
}


/* ------------------------------------------------------------------ helpers */

/** A single static key for every bone in `o` — the base of a stance. */
export function pose(o) {
  const r = {};
  for (const k in o) { const v = o[k]; r[k] = [0, v[0] || 0, v[1] || 0, v[2] || 0]; }
  return r;
}
/** Mirror a track left<->right: pitch keeps, twist and roll flip. */
function mir(tr) {
  const o = tr.slice();
  for (let i = 0; i < o.length; i += 4) { o[i + 2] = -o[i + 2]; o[i + 3] = -o[i + 3]; }
  return o;
}
/** Phase-shift a cyclic track by `dt` (0..1), wrapping and re-sorting. */
function shift(tr, dt) {
  const keys = [];
  for (let i = 0; i < tr.length; i += 4) {
    let t = tr[i] + dt; t -= Math.floor(t);
    keys.push([t, tr[i + 1], tr[i + 2], tr[i + 3]]);
  }
  keys.sort((a, b) => a[0] - b[0]);
  const out = [];
  for (const k of keys) out.push(k[0], k[1], k[2], k[3]);
  return out;
}
/** Merge track dicts; later ones win per-bone. */
function on(...dicts) { return Object.assign({}, ...dicts); }
const ev = (t, type, o) => Object.assign({ t, type }, o);

/* ------------------------------------------------------------- compilation */

const DEFS = [];
/**
 * Declare a clip.
 * @param {string} name
 * @param {number} dur seconds
 * @param {object} o { loop, blend, speed, rm, R, T, events, tags }
 *   speed : metres/second this cycle is authored for (locomotion clips only)
 *   rm    : forward metres travelled over the whole clip (root motion)
 */
function clip(name, dur, o) { DEFS.push(Object.assign({ name, dur }, o)); }

/* =========================================================================
 *  STANCES — reusable base poses. Everything else is written as a delta of
 *  one of these, which is what keeps the library readable.
 * ========================================================================= */

/** Relaxed "at ease", weapon shouldered. Used by idle / walk / celebrate. */
const EASE = pose({
  hips: [3, -5, 0], spine1: [3, -3, 0], spine2: [-2, -3, 0], chest: [-4, -5, 0],
  neck: [5, 5, 0], head: [-3, 6, 0],
  clavicleL: [0, -3, -7], upperArmL: [-14, -12, -13], foreArmL: [-40, 14, -6], handL: [-10, 0, 0],
  clavicleR: [0, 3, 7], upperArmR: [-10, 14, 13], foreArmR: [-48, -16, 6], handR: [-8, 0, 0],
  thighL: [-3, 5, 5], shinL: [5, 0, -1], footL: [-2, 7, 0],
  thighR: [-4, -8, -6], shinR: [7, 0, 1], footR: [-2, -10, 0],
});

/** Combat guard: bladed stance, weapon carried across the body. */
const GUARD = pose({
  hips: [4, -14, 0], spine1: [3, -5, 0], spine2: [-1, -6, 1], chest: [-3, -8, 1],
  neck: [6, 12, 0], head: [-4, 14, 0],
  clavicleL: [2, -6, -10], upperArmL: [-34, -16, -8], foreArmL: [-62, 20, -4], handL: [-14, 6, 0],
  clavicleR: [2, 6, 10], upperArmR: [-16, 20, 6], foreArmR: [-78, -22, 6], handR: [-10, -6, 0],
  thighL: [-14, 10, 7], shinL: [22, 0, -1], footL: [-6, 12, 0],
  thighR: [-19, -16, -9], shinR: [29, 0, 1], footR: [-8, -18, 0],
});

/** Airborne / hurt rag-doll-ish base: limbs loose and trailing. */
const LOOSE = pose({
  hips: [-7, 0, 0], spine1: [-4, 0, 0], spine2: [-5, 0, 0], chest: [-7, 0, 0],
  neck: [-6, 0, 0], head: [-7, 0, 0],
  clavicleL: [0, 0, -18], upperArmL: [30, -10, 34], foreArmL: [-46, 0, 0], handL: [-14, 0, 0],
  clavicleR: [0, 0, 18], upperArmR: [34, 10, -30], foreArmR: [-40, 0, 0], handR: [-12, 0, 0],
  thighL: [-26, 4, 10], shinL: [44, 0, 0], footL: [16, 6, 0],
  thighR: [-16, -4, -12], shinR: [58, 0, 0], footR: [12, -6, 0],
});

/* =========================================================================
 *  LOCOMOTION
 * ========================================================================= */

/* --- idle: weight shifting, breathing, weapon settling ------------------- */
clip('idle', 3.4, {
  loop: true, blend: 0.28, tags: 'loco', fps: 16,
  R: on(EASE, {
    hips: [0, 3, -5, -2, 0.25, 3, -3, 1, 0.5, 3, -6, 2, 0.75, 3, -4, -1],
    spine1: [0, 3, -3, 1, 0.5, 4, -2, -1],
    spine2: [0, -1, -3, 1, 0.28, -3, -4, 0, 0.5, -1, -2, -1, 0.78, -3, -3, 0],
    chest: [0, -3, -5, 1, 0.28, -6, -6, 0, 0.5, -3, -4, -1, 0.78, -6, -5, 0],
    neck: [0, 5, 5, 0, 0.3, 6, 8, 0, 0.62, 4, 1, 0],
    head: [0, -3, 6, 1, 0.3, -1, 10, 0, 0.62, -4, 0, -1, 0.85, -2, 7, 1],
    upperArmL: [0, -14, -12, -13, 0.5, -17, -13, -11, 0.75, -12, -11, -14],
    foreArmL: [0, -40, 14, -6, 0.5, -44, 16, -6],
    upperArmR: [0, -10, 14, 13, 0.35, -13, 15, 12, 0.7, -8, 13, 14],
    foreArmR: [0, -48, -16, 6, 0.35, -52, -17, 6, 0.7, -46, -15, 6],
    thighL: [0, -3, 5, 5, 0.5, -5, 5, 6],
    thighR: [0, -4, -8, -6, 0.5, -2, -8, -5],
  }),
  T: { root: [0, 0, 0, 0, 0.28, 0, 0.012, 0, 0.5, 0, 0.002, 0, 0.78, 0, 0.014, 0] },
});

/* --- idle_b: the same soldier, a different man ---------------------------
 * A crowd where every unit shares ONE idle reads as one mesh repeated, no
 * matter how well the phases are spread — the pose itself has to differ. This
 * is the second standing stance: weight dumped onto the right leg, hip cocked,
 * left hand on the belt, weapon shouldered. Units pick one of the two by their
 * deterministic battle-local spawn key
 * (see IDLES in animation.js). Baked at 16 Hz: nothing in an idle moves fast.
 */
clip('idle_b', 3.0, {
  loop: true, blend: 0.28, tags: 'loco', fps: 16,
  R: on(EASE, {
    hips: [0, 2, -2, 6, 0.3, 3, -1, 7, 0.62, 2, -3, 6, 0.85, 3, -2, 7],
    spine1: [0, 3, 2, -3, 0.5, 4, 3, -4],
    spine2: [0, -2, 2, -2, 0.5, -3, 3, -2],
    chest: [0, -4, 3, -2, 0.5, -5, 4, -3],
    neck: [0, 4, -6, 1, 0.35, 5, -11, 1, 0.72, 4, 2, 0],
    head: [0, -4, -8, 1, 0.35, -2, -15, 1, 0.72, -5, 3, 0],
    clavicleL: [0, 2, -4, -8],
    upperArmL: [0, -10, -20, -12, 0.5, -12, -21, -11],
    foreArmL: [0, -74, 40, -10, 0.5, -77, 42, -10],
    handL: [0, -6, 10, 0],
    clavicleR: [0, 0, 3, 8],
    upperArmR: [0, -22, 10, 16, 0.4, -25, 11, 15, 0.8, -20, 9, 17],
    foreArmR: [0, -96, -20, 4, 0.4, -101, -21, 4],
    handR: [0, -6, -4, 0],
    thighL: [0, -1, 9, 8, 0.5, -2, 9, 8],
    shinL: [0, 3, 0, -2, 0.5, 4, 0, -2],
    footL: [0, -2, 11, 0],
    thighR: [0, -6, -6, -4, 0.5, -5, -6, -4],
    shinR: [0, 5, 0, 1, 0.5, 6, 0, 1],
    footR: [0, -1, -8, 0],
  }),
  T: { root: [0, 0, -0.004, 0, 0.5, 0, 0.004, 0] },
});

/* --- idle_c: spear grounded, both hands on the haft ----------------------
 * A third standing stance. Two idles is not enough vocabulary for a five
 * hundred man line: with two, half the field is always in the same silhouette
 * and the mass still reads as one mesh repeated no matter how the phases are
 * spread. This one is deliberately the odd shape of the three — feet wide, the
 * weapon butt planted, both hands stacked on the haft in front of the body,
 * head panning across the field — so a rank mixes three distinct outlines.
 */
clip('idle_c', 3.8, {
  loop: true, blend: 0.28, tags: 'loco', fps: 16,
  R: on(EASE, {
    hips: [0, 6, 2, -1, 0.3, 7, 4, -2, 0.62, 6, 0, 0, 0.85, 7, 3, -1],
    spine1: [0, 5, 1, 0, 0.5, 6, -1, 0],
    spine2: [0, -2, 1, 0, 0.5, -3, -1, 0],
    chest: [0, -5, 2, 0, 0.5, -6, -2, 0],
    neck: [0, 6, -10, 0, 0.32, 7, 14, 1, 0.68, 6, -16, -1],
    head: [0, -2, -14, 0, 0.32, -1, 20, 1, 0.68, -3, -22, -1],
    clavicleL: [0, 4, -2, -4], clavicleR: [0, 4, 2, 4],
    upperArmL: [0, -30, -10, -6, 0.5, -33, -11, -6],
    foreArmL: [0, -54, 18, -4, 0.5, -57, 19, -4],
    handL: [0, -16, 4, 0],
    upperArmR: [0, -24, 12, 6, 0.45, -27, 13, 6],
    foreArmR: [0, -46, -18, 4, 0.45, -49, -19, 4],
    handR: [0, -14, -4, 0],
    thighL: [0, -2, 12, 9, 0.5, -3, 12, 9],
    shinL: [0, 4, 0, -2, 0.5, 5, 0, -2],
    footL: [0, -2, 14, 0],
    thighR: [0, -2, -12, -9, 0.5, -1, -12, -9],
    shinR: [0, 4, 0, 2, 0.5, 5, 0, 2],
    footR: [0, -2, -14, 0],
  }),
  T: { root: [0, 0, -0.014, 0, 0.3, 0, -0.006, 0, 0.62, 0, -0.016, 0, 0.85, 0, -0.007, 0] },
});

/* --- idle_alert: guard up, bouncing on the balls of the feet ------------- */
clip('idle_alert', 2.0, {
  loop: true, blend: 0.2, tags: 'loco', fps: 20,
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.25, 6, -12, -1, 0.5, 4, -15, 0, 0.75, 6, -13, 1],
    chest: [0, -3, -8, 1, 0.25, -6, -9, 1, 0.5, -3, -7, 1, 0.75, -6, -8, 1],
    head: [0, -4, 14, 0, 0.33, -2, 17, 1, 0.66, -6, 11, -1],
    upperArmR: [0, -16, 20, 6, 0.25, -20, 21, 6, 0.5, -15, 19, 7, 0.75, -19, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.25, -84, -23, 6, 0.5, -77, -21, 6, 0.75, -83, -22, 6],
    upperArmL: [0, -34, -16, -8, 0.5, -38, -17, -8],
    foreArmL: [0, -62, 20, -4, 0.5, -67, 21, -4],
    thighL: [0, -6, 10, 6, 0.25, -10, 10, 6, 0.5, -6, 10, 6, 0.75, -10, 10, 6],
    thighR: [0, -10, -16, -8, 0.25, -14, -16, -8, 0.5, -10, -16, -8, 0.75, -14, -16, -8],
    shinL: [0, 10, 0, -1, 0.25, 16, 0, -1, 0.5, 10, 0, -1, 0.75, 16, 0, -1],
    shinR: [0, 16, 0, 1, 0.25, 22, 0, 1, 0.5, 16, 0, 1, 0.75, 22, 0, 1],
  }),
  T: { root: [0, 0, 0.02, 0, 0.25, 0, -0.01, 0, 0.5, 0, 0.02, 0, 0.75, 0, -0.01, 0] },
});

/* -------------------------------------------------------------------------
 * THE LOCOMOTION LADDER
 *
 * The legs of walk / run / sprint are NOT hand-keyed. Each gait declares the
 * two numbers that decide whether a character skates — how long the foot is
 * planted (`contact`, a fraction of the cycle) and how far the planted ankle
 * travels backwards through the body while it is (`E`, metres) — and gait.js
 * solves the thigh/shin/foot angles that put the ankle exactly there. The
 * declared stride is E / contact by construction, so the distance the body
 * covers during stance always equals the distance the foot travels back.
 *
 * Keep `contact` monotonic down the ladder (a walk is planted longer than a
 * sprint) and keep touch-down at phase 0 on the LEFT foot in every clip, or
 * the cross-fades between them stop lining up.
 * ------------------------------------------------------------------------- */

/* --- walk --------------------------------------------------------------- */
const WALK_G = { dur: 0.74, contact: 0.47, E: 0.60, front: 0.45 };
/*
 * The hips track is [t, pitch, yaw, roll] and a mirrored gait requires
 * yaw(t+0.5) = -yaw(t). The half keys were negated; the QUARTER keys were not,
 * in all three locomotion clips, so the pelvis carried a constant yaw bias
 * that never cancelled: mean (6 + 2 - 6 + 2) / 4 = +1.0 deg on walk, +1.5 on
 * run, +2.0 on sprint. That is the whole of the right-foot slide asymmetry
 * that survived r3, r4 and r5 -- a permanently yawed pelvis rides the right
 * ankle 38 mm lower than the left (mean 0.251 m against 0.289, and 26 frames
 * below 5 cm against 1), so `down` in ik.js plantFeet saturates earlier and
 * longer on that side, the lock is asked to move the foot 2.4x further
 * (wantR 0.103 m against wantL 0.043) and the solver misses 3x more
 * (missR 0.027 against missL 0.009). Two rounds of release-curve tuning were
 * aimed at the lock; the lock was doing its job on a pose that was leaning.
 */
const WALK_HIPS = [0, 4, 6, -3, 0.25, 5, 2, 4, 0.5, 4, -6, 3, 0.75, 5, -2, -4];
const WALK_ROOT = bob(-0.020, 0.014, WALK_G.contact);
const WALK_LEG = legCycle({
  contact: WALK_G.contact, E: WALK_G.E, front: WALK_G.front,
  hips: WALK_HIPS, root: WALK_ROOT,
  yStrike: 0.088, yMid: 0.079, yToe: 0.150,
  swing: [[0, 0.150], [0.22, 0.21], [0.5, 0.20], [0.78, 0.155], [1, 0.088]],
  pitch: [[0, -9], [0.16, 0], [0.4, 5], [WALK_G.contact, 22], [1, -9]],
  ankle: [[0, 12], [0.3, 0], [0.7, -5], [1, -9]],
  splay: [5, 5], keys: 24,
});
clip('walk', WALK_G.dur, {
  loop: true, blend: 0.2, tags: 'loco', fps: 90,
  speed: WALK_G.E / WALK_G.contact / WALK_G.dur, contact: WALK_G.contact, excursion: WALK_G.E,
  events: [ev(0.01, 'foot', { side: 'L' }), ev(0.51, 'foot', { side: 'R' })],
  R: on(EASE, {
    hips: WALK_HIPS,
    spine1: [0, 4, 3, -1, 0.5, 4, -3, 1],
    spine2: [0, -1, -5, 1, 0.5, -1, 5, -1],
    chest: [0, -3, -8, 2, 0.5, -3, 8, -2],
    neck: [0, 5, 4, -1, 0.5, 5, -4, 1],
    head: [0, -3, 5, 0, 0.5, -3, -3, 0],
    clavicleL: [0, 0, -3, -7], clavicleR: [0, 0, 3, 7],
    upperArmL: [0, 24, -8, -11, 0.24, 8, -10, -12, 0.5, -28, -12, -13, 0.76, 6, -10, -12],
    foreArmL: [0, -30, 10, -5, 0.25, -40, 12, -5, 0.5, -56, 14, -6, 0.75, -40, 12, -5],
    upperArmR: [0, -20, 15, 12, 0.25, -14, 14, 13, 0.5, -8, 12, 14, 0.75, -14, 14, 13],
    foreArmR: [0, -54, -17, 6, 0.5, -44, -15, 6],
    handR: [0, -14, 8, 0],
    thighL: WALK_LEG.thigh, shinL: WALK_LEG.shin, footL: WALK_LEG.foot,
    thighR: shift(mir(WALK_LEG.thigh), 0.5), shinR: shift(mir(WALK_LEG.shin), 0.5), footR: shift(mir(WALK_LEG.foot), 0.5),
  }),
  T: { root: WALK_ROOT },
});

/* --- run ---------------------------------------------------------------- */
const RUN_G = { dur: 0.62, contact: 0.275, E: 0.745, front: 0.43 };
const RUN_HIPS = [0, 10, 9, -5, 0.25, 12, 3, 6, 0.5, 10, -9, 5, 0.75, 12, -3, -6];
const RUN_ROOT = bob(-0.046, 0.024, RUN_G.contact);
const RUN_LEG = legCycle({
  contact: RUN_G.contact, E: RUN_G.E, front: RUN_G.front,
  hips: RUN_HIPS, root: RUN_ROOT,
  yStrike: 0.098, yMid: 0.084, yToe: 0.170,
  swing: [[0, 0.170], [0.17, 0.36], [0.36, 0.42], [0.56, 0.36], [0.78, 0.25], [0.9, 0.19], [1, 0.098]],
  pitch: [[0, -13], [0.1, -1], [0.22, 6], [RUN_G.contact, 32], [1, -13]],
  ankle: [[0, 16], [0.28, 2], [0.62, -6], [1, -13]],
  splay: [4, 4], keys: 26,
});
clip('run', RUN_G.dur, {
  loop: true, blend: 0.16, tags: 'loco', fps: 90,
  speed: RUN_G.E / RUN_G.contact / RUN_G.dur, contact: RUN_G.contact, excursion: RUN_G.E,
  events: [ev(0.01, 'foot', { side: 'L' }), ev(0.51, 'foot', { side: 'R' })],
  R: on(EASE, {
    hips: RUN_HIPS,
    spine1: [0, 6, 4, -2, 0.5, 6, -4, 2],
    spine2: [0, 1, -7, 2, 0.5, 1, 7, -2],
    chest: [0, 1, -13, 3, 0.5, 1, 13, -3],
    neck: [0, 2, 6, -1, 0.5, 2, -6, 1],
    head: [0, -6, 7, 0, 0.5, -6, -5, 0],
    clavicleL: [0, 2, -4, -10], clavicleR: [0, 2, 4, 10],
    upperArmL: [0, 44, -6, -14, 0.25, 12, -10, -14, 0.5, -46, -14, -14, 0.75, 10, -10, -14],
    foreArmL: [0, -66, 8, -4, 0.25, -84, 10, -4, 0.5, -96, 12, -4, 0.75, -84, 10, -4],
    upperArmR: [0, -26, 16, 14, 0.25, -16, 14, 15, 0.5, -6, 12, 16, 0.75, -16, 14, 15],
    foreArmR: [0, -58, -18, 6, 0.5, -44, -14, 6],
    handR: [0, -20, 14, 0],
    thighL: RUN_LEG.thigh, shinL: RUN_LEG.shin, footL: RUN_LEG.foot,
    thighR: shift(mir(RUN_LEG.thigh), 0.5), shinR: shift(mir(RUN_LEG.shin), 0.5), footR: shift(mir(RUN_LEG.foot), 0.5),
  }),
  T: { root: RUN_ROOT },
});

/* --- sprint ------------------------------------------------------------- */
const SPR_G = { dur: 0.54, contact: 0.205, E: 0.82, front: 0.41 };
const SPR_HIPS = [0, 15, 11, -6, 0.25, 18, 4, 7, 0.5, 15, -11, 6, 0.75, 18, -4, -7];
const SPR_ROOT = bob(-0.058, 0.026, SPR_G.contact);
const SPR_LEG = legCycle({
  contact: SPR_G.contact, E: SPR_G.E, front: SPR_G.front,
  hips: SPR_HIPS, root: SPR_ROOT,
  yStrike: 0.104, yMid: 0.086, yToe: 0.186,
  swing: [[0, 0.186], [0.15, 0.42], [0.34, 0.50], [0.54, 0.42], [0.76, 0.28], [0.9, 0.20], [1, 0.104]],
  pitch: [[0, -15], [0.09, -1], [0.18, 8], [SPR_G.contact, 38], [1, -15]],
  ankle: [[0, 20], [0.26, 2], [0.6, -8], [1, -15]],
  splay: [3, 3], keys: 26,
});
clip('sprint', SPR_G.dur, {
  loop: true, blend: 0.14, tags: 'loco', fps: 90,
  speed: SPR_G.E / SPR_G.contact / SPR_G.dur, contact: SPR_G.contact, excursion: SPR_G.E,
  events: [ev(0.01, 'foot', { side: 'L' }), ev(0.51, 'foot', { side: 'R' })],
  R: on(EASE, {
    hips: SPR_HIPS,
    spine1: [0, 8, 5, -2, 0.5, 8, -5, 2],
    spine2: [0, 3, -9, 3, 0.5, 3, 9, -3],
    chest: [0, 4, -16, 4, 0.5, 4, 16, -4],
    neck: [0, -4, 7, -1, 0.5, -4, -7, 1],
    head: [0, -14, 8, 0, 0.5, -14, -6, 0],
    clavicleL: [0, 3, -5, -12], clavicleR: [0, 3, 5, 12],
    upperArmL: [0, 56, -4, -16, 0.25, 14, -10, -16, 0.5, -58, -16, -16, 0.75, 12, -10, -16],
    foreArmL: [0, -74, 6, -3, 0.25, -96, 8, -3, 0.5, -108, 10, -3, 0.75, -96, 8, -3],
    upperArmR: [0, -30, 16, 16, 0.25, -18, 14, 17, 0.5, -4, 12, 18, 0.75, -18, 14, 17],
    foreArmR: [0, -62, -18, 6, 0.5, -44, -12, 6],
    handR: [0, -24, 18, 0],
    thighL: SPR_LEG.thigh, shinL: SPR_LEG.shin, footL: SPR_LEG.foot,
    thighR: shift(mir(SPR_LEG.thigh), 0.5), shinR: shift(mir(SPR_LEG.shin), 0.5), footR: shift(mir(SPR_LEG.foot), 0.5),
  }),
  T: { root: SPR_ROOT },
});


/* --- turns -------------------------------------------------------------- */
clip('turn_l', 0.52, {
  blend: 0.14,
  events: [ev(0.18, 'foot', { side: 'R' }), ev(0.62, 'foot', { side: 'L' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.35, 8, 26, -6, 0.7, 5, 4, 2, 1, 4, -14, 0],
    chest: [0, -3, -8, 1, 0.35, 0, 18, -4, 1, -3, -8, 1],
    head: [0, -4, 14, 0, 0.3, -2, 26, 0, 1, -4, 14, 0],
    thighL: [0, -6, 10, 6, 0.35, -30, 22, 8, 0.7, -8, 12, 6, 1, -6, 10, 6],
    shinL: [0, 10, 0, -1, 0.35, 46, 0, -1, 1, 10, 0, -1],
    thighR: [0, -10, -16, -8, 0.35, 10, -28, -8, 1, -10, -16, -8],
    shinR: [0, 16, 0, 1, 0.35, 30, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.35, 0, 0.03, 0, 1, 0, 0, 0] },
});
clip('turn_r', 0.52, {
  blend: 0.14,
  events: [ev(0.18, 'foot', { side: 'L' }), ev(0.62, 'foot', { side: 'R' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.35, 8, -46, 6, 0.7, 5, -24, -2, 1, 4, -14, 0],
    chest: [0, -3, -8, 1, 0.35, 0, -22, 4, 1, -3, -8, 1],
    head: [0, -4, 14, 0, 0.3, -2, -6, 0, 1, -4, 14, 0],
    thighR: [0, -10, -16, -8, 0.35, -32, -30, -10, 0.7, -12, -18, -8, 1, -10, -16, -8],
    shinR: [0, 16, 0, 1, 0.35, 50, 0, 1, 1, 16, 0, 1],
    thighL: [0, -6, 10, 6, 0.35, 12, 24, 6, 1, -6, 10, 6],
    shinL: [0, 10, 0, -1, 0.35, 28, 0, -1, 1, 10, 0, -1],
  }),
  T: { root: [0, 0, 0, 0, 0.35, 0, 0.03, 0, 1, 0, 0, 0] },
});

/* =========================================================================
 *  ATTACKS — a four-hit light chain with escalating body rotation, then the
 *  heavies. Every one of them lunges (root motion) the way DW's do.
 * ========================================================================= */

/* C1: flat sweep, right -> left. Small wind, fast blade, long follow-through. */
clip('attack_light_1', 0.54, {
  blend: 0.09, rm: 0.9, tags: 'attack',
  events: [ev(0.30, 'swoosh', { power: 0.6 }), ev(0.34, 'hitboxOn', { arc: 105, reach: 2.5, height: 1.15, power: 0.55, dmg: 1.0 }),
    ev(0.56, 'hitboxOff'), ev(0.58, 'cancel'), ev(0.22, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.26, 6, -34, -3, 0.44, 3, 16, 4, 0.66, 2, 30, 5, 1, 4, -8, 0],
    spine1: [0, 3, -5, 0, 0.26, 4, -14, -2, 0.44, 2, 8, 3, 0.66, 1, 14, 3, 1, 3, -3, 0],
    spine2: [0, -1, -6, 1, 0.26, 2, -18, -3, 0.44, -2, 10, 4, 0.66, -3, 16, 4, 1, -1, -4, 1],
    chest: [0, -3, -8, 1, 0.26, 4, -30, -6, 0.44, -4, 16, 7, 0.66, -6, 26, 8, 1, -3, -6, 1],
    neck: [0, 6, 12, 0, 0.26, 4, 20, 0, 0.5, 6, -4, 0, 1, 6, 10, 0],
    head: [0, -4, 14, 0, 0.26, -2, 26, 2, 0.5, -3, -6, -2, 1, -4, 12, 0],
    clavicleR: [0, 2, 6, 10, 0.26, 0, 16, -6, 0.44, 4, -8, 20, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.26, -4, 30, -42, 0.44, -48, -12, 40, 0.62, -40, -20, 52, 1, -18, 18, 6],
    foreArmR: [0, -78, -22, 6, 0.26, -52, -30, 6, 0.44, -26, -14, 4, 0.66, -46, -8, 2, 1, -76, -20, 6],
    handR: [0, -10, -6, 0, 0.26, -18, -14, 0, 0.44, 6, 8, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.44, 2, -14, -18, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.26, -22, -8, 20, 0.44, -52, -30, -34, 0.66, -42, -34, -40, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.26, -46, 14, -4, 0.44, -78, 26, -6, 1, -62, 20, -4],
    thighL: [0, -6, 10, 6, 0.26, -2, 20, 6, 0.5, -16, -4, 6, 1, -6, 10, 6],
    thighR: [0, -10, -16, -8, 0.26, -18, -28, -8, 0.5, 2, 4, -8, 1, -10, -16, -8],
    shinL: [0, 10, 0, -1, 0.5, 22, 0, -1, 1, 10, 0, -1],
    shinR: [0, 16, 0, 1, 0.26, 26, 0, 1, 0.5, 12, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.24, 0, 0.01, -0.06, 0.5, 0, -0.02, 0.55, 0.75, 0, 0, 0.86, 1, 0, 0, 0.9] },
});

/* C2: reverse sweep, left -> right, lower and faster. */
clip('attack_light_2', 0.5, {
  blend: 0.08, rm: 0.8, tags: 'attack',
  events: [ev(0.24, 'swoosh', { power: 0.65 }), ev(0.28, 'hitboxOn', { arc: 110, reach: 2.5, height: 0.95, power: 0.55, dmg: 1.05 }),
    ev(0.5, 'hitboxOff'), ev(0.54, 'cancel'), ev(0.18, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, 16, 3, 0.2, 6, 30, 4, 0.42, 5, -30, -5, 0.62, 4, -44, -6, 1, 4, -14, 0],
    spine1: [0, 3, 8, 2, 0.2, 4, 14, 2, 0.42, 3, -14, -3, 1, 3, -5, 0],
    spine2: [0, -1, 10, 3, 0.2, 2, 16, 3, 0.42, -1, -18, -4, 1, -1, -6, 1],
    chest: [0, -3, 16, 5, 0.2, 2, 26, 6, 0.42, -2, -28, -7, 0.62, -4, -38, -8, 1, -3, -8, 1],
    neck: [0, 6, -6, 0, 0.42, 6, 18, 0, 1, 6, 12, 0],
    head: [0, -4, -4, 0, 0.2, -2, -14, -2, 0.45, -2, 22, 2, 1, -4, 14, 0],
    clavicleR: [0, 4, -8, 20, 0.2, 4, -14, 24, 0.42, 0, 14, -8, 1, 2, 6, 10],
    upperArmR: [0, -40, -20, 52, 0.2, -52, -24, 58, 0.42, -26, 24, -44, 0.6, -14, 32, -54, 1, -16, 20, 6],
    foreArmR: [0, -46, -8, 2, 0.2, -34, -6, 2, 0.42, -30, -22, 8, 1, -78, -22, 6],
    handR: [0, 6, 8, 0, 0.42, -14, -12, 0, 1, -10, -6, 0],
    upperArmL: [0, -42, -34, -40, 0.2, -54, -36, -44, 0.42, -22, -6, 26, 1, -34, -16, -8],
    foreArmL: [0, -78, 26, -6, 0.42, -50, 10, -2, 1, -62, 20, -4],
    thighL: [0, -16, -4, 6, 0.24, -20, -12, 6, 0.5, -4, 18, 6, 1, -6, 10, 6],
    thighR: [0, 2, 4, -8, 0.24, 6, 12, -8, 0.5, -16, -26, -8, 1, -10, -16, -8],
    shinL: [0, 22, 0, -1, 0.5, 14, 0, -1, 1, 10, 0, -1],
    shinR: [0, 12, 0, 1, 0.5, 26, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.18, 0, 0.01, -0.04, 0.48, 0, -0.02, 0.5, 1, 0, 0, 0.8] },
});

/* C3: rising diagonal, steps in behind the blade. */
clip('attack_light_3', 0.62, {
  blend: 0.09, rm: 1.2, tags: 'attack',
  events: [ev(0.3, 'swoosh', { power: 0.75 }), ev(0.34, 'hitboxOn', { arc: 95, reach: 2.7, height: 1.35, power: 0.65, dmg: 1.15 }),
    ev(0.58, 'hitboxOff'), ev(0.66, 'cancel'), ev(0.24, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.24, 12, -40, -6, 0.46, -2, 18, 6, 0.68, -6, 30, 8, 1, 4, -12, 0],
    spine1: [0, 3, -5, 0, 0.24, 8, -16, -3, 0.46, -4, 10, 4, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.24, 6, -20, -4, 0.46, -8, 12, 5, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.24, 10, -34, -8, 0.46, -14, 20, 9, 0.68, -18, 30, 10, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.24, 10, 22, 0, 0.5, 2, -6, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.24, 6, 26, 2, 0.5, -10, -6, -2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.24, -4, 18, -8, 0.5, 2, -10, 24, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.24, 16, 26, -46, 0.48, -78, -18, 34, 0.66, -68, -26, 44, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.24, -40, -34, 8, 0.48, -18, -10, 2, 0.7, -40, -6, 0, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.24, -22, -16, 0, 0.48, 10, 10, 0, 1, -10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.24, -14, -6, 24, 0.48, -70, -32, -30, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.24, -40, 12, -4, 0.48, -86, 30, -8, 1, -62, 20, -4],
    thighL: [0, -6, 10, 6, 0.24, 4, 22, 6, 0.5, -30, -6, 6, 0.72, -12, 4, 6, 1, -6, 10, 6],
    shinL: [0, 10, 0, -1, 0.24, 8, 0, -1, 0.5, 40, 0, -1, 1, 10, 0, -1],
    thighR: [0, -10, -16, -8, 0.24, -26, -30, -8, 0.5, 12, 6, -8, 1, -10, -16, -8],
    shinR: [0, 16, 0, 1, 0.24, 36, 0, 1, 0.5, 12, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.22, 0, 0.02, -0.08, 0.52, 0, 0.03, 0.72, 0.8, 0, 0, 1.1, 1, 0, 0, 1.2] },
});

/* C4: the finisher — a full spin, two hitbox windows, big recovery. */
clip('attack_light_4', 0.92, {
  blend: 0.1, rm: 1.6, tags: 'attack',
  events: [ev(0.22, 'swoosh', { power: 0.9 }), ev(0.26, 'hitboxOn', { arc: 180, reach: 2.9, height: 1.1, power: 0.8, dmg: 1.4, gb: true }),
    ev(0.46, 'hitboxOff'), ev(0.5, 'swoosh', { power: 1.0 }),
    ev(0.54, 'hitboxOn', { arc: 180, reach: 3.0, height: 1.2, power: 0.95, dmg: 1.6, launch: true }),
    ev(0.72, 'hitboxOff'), ev(0.86, 'cancel'), ev(0.16, 'voice', { tag: 'shout' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.16, 8, -54, -6, 0.42, 2, 120, 8, 0.62, 0, 250, 6, 0.84, 4, 344, 0, 1, 4, 352, 0],
    spine1: [0, 3, -5, 0, 0.16, 6, -16, -2, 0.5, 0, 14, 4, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.16, 4, -18, -3, 0.5, -4, 16, 5, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.16, 8, -28, -6, 0.42, -8, 22, 8, 0.72, -6, 14, 6, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.5, 4, -8, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.16, 0, 30, 2, 0.5, -6, -18, -2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.16, -2, 20, 24, 0.5, 6, -12, -4, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.16, 6, 36, 34, 0.4, -34, -28, -64, 0.62, -26, -34, -70, 0.84, -24, 4, -22, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.16, -44, -34, 8, 0.4, -10, -6, 2, 0.7, -22, -4, 0, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.4, 22, 30, 0, 0.7, 16, 22, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.4, 4, -18, -24, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.16, -18, -4, -26, 0.4, -40, -34, 46, 0.62, -32, -38, 54, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.16, -34, 10, -4, 0.4, -80, 34, -8, 1, -62, 20, -4],
    thighL: [0, -6, 10, 6, 0.16, 2, 26, 6, 0.42, -26, -14, 8, 0.7, -8, 10, 6, 1, -6, 10, 6],
    shinL: [0, 10, 0, -1, 0.42, 46, 0, -1, 1, 10, 0, -1],
    thighR: [0, -10, -16, -8, 0.16, -22, -34, -8, 0.42, 8, 14, -8, 0.7, -18, -20, -8, 1, -10, -16, -8],
    shinR: [0, 16, 0, 1, 0.16, 34, 0, 1, 0.42, 14, 0, 1, 0.7, 40, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.14, 0, 0.02, -0.1, 0.46, 0, 0.02, 0.7, 0.72, 0, 0, 1.4, 1, 0, 0, 1.6] },
});

/* Heavy: overhead two-handed smash into the ground. */
clip('attack_heavy', 1.0, {
  blend: 0.12, rm: 1.1, tags: 'attack',
  events: [ev(0.44, 'swoosh', { power: 1.0 }), ev(0.48, 'hitboxOn', { arc: 60, reach: 2.8, height: 1.0, power: 1.0, dmg: 2.2, launch: true, gb: true }),
    ev(0.62, 'hitboxOff'), ev(0.52, 'vfx', { name: 'groundSlam' }), ev(0.88, 'cancel'), ev(0.2, 'voice', { tag: 'shout' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.3, -12, -22, -2, 0.52, 16, -4, 2, 0.7, 12, -8, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.3, -10, -8, 0, 0.52, 12, 0, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.3, -13, -10, 0, 0.52, 14, 0, 0, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.3, -18, -14, -2, 0.52, 18, 2, 2, 0.72, 13, -2, 0, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.3, 16, 6, 0, 0.52, -6, 2, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.3, 8, 8, 0, 0.52, 22, 0, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.3, -20, 4, 34, 0.52, 12, 0, -6, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.3, 16, 22, 24, 0.52, -10, -6, 4, 0.7, -32, -4, 6, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.3, -14, 8, 6, 0.52, -36, 4, 2, 0.72, -62, 2, 4, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.3, -8, -2, 0, 0.52, 50, 1, 5, 0.72, 46, 3, 10, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.3, -20, -4, -34, 0.52, 12, 0, 6, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.3, 12, -22, -24, 0.52, -6, 6, -6, 0.7, -28, 4, -8, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.3, -16, -8, -4, 0.52, -28, 6, -2, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.3, 6, 12, 7, 0.52, -38, 8, 8, 0.74, -20, 10, 7, 1, -14, 10, 7],
    shinL: [0, 10, 0, -1, 0.3, 6, 0, -1, 0.52, 52, 0, -1, 1, 10, 0, -1],
    thighR: [0, -10, -16, -8, 0.3, -26, -18, -8, 0.52, 6, -14, -8, 1, -10, -16, -8],
    shinR: [0, 16, 0, 1, 0.3, 40, 0, 1, 0.52, 20, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.3, 0, 0.06, -0.14, 0.52, 0, -0.08, 0.75, 0.72, 0, -0.04, 1.05, 1, 0, 0, 1.1] },
});

/* Charge attack: hold, coil, then an explosive lunging thrust. */
clip('attack_charge', 1.25, {
  blend: 0.14, rm: 2.6, tags: 'attack',
  events: [ev(0.5, 'swoosh', { power: 1.1 }), ev(0.54, 'hitboxOn', { arc: 40, reach: 3.6, height: 1.2, power: 1.0, dmg: 2.6, launch: true, gb: true }),
    ev(0.78, 'hitboxOff'), ev(1.1, 'cancel'), ev(0.46, 'voice', { tag: 'shout' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.34, 10, -46, -4, 0.44, 12, -52, -5, 0.58, 4, 6, 4, 1, 4, -10, 0],
    spine1: [0, 3, -5, 0, 0.4, 8, -18, -2, 0.58, 4, 4, 2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.4, 6, -22, -3, 0.58, 2, 6, 3, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.4, 10, -34, -6, 0.58, 6, 10, 5, 0.76, 2, 6, 3, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.4, 2, 26, 0, 0.58, 4, 0, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.4, -6, 28, 0, 0.58, -2, 0, 0, 1, -4, 14, 0],
    upperArmR: [0, -16, 20, 6, 0.4, 20, 24, 26, 0.58, -14, -8, 2, 0.74, -34, -6, 6, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.4, -18, 10, 6, 0.58, -34, 6, 2, 0.74, -60, 2, 4, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.4, -8, -2, 0, 0.58, 54, 2, 6, 0.74, 44, 3, 10, 1, -10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.4, 16, -24, -26, 0.58, -10, 8, -2, 0.74, -30, 4, -8, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.4, -20, -6, -4, 0.58, -26, 6, -2, 1, -62, 20, -4],
    thighL: [0, -6, 10, 6, 0.4, 6, 20, 6, 0.58, -44, 4, 8, 0.78, -14, 10, 6, 1, -6, 10, 6],
    shinL: [0, 10, 0, -1, 0.4, 12, 0, -1, 0.58, 42, 0, -1, 1, 10, 0, -1],
    thighR: [0, -10, -16, -8, 0.4, -34, -26, -8, 0.58, 20, -8, -8, 1, -10, -16, -8],
    shinR: [0, 16, 0, 1, 0.4, 52, 0, 1, 0.58, 14, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.4, 0, 0.02, -0.2, 0.6, 0, -0.05, 1.4, 0.8, 0, 0, 2.4, 1, 0, 0, 2.6] },
});

/* =========================================================================
 *  CHARGE BRANCH — one clip per PROPERTY.
 * =========================================================================
 * The charge button branches out of every chain position into a move with a
 * different property (launcher / stun / ground-slam / 360 / wall-slam). Those
 * properties used to exist only as hitbox numbers while the animation replayed
 * a light-chain swing at a slower rate, so five different ideas all looked
 * like the same sweep. Each one now has a clip whose BLADE PATH is the idea:
 *
 *   charge_launcher   tip rises 0.02 m -> 3.20 m across the window, legs drive it
 *   charge_stun       the flattest arc in the set: 0.49 m of tip height at a
 *                     near-constant 1.7 m radius, then a recoil
 *   charge_slam       overhead -> buried in the dirt (tip -0.06 m), on one knee
 *   charge_spin360    TWO revolutions (hips span 759 deg), blade held level
 *   charge_lance      couched weapon, four foot plants, then a rip upward
 * ========================================================================= */

/* ch1 LAUNCHER — "Crescent Lift". A scoop: the tip is dropped to the dirt on
 * the hero's right, then thrown up through the whole vertical band and over
 * his head. The legs do the work — deep coil, then full extension onto the
 * toes — because a launcher that is only an arm swing has no lift in it. */
clip('charge_launcher', 0.64, {
  blend: 0.10, fps: 15, rm: 1.0, tags: 'attack',
  events: [ev(0.18, 'voice', { tag: 'shout' }), ev(0.30, 'swoosh', { power: 0.9 }),
    ev(0.34, 'hitboxOn', { arc: 96, reach: 3.15, height: 1.30, power: 0.85, dmg: 1.5, launch: true }),
    ev(0.57, 'hitboxOff'), ev(0.70, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.17, 24, -46, -8, 0.34, 17, -20, -6, 0.46, 0, 6, 2, 0.58, -15, 34, 8, 0.7, -14, 46, 8, 0.84, -2, 26, 4, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.17, 10, -16, -3, 0.34, 7, -7, -2, 0.46, 0, 3, 1, 0.58, -8, 13, 4, 0.7, -8, 17, 4, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.17, 8, -19, -4, 0.34, 6, -9, -3, 0.46, -2, 4, 2, 0.58, -12, 15, 5, 0.7, -12, 19, 5, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.17, 16, -30, -7, 0.34, 10, -14, -4, 0.46, -2, 6, 4, 0.58, -20, 26, 10, 0.7, -20, 34, 10, 0.84, -8, 20, 6, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.17, 12, 16, 0, 0.46, 0, -2, 0, 0.66, -8, -10, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.17, 15, 20, 2, 0.46, -10, 0, -2, 0.66, -22, -8, -2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.17, 8, 12, 14, 0.46, -4, -2, -6, 0.58, -10, -8, -16, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.07, -2, 24, -30, 0.17, 59, 24, -68, 0.34, 45, 22, -40, 0.46, 45, 6, -3, 0.58, 45, 6, 10, 0.7, 45, 6, -12, 0.86, 8, 14, -4, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.07, -58, -22, 6, 0.17, -38, -20, 6, 0.34, -38, -20, 6, 0.46, -38, -23, 6, 0.58, -39, -37, 6, 0.7, -39, -33, 6, 0.86, -60, -26, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.07, -14, -6, 0, 0.17, 50, -6, 0, 0.34, 50, -6, 0, 0.46, 30, -6, 0, 0.58, 10, -6, 0, 0.7, 10, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.17, 8, -12, -14, 0.46, -4, 2, 6, 0.58, -10, 8, 16, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.17, 22, -20, 38, 0.34, 6, -16, 18, 0.46, -30, -20, -16, 0.58, -66, -28, -34, 0.7, -58, -30, -32, 0.86, -40, -20, -14, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.17, -44, 12, -4, 0.34, -48, 14, -4, 0.46, -62, 20, -6, 0.58, -84, 28, -8, 0.7, -78, 26, -8, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.17, -34, 22, 7, 0.34, -26, 12, 7, 0.46, -12, -4, 7, 0.58, -2, -14, 7, 0.74, -10, 4, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.17, 66, 0, -1, 0.34, 54, 0, -1, 0.46, 20, 0, -1, 0.58, 6, 0, -1, 0.74, 20, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.17, -14, 12, 0, 0.58, 22, 6, 0, 0.78, 2, 10, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.17, -36, -28, -9, 0.34, -28, -18, -9, 0.46, -14, 4, -9, 0.58, -6, 14, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.17, 72, 0, 1, 0.34, 58, 0, 1, 0.46, 24, 0, 1, 0.58, 10, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.17, -16, -18, 0, 0.58, 26, -8, 0, 0.78, 0, -14, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.17, 0, -0.19, -0.05, 0.34, 0, -0.12, 0.22, 0.46, 0, -0.02, 0.46, 0.58, 0, 0.15, 0.72, 0.78, 0, 0.02, 0.92, 1, 0, 0, 1.0] },
});

/* ch2 STUN — "Haft Crack". A blunt bar, not a blade: the hands choke up on the
 * haft and the shaft is driven flat across the front at chest height, then the
 * hips recoil 10 degrees BACK the way they came, the way a strike into armour
 * rebounds. Measured inside the active window the tip moves through 0.49 m of
 * height at a near-constant 1.66-1.73 m radius — the flattest and most
 * constant-radius pass in the whole set, against 2.19 m of height and a
 * 0.68-1.80 m radius on C1. Nothing is cut here; something is HIT. */
clip('charge_stun', 0.62, {
  blend: 0.08, fps: 15, rm: 0.7, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.25, 'swoosh', { power: 0.55 }),
    ev(0.296, 'hitboxOn', { arc: 72, reach: 3.0, height: 1.25, power: 0.8, dmg: 1.2 }),
    ev(0.51, 'hitboxOff'), ev(0.52, 'sfx', { name: 'impact_blunt' }), ev(0.68, 'cancel')],
  R: on(GUARD, {
    // hips run -34 -> -38 across the hit and then come BACK to -28: a 10 deg
    // rebound. Every other attack in the library settles forward out of its
    // follow-through; this one bounces off.
    hips: [0, 4, -14, 0, 0.16, 6, 24, 5, 0.3, 5, 12, 3, 0.4, 3, -12, -2, 0.48, 2, -34, -5, 0.56, 2, -38, -5, 0.7, 3, -28, -4, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.16, 4, 12, 2, 0.3, 3, 6, 1, 0.48, 2, -14, -3, 0.56, 2, -15, -3, 0.7, 2, -11, -2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.16, 1, 14, 3, 0.3, 0, 7, 2, 0.48, -1, -17, -3, 0.56, -1, -18, -3, 0.7, -1, -13, -3, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.16, 2, 20, 4, 0.3, 1, 10, 3, 0.4, -1, -10, -2, 0.48, -2, -26, -5, 0.56, -3, -29, -5, 0.7, -3, -21, -4, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.16, 6, -8, 0, 0.48, 6, 18, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.16, -2, -10, 0, 0.48, 0, 20, 2, 0.56, 2, 21, 2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.16, 4, -8, 20, 0.48, -2, 12, -8, 0.56, -3, 13, -9, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.16, 11, 43, 54, 0.3, 8, 44, 53, 0.4, 0, 46, 53, 0.48, -10, 46, 53, 0.56, -10, 50, 53, 0.7, 1, 40, 18, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.16, -3, 2, 6, 0.3, -14, 2, 6, 0.4, -14, 2, 6, 0.48, -3, 2, 6, 0.56, -3, 4, 6, 0.7, -3, 4, 6, 0.86, -44, -10, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.16, 36, 1, 0, 0.3, 30, 1, 0, 0.4, 49, 1, 0, 0.48, 55, 1, 0, 0.56, 55, 5, 0, 0.7, 3, 5, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.16, 4, 8, -20, 0.48, -2, -12, 8, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.16, -46, -6, -38, 0.3, -50, -10, -30, 0.4, -44, -4, -2, 0.48, -30, 10, 30, 0.56, -29, 12, 32, 0.7, -34, 4, 16, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.16, -70, 26, -4, 0.4, -52, 10, -4, 0.48, -46, 2, -4, 0.56, -45, 2, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.16, -20, 20, 7, 0.4, -12, -2, 7, 0.48, -22, -10, 7, 0.56, -23, -11, 7, 0.7, -18, -6, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.16, 30, 0, -1, 0.4, 16, 0, -1, 0.48, 34, 0, -1, 0.56, 36, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.16, -12, -4, -9, 0.4, -24, -28, -9, 0.48, -30, -34, -9, 0.56, -31, -35, -9, 0.7, -24, -24, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.16, 20, 0, 1, 0.4, 36, 0, 1, 0.48, 46, 0, 1, 0.56, 48, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.16, 0, 0.01, -0.05, 0.3, 0, 0, 0.22, 0.44, 0, -0.02, 0.58, 0.52, 0, -0.02, 0.66, 0.62, 0, -0.01, 0.68, 1, 0, 0, 0.7] },
});

/* ch3 GROUND SLAM — "Earthbreaker". Rears the whole body back until the blade
 * points at the sky BEHIND him, then drives it straight down into the dirt and
 * DROPS ONTO ONE KNEE with the blade buried. The point of difference from the
 * overhead smash (attack_heavy) is the last third: attack_heavy pulls the
 * blade straight back up into guard, this one holds the tip between -0.06 and
 * +0.14 m for the ten frames the shockwave ring is running, with the hero's
 * weight on the haft. */
clip('charge_slam', 1.02, {
  blend: 0.12, fps: 15, rm: 1.1, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.45, 'swoosh', { power: 1.15 }),
    ev(0.474, 'hitboxOn', { arc: 180, reach: 5.0, height: 0.75, power: 1.0, dmg: 2.4, launch: true, gb: true }),
    ev(0.53, 'vfx', { name: 'groundSlam' }), ev(0.637, 'hitboxOff'),
    ev(0.66, 'sfx', { name: 'impact_slam' }), ev(0.86, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.26, -18, -24, -2, 0.4, -14, -20, -2, 0.53, 27, -6, 2, 0.63, 26, -8, 2, 0.74, 22, -10, 1, 0.88, 12, -12, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.26, -14, -8, 0, 0.53, 14, -1, 0, 0.74, 12, -3, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.26, -16, -10, 0, 0.53, 16, -1, 0, 0.74, 13, -4, 0, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.26, -24, -14, -2, 0.4, -19, -12, -2, 0.53, 22, 2, 2, 0.63, 21, 0, 2, 0.74, 17, -2, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.26, 20, 8, 0, 0.53, -10, 2, 0, 0.74, -14, 4, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.26, 10, 10, 0, 0.53, 24, 0, 0, 0.72, -6, 6, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.26, -26, 4, 40, 0.53, 14, 0, -8, 0.74, 10, 2, -4, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.26, 22, 24, 10, 0.4, 12, 18, 6, 0.53, -22, 18, -39, 0.63, -14, 19, -39, 0.74, -3, 20, -39, 0.88, -10, 18, -14, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.26, -10, 10, 6, 0.4, -16, 8, 6, 0.53, -24, -20, 6, 0.63, -25, -20, 6, 0.74, -26, -20, 6, 0.88, -46, -14, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.26, -6, -2, 0, 0.53, 27, -7, 0, 0.63, 28, -4, 0, 0.74, 28, -1, 0, 0.88, 20, -4, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.26, -26, -4, -40, 0.53, 14, 0, 8, 0.74, 10, -2, 4, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.26, 18, -26, -28, 0.4, 8, -20, -22, 0.53, -4, 6, -6, 0.63, -2, 6, -6, 0.74, -10, 4, -6, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.26, -12, -10, -4, 0.53, -24, 6, -2, 0.74, -34, 4, -2, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.26, 4, 12, 7, 0.42, -30, 10, 7, 0.53, -84, 12, 7, 0.74, -82, 12, 7, 0.88, -40, 11, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.26, 8, 0, -1, 0.42, 40, 0, -1, 0.53, 90, 0, -1, 0.74, 88, 0, -1, 0.88, 44, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.53, -16, 12, 0, 0.74, -15, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.26, -30, -18, -9, 0.42, -12, -14, -9, 0.53, 10, -10, -9, 0.74, 9, -10, -9, 0.88, -6, -14, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.26, 44, 0, 1, 0.42, 60, 0, 1, 0.53, 140, 0, 1, 0.74, 138, 0, 1, 0.88, 62, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.53, 34, -14, 0, 0.74, 33, -14, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.26, 0, 0.06, -0.16, 0.42, 0, 0.02, 0.32, 0.53, 0, -0.42, 0.86, 0.74, 0, -0.41, 1.02, 0.88, 0, -0.16, 1.08, 1, 0, 0, 1.1] },
});

/* ch4 WIDE 360 — "Storm of Blades". TWO revolutions, which is what the table
 * has always declared (sweep [0,720]) and what no clip in the library
 * performed: the light-chain finisher turns 410 degrees, a little over one.
 * The hero pivots on the spot with the weapon thrown out to full extension and
 * held LEVEL, leaning out against it, so the blade traces one flat disc at
 * chest height twice instead of the light chain's diagonal figure of eight. */
clip('charge_spin360', 0.99, {
  blend: 0.11, fps: 15, rm: 1.05, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.22, 'swoosh', { power: 1.0 }),
    ev(0.2525, 'hitboxOn', { arc: 180, reach: 4.6, height: 1.30, power: 0.9, dmg: 1.5, launch: true }),
    ev(0.48, 'swoosh', { power: 1.0 }), ev(0.707, 'hitboxOff'), ev(0.86, 'cancel')],
  R: on(GUARD, {
    // TWO revolutions — the table has always declared sweep [0,720] and the
    // clip it used to borrow (attack_light_4) turned 410 deg, a little over
    // one. Measured hips span here is 759 deg, so the ribbon VFX is finally
    // being told the rotation the body actually performs.
    hips: [0, 4, -14, 0, 0.14, 11, -58, -9, 0.24, 6, 30, 5, 0.36, 3, 190, 8, 0.47, 3, 360, 8, 0.58, 3, 520, 8, 0.69, 4, 640, 6, 0.8, 6, 690, 3, 1, 4, 700, 0],
    spine1: [0, 3, -5, 0, 0.14, 7, -18, -3, 0.26, 3, 10, 4, 0.7, 2, 8, 3, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.14, 5, -20, -4, 0.26, 0, 12, 5, 0.7, -1, 9, 4, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.14, 9, -32, -7, 0.26, 2, 18, 9, 0.44, 0, 12, 10, 0.58, 0, 14, 10, 0.72, 1, 16, 8, 0.86, -1, 10, 4, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.14, 8, 24, 0, 0.3, 4, -18, 0, 0.58, 4, -16, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.14, 2, 30, 2, 0.3, -6, -26, -2, 0.58, -6, -24, -2, 0.86, -5, -10, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.14, -4, 22, 22, 0.26, 2, 10, -12, 0.72, 2, 10, -12, 1, 2, 6, 10],
    // arm locked out for both revolutions: the blade traces one flat disc at
    // chest height, where the light-chain spin draws a diagonal figure of eight
    upperArmR: [0, -16, 20, 6, 0.14, 4, 30, 6, 0.26, -45, 19, 13, 0.4, -45, 19, 13, 0.58, -46, 19, 13, 0.72, -44, 19, 13, 0.86, -40, 20, 6, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.14, -46, -32, 8, 0.26, -20, -22, 6, 0.58, -20, -22, 6, 0.72, -22, -22, 6, 0.86, -34, -24, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.14, -22, -16, 0, 0.26, 90, -6, 0, 0.58, 90, -6, 0, 0.72, 88, -6, 0, 0.88, 30, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.14, -4, -22, -22, 0.26, 2, -10, 12, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.14, -14, -2, -30, 0.26, -64, -22, 42, 0.4, -66, -22, 44, 0.58, -68, -22, 46, 0.72, -64, -22, 42, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.14, -30, 8, -4, 0.26, -20, 16, -6, 0.58, -18, 16, -6, 0.72, -24, 16, -6, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.14, -4, 26, 7, 0.26, -34, -10, 9, 0.4, -6, 8, 7, 0.53, -34, -10, 9, 0.66, -8, 6, 7, 0.8, -22, 2, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.14, 12, 0, -1, 0.26, 58, 0, -1, 0.4, 18, 0, -1, 0.53, 58, 0, -1, 0.66, 20, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.26, 12, 12, 0, 0.53, 12, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.14, -26, -34, -9, 0.26, -4, 14, -11, 0.4, -34, -12, -9, 0.53, -4, 14, -11, 0.66, -30, -10, -9, 0.8, -16, -6, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.14, 40, 0, 1, 0.26, 14, 0, 1, 0.4, 56, 0, 1, 0.53, 14, 0, 1, 0.66, 54, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.4, 14, -18, 0, 0.66, 14, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.14, 0, 0.02, -0.08, 0.3, 0, 0.05, 0.2, 0.58, 0, 0.05, 0.62, 0.86, 0, 0.02, 0.96, 1, 0, 0, 1.05] },
});

/* ch5 WALL SLAM — "Banner Charge". A couched charge, not a standing thrust:
 * the weapon is locked under the arm and does almost nothing for two thirds of
 * the clip while the LEGS take four strides and the root eats 2.4 m, then the
 * hero plants and RIPS the haft upward, which is where the 11 m of knockback
 * comes from. Measured hips span 10 deg and chest 2 — near-static arms and a
 * near-static torso over a full locomotion cycle, a signature no other attack
 * in the library has (the next quietest is the ground slam at 19). */
clip('charge_lance', 1.27, {
  blend: 0.13, fps: 15, rm: 3.0, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.24, 'foot', { side: 'R' }), ev(0.38, 'foot', { side: 'L' }),
    ev(0.5, 'swoosh', { power: 1.2 }), ev(0.52, 'foot', { side: 'R' }),
    ev(0.538, 'hitboxOn', { arc: 58, reach: 4.8, height: 1.20, power: 1.0, dmg: 2.8, launch: true, gb: true }),
    ev(0.735, 'hitboxOff'), ev(0.78, 'foot', { side: 'L' }), ev(0.92, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.12, 26, -20, 0, 0.24, 30, -12, 2, 0.38, 30, -16, -2, 0.52, 30, -10, 2, 0.62, 12, -14, 0, 0.74, -6, -16, -2, 0.88, 4, -15, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.12, 12, -6, 0, 0.52, 13, -5, 0, 0.68, 2, -6, 0, 0.78, -6, -6, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.12, 12, -7, 0, 0.52, 13, -6, 0, 0.68, 0, -7, 0, 0.78, -8, -7, 0, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.12, 18, -10, 0, 0.3, 20, -8, 0, 0.52, 20, -9, 0, 0.66, 2, -10, 0, 0.78, -14, -10, 0, 0.9, -2, -9, 0, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.12, -6, 8, 0, 0.52, -8, 8, 0, 0.78, 12, 10, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.12, -22, 10, 0, 0.52, -24, 10, 0, 0.78, 6, 12, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.12, 6, 14, 6, 0.52, 6, 14, 6, 0.7, -6, 2, 18, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.12, -14, 26, 22, 0.3, -13, 26, 22, 0.52, -14, 26, 22, 0.62, -30, 14, 12, 0.72, -66, -6, 16, 0.82, -58, -12, 22, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.12, -96, -18, 6, 0.52, -95, -18, 6, 0.62, -72, -14, 6, 0.72, -34, -8, 4, 0.86, -60, -16, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.12, 22, -4, 0, 0.52, 22, -4, 0, 0.62, 36, 0, 0, 0.72, 18, -2, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.12, 6, -14, -6, 0.52, 6, -14, -6, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.12, -46, -20, -14, 0.3, -44, -20, -14, 0.52, -46, -20, -14, 0.62, -58, -22, -20, 0.72, -84, -28, -30, 0.86, -60, -22, -20, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.12, -84, 16, -4, 0.52, -83, 16, -4, 0.72, -48, 10, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.12, -44, 10, 7, 0.24, 16, 10, 7, 0.32, -50, 10, 7, 0.45, 12, 10, 7, 0.58, -52, 10, 7, 0.7, 6, 10, 7, 0.78, -30, 10, 7, 0.88, -18, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.12, 46, 0, -1, 0.24, 14, 0, -1, 0.32, 52, 0, -1, 0.45, 12, 0, -1, 0.58, 54, 0, -1, 0.7, 16, 0, -1, 0.78, 44, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.24, 14, 12, 0, 0.45, 12, 12, 0, 0.7, 10, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.12, 14, -16, -9, 0.2, -48, -16, -9, 0.32, 12, -16, -9, 0.45, -50, -16, -9, 0.58, 10, -16, -9, 0.7, -44, -16, -9, 0.82, -6, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.12, 16, 0, 1, 0.2, 50, 0, 1, 0.32, 14, 0, 1, 0.45, 52, 0, 1, 0.58, 12, 0, 1, 0.7, 48, 0, 1, 0.82, 34, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.2, 16, -18, 0, 0.45, 14, -18, 0, 0.7, 12, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.12, 0, -0.06, 0.18, 0.3, 0, -0.04, 0.94, 0.45, 0, -0.03, 1.52, 0.538, 0, -0.03, 1.92, 0.66, 0, 0.01, 2.42, 0.78, 0, 0.02, 2.74, 1, 0, 0, 3.0] },
});

/* =========================================================================
 *  WEAPON-SPECIFIC CHARGE BRANCH
 * =========================================================================
 * The five charge properties had one clip each, and all three weapon sets
 * played those same five separated only by `rate`. Measured offline with
 * tools/clip-shape.mjs, the hips turned an IDENTICAL 184 / 126 / 37 / 804 /
 * 32 degrees on glaive, spear and sabres alike, and the tip boxes differed
 * only by blade length — the spear's 0.73x the glaive's on every axis, the
 * sabres' 0.58x. The same move with a shorter stick. These ten end that.
 *
 * Each keeps its glaive counterpart's duration and event times to the frame,
 * because validateMovesets() asserts every move's declared windup / active /
 * total against the clip's own hitboxOn / hitboxOff markers. Only the body
 * changes.
 * ========================================================================= */

/* SPEAR ch1 — "Rising Point". The glaive scoops; a spear does not scoop. The
 * point is dropped to the hip and then DRIVEN straight up the vertical band on
 * a locking-out arm and a rising body, square to the target the whole way. */
clip('spear_ch1_rise', 0.64, {
  blend: 0.10, fps: 15, rm: 1.2, tags: 'attack',
  events: [ev(0.18, 'voice', { tag: 'shout' }), ev(0.30, 'swoosh', { power: 0.9 }),
    ev(0.34, 'hitboxOn', { arc: 96, reach: 3.15, height: 1.30, power: 0.85, dmg: 1.5, launch: true }),
    ev(0.57, 'hitboxOff'), ev(0.70, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.17, 20, -22, -2, 0.34, 12, -18, -2, 0.46, -4, -10, 0, 0.58, -20, -4, 2, 0.7, -22, -2, 2, 0.84, -6, -10, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.17, 9, -8, -1, 0.34, 5, -7, -1, 0.46, -2, -4, 0, 0.58, -10, -1, 1, 0.7, -11, 0, 1, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.17, 7, -9, -1, 0.34, 4, -8, -1, 0.46, -3, -5, 1, 0.58, -13, -2, 2, 0.7, -14, -1, 2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.17, 12, -12, -2, 0.34, 7, -10, -2, 0.46, -4, -6, 1, 0.58, -22, -2, 3, 0.7, -24, 0, 3, 0.84, -8, -6, 2, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.17, 10, 8, 0, 0.46, 0, 0, 0, 0.66, -10, -4, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.17, 12, 10, 1, 0.46, -8, 0, -1, 0.66, -24, -4, -1, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.17, 12, -8, 12, 0.46, 0, 2, 4, 0.58, -8, 6, -6, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.07, 6, 18, -14, 0.17, 34, 14, -30, 0.34, 10, 14, -20, 0.46, -30, 12, -8, 0.58, -86, 14, 2, 0.7, -92, 14, 2, 0.86, -40, 18, 2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.07, -50, -22, 6, 0.17, -22, -20, 6, 0.34, -10, -20, 6, 0.46, -4, -21, 6, 0.58, -2, -24, 6, 0.7, -2, -24, 6, 0.86, -38, -23, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.17, 20, -6, 0, 0.34, 34, -6, 0, 0.58, 46, -6, 0, 0.7, 44, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.17, 10, 4, -12, 0.46, 0, -2, -4, 0.58, -8, -6, 6, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.17, -14, -14, -14, 0.34, -30, -14, -12, 0.46, -54, -16, -10, 0.58, -84, -20, -6, 0.7, -88, -20, -6, 0.86, -50, -18, -8, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.17, -40, 14, -4, 0.34, -34, 14, -4, 0.46, -30, 16, -4, 0.58, -26, 20, -4, 0.7, -26, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.17, -32, 14, 7, 0.34, -24, 12, 7, 0.46, -8, 8, 7, 0.58, 4, 6, 7, 0.74, -8, 8, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.17, 62, 0, -1, 0.34, 48, 0, -1, 0.46, 14, 0, -1, 0.58, 2, 0, -1, 0.74, 18, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.17, -12, 12, 0, 0.58, 26, 10, 0, 0.78, 4, 11, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.17, -34, -18, -9, 0.34, -26, -17, -9, 0.46, -10, -15, -9, 0.58, 2, -14, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.17, 68, 0, 1, 0.34, 52, 0, 1, 0.46, 18, 0, 1, 0.58, 4, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.17, -14, -18, 0, 0.58, 28, -16, 0, 0.78, 2, -17, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.17, 0, -0.16, 0.02, 0.34, 0, -0.10, 0.26, 0.46, 0, 0.02, 0.58, 0.58, 0, 0.20, 0.86, 0.78, 0, 0.06, 1.06, 1, 0, 0, 1.2] },
});

/* SPEAR ch2 — "Butt Strike". The haft is reversed and the BUTT is jabbed into
 * the man in front at chest height: the shortest, tightest thing in the set,
 * with the point swung back and out of play behind the hero's shoulder. */
clip('spear_ch2_butt', 0.62, {
  blend: 0.08, fps: 15, rm: 0.6, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.25, 'swoosh', { power: 0.55 }),
    ev(0.296, 'hitboxOn', { arc: 72, reach: 3.0, height: 1.25, power: 0.8, dmg: 1.2 }),
    ev(0.51, 'hitboxOff'), ev(0.52, 'sfx', { name: 'impact_blunt' }), ev(0.68, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.16, 6, -34, -3, 0.3, 2, -20, -1, 0.42, 0, 8, 2, 0.52, 2, 14, 2, 0.66, 6, -4, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.16, 5, -14, -2, 0.3, 2, -8, -1, 0.42, 0, 4, 1, 0.52, 1, 7, 1, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.16, 2, -16, -2, 0.3, -1, -9, -1, 0.42, -2, 5, 2, 0.52, -1, 8, 2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.16, 0, -22, -3, 0.3, -4, -12, -2, 0.42, -6, 7, 3, 0.52, -4, 11, 3, 0.66, -2, -2, 2, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.16, 8, 14, 0, 0.42, 2, -2, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.16, 4, 16, 1, 0.42, -6, -2, -1, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.16, 16, -14, 12, 0.3, 14, -10, 12, 0.42, -4, 12, 6, 0.52, -8, 14, 4, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.16, 24, 22, -6, 0.3, 14, 20, -2, 0.42, -34, 16, 8, 0.52, -44, 14, 10, 0.66, -20, 18, 8, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.16, -96, -18, 6, 0.3, -88, -18, 6, 0.42, -40, -24, 6, 0.52, -30, -26, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.16, -26, -6, 0, 0.42, 14, -6, 0, 0.52, 20, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.16, -10, 10, -12, 0.42, 8, -12, -6, 0.52, 10, -14, -4, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.16, -12, -10, -22, 0.3, -20, -12, -18, 0.42, -62, -22, -4, 0.52, -70, -24, 0, 0.66, -46, -20, -6, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.16, -34, 10, -4, 0.3, -42, 13, -4, 0.42, -78, 24, -4, 0.52, -86, 26, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.16, -20, 12, 7, 0.42, -6, 8, 7, 0.52, -2, 7, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.16, 34, 0, -1, 0.42, 14, 0, -1, 0.52, 10, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.42, 4, 11, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.16, -12, -17, -9, 0.42, -28, -15, -9, 0.52, -30, -15, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.16, 20, 0, 1, 0.42, 40, 0, 1, 0.52, 42, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.42, -16, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.16, 0, -0.06, -0.10, 0.3, 0, -0.04, -0.02, 0.42, 0, 0.0, 0.34, 0.52, 0, 0.0, 0.48, 0.7, 0, 0.0, 0.56, 1, 0, 0, 0.6] },
});

/* SPEAR ch3 — "Quake Thrust". Not an overhead smash: both hands drive the
 * point STRAIGHT DOWN into the ground in front of the leading foot and the
 * hero drops onto the knee behind it, so the shock runs out of a puncture
 * rather than a swung edge. */
clip('spear_ch3_quake', 1.02, {
  blend: 0.12, fps: 15, rm: 1.0, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.45, 'swoosh', { power: 1.15 }),
    ev(0.474, 'hitboxOn', { arc: 180, reach: 5.0, height: 0.75, power: 1.0, dmg: 2.4, launch: true, gb: true }),
    ev(0.53, 'vfx', { name: 'groundSlam' }), ev(0.637, 'hitboxOff'),
    ev(0.66, 'sfx', { name: 'impact_slam' }), ev(0.86, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.26, -26, -18, -2, 0.4, -22, -16, -2, 0.53, 34, -12, 1, 0.63, 32, -12, 1, 0.74, 28, -13, 1, 0.88, 14, -13, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.26, -12, -7, -1, 0.4, -10, -6, -1, 0.53, 15, -5, 0, 0.74, 13, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.26, -14, -8, -1, 0.4, -12, -7, -1, 0.53, 18, -6, 1, 0.74, 15, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.26, -20, -10, -2, 0.4, -17, -9, -2, 0.53, 26, -7, 1, 0.63, 24, -7, 1, 0.74, 21, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.26, 16, 8, 0, 0.53, -14, 2, 0, 0.74, -12, 2, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.26, 20, 10, 1, 0.53, -26, 2, -1, 0.74, -22, 2, -1, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.26, 18, -4, 16, 0.53, -6, 6, 2, 0.74, -4, 6, 4, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.26, -128, 16, -8, 0.4, -122, 16, -8, 0.53, 22, 18, 4, 0.63, 30, 18, 4, 0.74, 26, 19, 5, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.26, -14, -20, 6, 0.4, -16, -20, 6, 0.53, -6, -22, 6, 0.74, -12, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.26, -30, -6, 0, 0.53, 34, -6, 0, 0.74, 28, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.26, 16, 4, -16, 0.53, -6, -6, -2, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.26, -134, -14, 10, 0.4, -128, -14, 10, 0.53, 14, -18, -4, 0.63, 22, -18, -4, 0.74, 18, -19, -5, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.26, -10, 18, -4, 0.4, -12, 18, -4, 0.53, -4, 20, -4, 0.74, -10, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.26, -20, 12, 7, 0.4, -16, 11, 7, 0.53, -76, 8, 7, 0.63, -84, 8, 7, 0.74, -80, 8, 7, 0.88, -34, 9, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.26, 30, 0, -1, 0.4, 26, 0, -1, 0.53, 96, 0, -1, 0.63, 108, 0, -1, 0.74, 104, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.53, 16, 11, 0, 0.74, 14, 11, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.26, -14, -17, -9, 0.4, -18, -17, -9, 0.53, -44, -14, -9, 0.63, -50, -14, -9, 0.74, -46, -14, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.26, 22, 0, 1, 0.4, 26, 0, 1, 0.53, 62, 0, 1, 0.63, 70, 0, 1, 0.74, 66, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.53, 10, -17, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.26, 0, 0.10, -0.06, 0.4, 0, 0.08, 0.06, 0.53, 0, -0.34, 0.62, 0.63, 0, -0.40, 0.76, 0.74, 0, -0.38, 0.86, 0.88, 0, -0.14, 0.96, 1, 0, 0, 1.0] },
});

/* SPEAR ch4 — "Circle of Spears". ONE revolution, not the glaive's two, taken
 * at the spear's full extension and held dead level at hip height, so the
 * whole point of the move is RADIUS where the glaive's is rotation count. */
clip('spear_ch4_circle', 0.99, {
  blend: 0.11, fps: 15, rm: 1.0, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.22, 'swoosh', { power: 1.0 }),
    ev(0.2525, 'hitboxOn', { arc: 180, reach: 4.6, height: 1.30, power: 0.9, dmg: 1.5, launch: true }),
    ev(0.48, 'swoosh', { power: 1.0 }), ev(0.707, 'hitboxOff'), ev(0.86, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.14, 8, -46, -6, 0.24, 4, 20, 4, 0.36, 2, 108, 6, 0.47, 2, 190, 6, 0.58, 2, 268, 6, 0.69, 3, 320, 5, 0.8, 5, 344, 3, 1, 4, 350, 0],
    spine1: [0, 3, -5, 0, 0.14, 5, -12, -2, 0.26, 2, 6, 3, 0.7, 1, 5, 2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.14, 3, -13, -3, 0.26, -1, 7, 3, 0.7, -1, 6, 3, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.14, 6, -20, -5, 0.26, 0, 10, 6, 0.44, -1, 7, 6, 0.58, -1, 8, 6, 0.72, 0, 9, 5, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.14, 7, 18, 0, 0.3, 4, -12, 0, 0.58, 4, -10, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.14, 2, 22, 1, 0.3, -4, -18, -1, 0.58, -4, -16, -1, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.14, -6, 18, 20, 0.26, 0, 8, -14, 0.72, 0, 8, -14, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.14, -20, 26, -18, 0.26, -8, 22, -76, 0.47, -6, 22, -80, 0.72, -6, 22, -78, 0.88, -12, 21, -30, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.14, -46, -22, 6, 0.26, -4, -22, 6, 0.72, -3, -22, 6, 0.88, -40, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.26, 6, -6, 0, 0.72, 6, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.14, -6, -18, -20, 0.26, 0, -8, 14, 0.72, 0, -8, 14, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.14, -30, -24, 14, 0.26, -14, -20, 62, 0.47, -12, -20, 66, 0.72, -12, -20, 64, 0.88, -26, -18, 24, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.14, -50, 18, -4, 0.26, -14, 14, -4, 0.72, -13, 14, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.14, -24, 12, 7, 0.3, -12, 10, 7, 0.6, -14, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.14, 40, 0, -1, 0.3, 22, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.3, -2, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.14, -28, -17, -9, 0.3, -18, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.14, 44, 0, 1, 0.3, 28, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.3, -4, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.14, 0, -0.10, 0.04, 0.3, 0, -0.06, 0.30, 0.5, 0, -0.05, 0.56, 0.7, 0, -0.05, 0.80, 1, 0, 0, 1.0] },
});

/* SPEAR ch5 — "Lance Charge". The couched run the glaive borrows, done the way
 * a spear is actually carried: the haft tucked UNDER the arm and locked to the
 * ribs, the point level at chest height for the whole run, no rip upward. */
clip('spear_ch5_couch', 1.27, {
  blend: 0.13, fps: 15, rm: 3.2, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.24, 'foot', { side: 'R' }), ev(0.38, 'foot', { side: 'L' }),
    ev(0.5, 'swoosh', { power: 1.2 }), ev(0.52, 'foot', { side: 'R' }),
    ev(0.538, 'hitboxOn', { arc: 58, reach: 4.8, height: 1.20, power: 1.0, dmg: 2.8, launch: true, gb: true }),
    ev(0.735, 'hitboxOff'), ev(0.78, 'foot', { side: 'L' }), ev(0.92, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.12, 20, -16, 0, 0.24, 24, -12, 2, 0.38, 24, -14, -2, 0.52, 24, -11, 2, 0.62, 20, -13, 0, 0.74, 8, -15, -1, 0.88, 5, -14, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.12, 9, -6, 0, 0.38, 10, -6, 0, 0.62, 9, -6, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.12, 6, -7, 1, 0.38, 7, -7, 0, 0.62, 6, -7, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.12, 8, -9, 1, 0.38, 9, -9, 0, 0.62, 8, -9, 1, 0.88, 0, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.12, 2, 8, 0, 0.62, 2, 8, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.12, -10, 10, 0, 0.62, -10, 10, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.12, 8, -6, 22, 0.38, 8, -6, 22, 0.74, 4, 2, 14, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.12, -34, 12, 24, 0.24, -36, 12, 26, 0.38, -36, 12, 26, 0.52, -36, 12, 26, 0.62, -34, 12, 24, 0.78, -26, 16, 14, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.12, -84, -18, 6, 0.38, -86, -18, 6, 0.62, -84, -18, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.12, -18, -6, 0, 0.62, -18, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.12, 6, 4, -16, 0.62, 6, 4, -16, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.12, -44, -12, -18, 0.24, -46, -12, -18, 0.38, -46, -12, -18, 0.52, -46, -12, -18, 0.62, -44, -12, -18, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.12, -74, 16, -4, 0.38, -76, 16, -4, 0.62, -74, 16, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.12, -40, 10, 7, 0.24, 12, 10, 7, 0.38, -44, 10, 7, 0.52, 10, 10, 7, 0.66, -40, 10, 7, 0.8, -6, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.12, 58, 0, -1, 0.24, 8, 0, -1, 0.38, 62, 0, -1, 0.52, 6, 0, -1, 0.66, 56, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.24, 16, 12, 0, 0.38, -18, 12, 0, 0.52, 18, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.12, 10, -16, -9, 0.24, -44, -16, -9, 0.38, 12, -16, -9, 0.52, -46, -16, -9, 0.66, 8, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.12, 8, 0, 1, 0.24, 60, 0, 1, 0.38, 6, 0, 1, 0.52, 64, 0, 1, 0.66, 10, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.24, -20, -18, 0, 0.38, 18, -18, 0, 0.52, -22, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.14, 0, -0.04, 0.30, 0.3, 0, -0.02, 0.86, 0.5, 0, -0.03, 1.62, 0.7, 0, -0.02, 2.40, 0.86, 0, -0.01, 2.92, 1, 0, 0, 3.2] },
});

/* SABRE ch1 — "Uppercut Pair". Two short blades, so the launcher is TWO rips
 * up the body's centre line a few frames apart, counter-phased left and right,
 * crossing at the top. The glaive throws one long arc; this scissors. */
clip('sabre_ch1_uppair', 0.64, {
  blend: 0.09, fps: 15, rm: 0.9, tags: 'attack',
  events: [ev(0.18, 'voice', { tag: 'shout' }), ev(0.30, 'swoosh', { power: 0.9 }),
    ev(0.34, 'hitboxOn', { arc: 96, reach: 3.15, height: 1.30, power: 0.85, dmg: 1.5, launch: true }),
    ev(0.57, 'hitboxOff'), ev(0.70, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.17, 18, -34, -6, 0.34, 10, -14, -4, 0.46, -6, 8, 2, 0.58, -18, 26, 6, 0.7, -16, 30, 6, 0.84, -2, 6, 2, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.17, 8, -12, -2, 0.34, 4, -5, -1, 0.46, -3, 4, 1, 0.58, -9, 10, 3, 0.7, -8, 12, 3, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.17, 6, -14, -3, 0.34, 3, -6, -2, 0.46, -4, 5, 2, 0.58, -12, 12, 4, 0.7, -11, 14, 4, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.17, 11, -22, -5, 0.34, 6, -9, -3, 0.46, -5, 8, 3, 0.58, -18, 20, 7, 0.7, -17, 24, 7, 0.84, -6, 6, 3, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.17, 11, 14, 0, 0.46, 0, -2, 0, 0.66, -9, -8, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.17, 13, 17, 2, 0.46, -8, 0, -2, 0.66, -20, -6, -2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.17, 10, 10, 16, 0.34, 4, 4, 4, 0.46, -6, -2, -10, 0.58, -12, -6, -18, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.1, 22, 22, -26, 0.24, 40, 20, -44, 0.34, 6, 18, -26, 0.46, -52, 14, -6, 0.58, -96, 12, 6, 0.7, -90, 12, 6, 0.86, -34, 16, 4, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.1, -54, -22, 6, 0.24, -26, -20, 6, 0.34, -14, -20, 6, 0.46, -6, -22, 6, 0.58, -4, -26, 6, 0.7, -6, -26, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.1, -16, -6, 0, 0.24, 28, -6, 0, 0.46, 40, -6, 0, 0.58, 34, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.24, 10, -10, -16, 0.42, 4, -4, -4, 0.54, -6, 2, 10, 0.66, -12, 6, 18, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.18, 12, -20, 30, 0.32, 30, -18, 48, 0.42, -4, -16, 30, 0.54, -60, -14, 10, 0.66, -100, -14, -2, 0.78, -94, -14, -2, 0.9, -46, -15, -6, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.18, -46, 18, -4, 0.32, -20, 16, -4, 0.42, -10, 16, -4, 0.54, -4, 18, -4, 0.66, -2, 22, -4, 0.78, -6, 22, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.17, -30, 14, 7, 0.34, -22, 12, 7, 0.46, -6, 8, 7, 0.58, 6, 5, 7, 0.74, -8, 8, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.17, 58, 0, -1, 0.34, 44, 0, -1, 0.46, 12, 0, -1, 0.58, 0, 0, -1, 0.74, 16, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.17, -12, 12, 0, 0.58, 28, 10, 0, 0.78, 4, 11, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.17, -34, -20, -9, 0.34, -26, -18, -9, 0.46, -10, -12, -9, 0.58, 4, -8, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.17, 64, 0, 1, 0.34, 50, 0, 1, 0.46, 16, 0, 1, 0.58, 2, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.17, -14, -18, 0, 0.58, 26, -14, 0, 0.78, 0, -16, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.17, 0, -0.18, 0.0, 0.34, 0, -0.11, 0.20, 0.46, 0, 0.0, 0.44, 0.58, 0, 0.17, 0.68, 0.78, 0, 0.03, 0.84, 1, 0, 0, 0.9] },
});

/* SABRE ch2 — "Pommel Rap". In close, inside the reach of anything longer: the
 * right hand turns over and the pommel is rapped into the face at arm's length
 * while the left blade stays up as a guard. The tightest radius in the set. */
clip('sabre_ch2_pommel', 0.62, {
  blend: 0.07, fps: 15, rm: 0.5, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.25, 'swoosh', { power: 0.55 }),
    ev(0.296, 'hitboxOn', { arc: 72, reach: 3.0, height: 1.25, power: 0.8, dmg: 1.2 }),
    ev(0.51, 'hitboxOff'), ev(0.52, 'sfx', { name: 'impact_blunt' }), ev(0.68, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.16, 8, -30, -4, 0.3, 4, -16, -2, 0.42, -2, 12, 3, 0.52, -4, 18, 4, 0.66, 2, -2, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.16, 6, -12, -2, 0.3, 3, -6, -1, 0.42, -1, 5, 2, 0.52, -2, 8, 2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.16, 3, -14, -2, 0.3, 0, -7, -1, 0.42, -3, 6, 2, 0.52, -4, 9, 3, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.16, 2, -20, -4, 0.3, -2, -10, -2, 0.42, -7, 9, 4, 0.52, -9, 14, 5, 0.66, -4, -2, 2, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.16, 9, 12, 0, 0.42, 3, -4, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.16, 6, 14, 1, 0.42, -8, -4, -1, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.16, 12, 2, 14, 0.3, 10, 0, 12, 0.42, -2, 10, 2, 0.52, -6, 12, 0, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.16, 4, 24, -12, 0.3, -6, 22, -6, 0.42, -44, 16, 10, 0.52, -54, 14, 14, 0.66, -26, 18, 8, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.16, -104, -16, 6, 0.3, -96, -16, 6, 0.42, -34, -26, 6, 0.52, -22, -30, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.16, -40, -6, 0, 0.3, -30, -6, 0, 0.42, 62, -6, 0, 0.52, 80, -6, 0, 0.66, 20, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.16, 4, -10, -14, 0.52, 4, -10, -14, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.16, -66, -22, 22, 0.3, -68, -22, 24, 0.52, -66, -22, 22, 0.7, -46, -18, 2, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.16, -98, 26, -4, 0.3, -100, 26, -4, 0.52, -98, 26, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.16, -22, 12, 7, 0.42, -4, 8, 7, 0.52, 0, 7, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.16, 36, 0, -1, 0.42, 12, 0, -1, 0.52, 8, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.42, 6, 11, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.16, -10, -17, -9, 0.42, -30, -14, -9, 0.52, -32, -14, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.16, 18, 0, 1, 0.42, 42, 0, 1, 0.52, 44, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.42, -18, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.16, 0, -0.05, -0.08, 0.3, 0, -0.03, 0.0, 0.42, 0, 0.0, 0.28, 0.52, 0, 0.0, 0.40, 0.7, 0, 0.0, 0.47, 1, 0, 0, 0.5] },
});

/* SABRE ch3 — "Kneeling Slam". Down onto BOTH knees with both blades driven
 * into the dirt on either side of him — a short blade cannot be buried by a
 * two-handed overhead, so this one gets its force from the drop, not the arc. */
clip('sabre_ch3_kneel', 1.02, {
  blend: 0.11, fps: 15, rm: 0.8, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.45, 'swoosh', { power: 1.15 }),
    ev(0.474, 'hitboxOn', { arc: 180, reach: 5.0, height: 0.75, power: 1.0, dmg: 2.4, launch: true, gb: true }),
    ev(0.53, 'vfx', { name: 'groundSlam' }), ev(0.637, 'hitboxOff'),
    ev(0.66, 'sfx', { name: 'impact_slam' }), ev(0.86, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.26, -22, -14, -1, 0.4, -18, -14, -1, 0.53, 40, -14, 0, 0.63, 38, -14, 0, 0.74, 34, -14, 0, 0.88, 16, -14, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.26, -10, -5, 0, 0.4, -8, -5, 0, 0.53, 18, -5, 0, 0.74, 15, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.26, -12, -6, 1, 0.4, -10, -6, 1, 0.53, 21, -6, 1, 0.74, 18, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.26, -18, -8, 1, 0.4, -15, -8, 1, 0.53, 30, -8, 1, 0.63, 28, -8, 1, 0.74, 24, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.26, 18, 12, 0, 0.53, -18, 12, 0, 0.74, -15, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.26, 22, 14, 0, 0.53, -30, 14, 0, 0.74, -26, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.26, 20, 6, 18, 0.53, -10, 6, -8, 0.74, -8, 6, -6, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.26, -118, 20, -20, 0.4, -112, 20, -20, 0.53, 34, 20, 22, 0.63, 42, 20, 24, 0.74, 38, 20, 23, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.26, -30, -22, 6, 0.4, -32, -22, 6, 0.53, -8, -22, 6, 0.74, -14, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.26, -40, -6, 0, 0.53, 30, -6, 0, 0.74, 24, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.26, 20, -6, -18, 0.53, -10, -6, 8, 0.74, -8, -6, 6, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.26, -122, -16, 22, 0.4, -116, -16, 22, 0.53, 30, -16, -20, 0.63, 38, -16, -22, 0.74, 34, -16, -21, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.26, -26, 20, -4, 0.4, -28, 20, -4, 0.53, -6, 20, -4, 0.74, -12, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.26, -18, 10, 7, 0.4, -14, 10, 7, 0.53, -88, 9, 7, 0.63, -96, 9, 7, 0.74, -92, 9, 7, 0.88, -36, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.26, 28, 0, -1, 0.4, 24, 0, -1, 0.53, 112, 0, -1, 0.63, 124, 0, -1, 0.74, 120, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.53, 22, 11, 0, 0.74, 20, 11, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.26, -22, -16, -9, 0.4, -18, -16, -9, 0.53, -90, -15, -9, 0.63, -98, -15, -9, 0.74, -94, -15, -9, 0.88, -38, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.26, 34, 0, 1, 0.4, 30, 0, 1, 0.53, 114, 0, 1, 0.63, 126, 0, 1, 0.74, 122, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.53, 24, -17, 0, 0.74, 22, -17, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.26, 0, 0.12, -0.04, 0.4, 0, 0.10, 0.04, 0.53, 0, -0.46, 0.46, 0.63, 0, -0.52, 0.58, 0.74, 0, -0.50, 0.68, 0.88, 0, -0.18, 0.76, 1, 0, 0, 0.8] },
});

/* SABRE ch4 — "Blade Storm". THREE revolutions where the glaive manages two,
 * because a 1.02 m blade can be spun faster than a 1.78 m one — and taken at a
 * tight radius with the elbows in, so the difference from Storm of Blades is
 * rate and compactness rather than reach. */
clip('sabre_ch4_storm', 0.99, {
  blend: 0.10, fps: 15, rm: 1.0, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.22, 'swoosh', { power: 1.0 }),
    ev(0.2525, 'hitboxOn', { arc: 180, reach: 4.6, height: 1.30, power: 0.9, dmg: 1.5, launch: true }),
    ev(0.48, 'swoosh', { power: 1.0 }), ev(0.707, 'hitboxOff'), ev(0.86, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.12, 10, -54, -8, 0.22, 6, 60, 6, 0.34, 4, 300, 8, 0.46, 3, 560, 8, 0.58, 3, 800, 8, 0.7, 4, 960, 6, 0.82, 6, 1030, 3, 1, 4, 1046, 0],
    spine1: [0, 3, -5, 0, 0.12, 6, -16, -3, 0.24, 3, 8, 4, 0.7, 2, 7, 3, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.12, 4, -18, -4, 0.24, 0, 10, 4, 0.7, -1, 8, 3, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.12, 8, -28, -6, 0.24, 2, 15, 8, 0.44, 0, 10, 8, 0.58, 0, 12, 8, 0.72, 1, 13, 7, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.12, 8, 20, 0, 0.28, 4, -14, 0, 0.58, 4, -12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.12, 2, 26, 2, 0.28, -6, -20, -2, 0.58, -6, -18, -2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.12, -2, 16, 16, 0.24, 2, 8, -6, 0.72, 2, 8, -6, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.12, -18, 24, -14, 0.24, -10, 22, -52, 0.46, -8, 22, -56, 0.72, -8, 22, -54, 0.88, -13, 21, -22, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.12, -60, -22, 6, 0.24, -44, -22, 6, 0.46, -42, -22, 6, 0.72, -43, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.24, 10, -6, 0, 0.72, 10, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.12, -2, -16, -16, 0.24, 2, -8, 6, 0.72, 2, -8, 6, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.12, -32, -24, 12, 0.24, -22, -20, 46, 0.46, -20, -20, 50, 0.72, -20, -20, 48, 0.88, -28, -18, 18, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.12, -56, 18, -4, 0.24, -40, 16, -4, 0.46, -38, 16, -4, 0.72, -39, 16, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.12, -26, 12, 7, 0.28, -14, 10, 7, 0.6, -15, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.12, 44, 0, -1, 0.28, 24, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.28, -2, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.12, -30, -17, -9, 0.28, -19, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.12, 48, 0, 1, 0.28, 30, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.28, -4, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.12, 0, -0.11, 0.04, 0.28, 0, -0.07, 0.30, 0.5, 0, -0.06, 0.58, 0.7, 0, -0.06, 0.82, 1, 0, 0, 1.0] },
});

/* SABRE ch5 — "Shoulder Rush". No reach to couch, so the hero leads with the
 * SHOULDER and carries both blades trailed back and low, then opens them into
 * a cross-cut on arrival. The run is the weapon; the blades are the finish. */
clip('sabre_ch5_barge', 1.27, {
  blend: 0.12, fps: 15, rm: 2.8, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.24, 'foot', { side: 'R' }), ev(0.38, 'foot', { side: 'L' }),
    ev(0.5, 'swoosh', { power: 1.2 }), ev(0.52, 'foot', { side: 'R' }),
    ev(0.538, 'hitboxOn', { arc: 58, reach: 4.8, height: 1.20, power: 1.0, dmg: 2.8, launch: true, gb: true }),
    ev(0.735, 'hitboxOff'), ev(0.78, 'foot', { side: 'L' }), ev(0.92, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.12, 18, -38, -4, 0.24, 20, -36, -3, 0.38, 20, -38, -5, 0.52, 20, -34, -3, 0.62, 14, -20, -1, 0.74, 2, 8, 3, 0.88, 4, -6, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.12, 8, -14, -2, 0.38, 9, -14, -2, 0.62, 6, -8, -1, 0.74, 0, 4, 1, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.12, 5, -16, -2, 0.38, 6, -16, -2, 0.62, 3, -9, 0, 0.74, -3, 5, 2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.12, 7, -24, -4, 0.38, 8, -24, -4, 0.62, 4, -14, -1, 0.74, -6, 8, 4, 0.88, -1, -4, 2, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.12, 2, 20, 0, 0.62, 2, 14, 0, 0.74, 4, -2, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.12, -12, 24, 1, 0.62, -12, 16, 1, 0.74, -6, -2, -1, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.12, -6, 14, 4, 0.52, -6, 14, 4, 0.66, 6, -2, 16, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.12, 26, 24, 30, 0.24, 28, 24, 32, 0.52, 28, 24, 32, 0.62, -14, 20, 4, 0.74, -66, 12, -28, 0.86, -40, 16, -10, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.12, -50, -20, 6, 0.52, -52, -20, 6, 0.62, -26, -22, 6, 0.74, -8, -26, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.12, -20, -6, 0, 0.52, -20, -6, 0, 0.74, 30, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.12, -6, -14, -4, 0.52, -6, -14, -4, 0.66, 6, 2, -16, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.12, 14, -24, -34, 0.24, 16, -24, -36, 0.52, 16, -24, -36, 0.62, -30, -20, -8, 0.74, -78, -12, 24, 0.86, -52, -16, 6, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.12, -38, 18, -4, 0.52, -40, 18, -4, 0.62, -14, 20, -4, 0.74, -4, 24, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.12, -42, 10, 7, 0.24, 14, 10, 7, 0.38, -46, 10, 7, 0.52, 12, 10, 7, 0.66, -42, 10, 7, 0.8, -8, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.12, 60, 0, -1, 0.24, 6, 0, -1, 0.38, 64, 0, -1, 0.52, 4, 0, -1, 0.66, 58, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.24, 18, 12, 0, 0.38, -20, 12, 0, 0.52, 20, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.12, 12, -16, -9, 0.24, -46, -16, -9, 0.38, 14, -16, -9, 0.52, -48, -16, -9, 0.66, 10, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.12, 6, 0, 1, 0.24, 62, 0, 1, 0.38, 4, 0, 1, 0.52, 66, 0, 1, 0.66, 8, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.24, -22, -18, 0, 0.38, 20, -18, 0, 0.52, -24, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.14, 0, -0.06, 0.26, 0.3, 0, -0.04, 0.76, 0.5, 0, -0.05, 1.42, 0.7, 0, -0.04, 2.10, 0.86, 0, -0.02, 2.56, 1, 0, 0, 2.8] },
});

/* =========================================================================
 *  WEAPON-SPECIFIC CLIPS
 * =========================================================================
 * The three weapon sets used to share one light chain, so a spear "Jab" and a
 * glaive "Rising Cut" turned the same 66 degrees of hip and the same 57 of
 * chest and differed only in playback rate. These are the clips that make the
 * sets read as different WEAPONS rather than the same swing at three speeds:
 *
 *   spear_*   the torso stays SQUARE (measured hips span 21 deg on the jab
 *             against the glaive C1's 66, chest 17 against 58) and the reach
 *             comes from the arm and the lunge — a thrust, not a sweep
 *   sabre_*   both arms cut, counter-phased, and the body turns 42-52 deg
 *             where the glaive turns 66-76; short blades, so the shoulder and
 *             the elbow do the work and the recovery is 8 frames
 * ========================================================================= */

/* SPEAR C1 — a real 30-degree jab. The old table declared a 30 deg thrust and
 * played attack_light_1, a horizontal sweep that wound the torso 22.6 deg and
 * followed through 55.9. This one keeps the whole chest inside a 17 deg span
 * and drives the point out along +Z and back along +Z: measured swept box
 * 0.96 x 1.19 x 3.02 m, i.e. three times as long as it is wide. */
clip('spear_thrust_1', 0.50, {
  blend: 0.07, fps: 15, rm: 0.75, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'effort' }), ev(0.26, 'swoosh', { power: 0.5 }),
    ev(0.29, 'hitboxOn', { arc: 30, reach: 3.3, height: 1.45, power: 0.5, dmg: 1.0 }),
    ev(0.48, 'hitboxOff'), ev(0.56, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 2, -18, 0, 0.29, 4, -10, 0, 0.4, 10, 2, 0, 0.48, 9, 2, 0, 0.64, 5, -4, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 2, -8, 0, 0.4, 5, 1, 0, 0.64, 3, -2, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, -1, -9, 1, 0.4, 3, 2, 1, 0.64, 0, -2, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, -4, -12, 1, 0.29, -2, -6, 1, 0.4, 4, 4, 1, 0.48, 3, 4, 1, 0.64, -1, -2, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 6, 10, 0, 0.4, 5, 4, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, -2, 12, 0, 0.4, -1, 4, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 24, -26, 10, 0.29, -5, 7, 10, 0.4, -10, 8, 9, 0.48, -10, 8, 9, 0.64, 24, -22, 7, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, 0, -30, -70, 0.29, 28, 21, -47, 0.4, -45, 16, -26, 0.48, -44, 16, -26, 0.64, 1, 20, -44, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -17, 6, 6, 0.29, -33, -20, 6, 0.4, -10, -20, 6, 0.48, -9, -20, 6, 0.64, -17, -11, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, -11, 2, -1, 0.29, 53, -4, 0, 0.4, 90, -2, 0, 0.48, 90, -2, 0, 0.64, 26, 0, 0, 1, -10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.2, -46, -20, -16, 0.29, -40, -16, -12, 0.4, -22, -8, -4, 0.48, -22, -8, -4, 0.64, -38, -18, -10, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -74, 24, -4, 0.4, -44, 12, -4, 0.64, -66, 22, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -10, 12, 7, 0.4, -34, 8, 7, 0.56, -22, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 18, 0, -1, 0.4, 44, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -24, -18, -9, 0.4, -2, -14, -9, 0.56, -14, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 34, 0, 1, 0.4, 18, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, -0.03, -0.04, 0.4, 0, -0.04, 0.5, 0.64, 0, -0.01, 0.68, 1, 0, 0, 0.75] },
});

/* SPEAR C2 — the second jab, dropped to the low line and led off the other
 * foot, so the pair reads as a one-two and not as one clip mirrored. */
clip('spear_thrust_2', 0.50, {
  blend: 0.07, fps: 15, rm: 0.75, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'effort' }), ev(0.26, 'swoosh', { power: 0.55 }),
    ev(0.29, 'hitboxOn', { arc: 32, reach: 3.45, height: 1.10, power: 0.5, dmg: 1.05 }),
    ev(0.48, 'hitboxOff'), ev(0.56, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 6, 14, 2, 0.29, 8, 6, 1, 0.4, 14, -4, 0, 0.48, 13, -4, 0, 0.64, 7, 2, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 4, 7, 1, 0.4, 7, -2, 0, 0.64, 4, 1, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 1, 8, 1, 0.4, 5, -2, 1, 0.64, 1, 1, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, 0, 10, 2, 0.29, 2, 5, 1, 0.4, 7, -2, 1, 0.48, 6, -2, 1, 0.64, 1, 2, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 6, 4, 0, 0.4, 6, 8, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, 0, 6, 0, 0.4, 4, 8, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 20, -26, -20, 0.29, 20, -9, -20, 0.4, -7, 10, 8, 0.48, -7, 10, 8, 0.64, 24, -13, 10, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, 0, -29, -70, 0.29, -19, -29, -70, 0.4, -54, 16, 21, 0.48, -53, 16, 21, 0.64, 13, 20, -31, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -26, 5, 6, 0.29, -26, 1, 6, 0.4, -13, -20, 6, 0.48, -12, -21, 6, 0.64, -2, 0, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, -6, 14, 0, 0.29, 33, 13, 0, 0.4, 90, -9, 0, 0.48, 90, -9, 0, 0.64, 8, -9, 0, 1, -10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.2, -40, -12, -10, 0.29, -36, -12, -8, 0.4, -18, -6, -2, 0.48, -18, -6, -2, 0.64, -36, -16, -10, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -70, 22, -4, 0.4, -40, 10, -4, 0.64, -64, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -22, 12, 7, 0.4, -4, 8, 7, 0.56, -16, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 32, 0, -1, 0.4, 16, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -12, -14, -9, 0.4, -40, -18, -9, 0.56, -26, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 20, 0, 1, 0.4, 50, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, -0.04, -0.04, 0.4, 0, -0.08, 0.5, 0.64, 0, -0.02, 0.68, 1, 0, 0, 0.75] },
});

/* SPEAR C5 — "Skewer". The longest single strike in the game, and now the
 * longest MEASURED one: the point reaches 2.19 m from the pelvis at full
 * extension against the glaive C6's 1.95 and the sabre C5's 1.45, even though
 * the spear MESH is the shortest of the three (tip 1.37 m from the grip
 * against the glaive's 1.79). The spear is cocked over the shoulder for 22
 * frames, then the hero falls into a lunge deep enough to drop the hips
 * 0.22 m, and the root carries him another 2.6 m through the window. */
clip('spear_skewer', 0.78, {
  blend: 0.10, fps: 15, rm: 2.6, tags: 'attack',
  events: [ev(0.30, 'voice', { tag: 'shout' }), ev(0.50, 'swoosh', { power: 1.0 }),
    ev(0.54, 'hitboxOn', { arc: 34, reach: 4.2, height: 1.20, power: 1.0, dmg: 2.4, launch: true, gb: true }),
    ev(0.78, 'hitboxOff'), ev(0.90, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.24, 0, -24, -2, 0.42, -2, -30, -3, 0.54, 12, -2, 0, 0.66, 24, 4, 0, 0.78, 22, 4, 0, 0.9, 12, -6, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.24, 1, -10, -1, 0.42, 0, -13, -1, 0.54, 6, -1, 0, 0.66, 10, 2, 0, 0.78, 9, 2, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.24, -2, -12, -1, 0.42, -3, -15, -2, 0.54, 4, -1, 1, 0.66, 9, 2, 1, 0.78, 8, 2, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.24, -6, -18, -2, 0.42, -8, -22, -3, 0.54, 6, 0, 1, 0.66, 14, 5, 1, 0.78, 13, 5, 1, 0.9, 4, -4, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.24, 8, 16, 0, 0.54, 2, 2, 0, 0.66, -4, 0, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.24, 2, 18, 0, 0.54, -2, 2, 0, 0.66, -8, 0, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.24, 24, -26, 10, 0.42, 24, -26, 10, 0.54, 2, 6, 14, 0.66, 2, 6, 10, 0.78, 2, 6, 10, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.24, 0, 0, -25, 0.42, 0, -14, -25, 0.54, -15, 20, -40, 0.66, -107, 20, 0, 0.78, -102, 19, 0, 0.9, -50, 20, 2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.24, -2, 22, 6, 0.42, -2, 22, 6, 0.54, -20, -22, 6, 0.66, -2, -22, 6, 0.78, -2, -22, 6, 0.9, -40, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.24, -2, 0, 0, 0.42, -2, 0, 0, 0.54, 50, -1, 0, 0.66, 90, -6, 0, 0.78, 90, -6, 0, 0.9, 30, -4, 0, 1, -10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.24, -54, -22, -20, 0.42, -58, -24, -22, 0.54, -34, -12, -8, 0.66, -14, -4, 0, 0.78, -14, -4, 0, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.24, -82, 26, -4, 0.42, -86, 28, -4, 0.54, -54, 14, -4, 0.66, -34, 8, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.24, -10, 14, 7, 0.42, -4, 16, 7, 0.54, -46, 10, 7, 0.66, -74, 8, 7, 0.78, -70, 8, 7, 0.9, -30, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.24, 16, 0, -1, 0.42, 10, 0, -1, 0.54, 44, 0, -1, 0.66, 66, 0, -1, 0.78, 62, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.66, -14, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.24, -26, -18, -9, 0.42, -30, -20, -9, 0.54, -8, -14, -9, 0.66, 22, -12, -9, 0.78, 20, -12, -9, 0.9, -4, -14, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.24, 36, 0, 1, 0.42, 40, 0, 1, 0.54, 20, 0, 1, 0.66, 8, 0, 1, 0.78, 10, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.66, 22, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.24, 0, 0.02, -0.10, 0.42, 0, 0.05, -0.18, 0.54, 0, -0.10, 0.55, 0.66, 0, -0.22, 1.55, 0.78, 0, -0.21, 2.15, 1, 0, 0, 2.6] },
});

/* SPEAR C6 — "Dragon Tail". Two revolutions taken LOW: the hero drops into a
 * crouch and whips the haft round at knee height twice before rising out of it
 * on the exit, so the finisher is a leg-sweep with a polearm and not the C4
 * pole spin slowed down. Tip stays between 0.3 and 1.3 m for both passes. */
clip('spear_dragon_tail', 1.06, {
  blend: 0.11, fps: 15, rm: 1.6, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.22, 'swoosh', { power: 0.95 }),
    ev(0.2516, 'hitboxOn', { arc: 180, reach: 4.4, height: 0.75, power: 0.95, dmg: 1.6, launch: true, gb: true }),
    ev(0.5, 'swoosh', { power: 1.0 }), ev(0.723, 'hitboxOff'), ev(0.88, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.14, 12, -54, -8, 0.25, 6, 26, 3, 0.37, 6, 190, 3, 0.5, 6, 356, 3, 0.62, 6, 520, 3, 0.74, 6, 634, 3, 0.86, 6, 690, 2, 1, 4, 702, 0],
    spine1: [0, 3, -5, 0, 0.14, 8, -16, -3, 0.25, 4, 8, 2, 0.62, 4, 6, 2, 0.86, 4, 2, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.14, 6, -18, -3, 0.25, 3, 8, 2, 0.62, 3, 6, 2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.14, 10, -28, -6, 0.25, 6, 14, 4, 0.5, 6, 10, 4, 0.74, 6, 12, 4, 0.88, 2, 6, 2, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.14, 4, 22, 0, 0.3, -2, -14, 0, 0.62, -2, -12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.14, 0, 26, 2, 0.3, -8, -20, -2, 0.62, -8, -18, -2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.14, -4, 20, 20, 0.25, -5, 11, 10, 0.74, -5, 11, 10, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.14, 8, 26, 4, 0.25, -31, 10, -8, 0.44, -31, 10, -8, 0.62, -42, 11, -7, 0.74, -42, 11, -7, 0.88, -28, 18, 2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.14, -44, -30, 8, 0.25, -2, -33, 6, 0.62, -2, -30, 6, 0.74, -2, -30, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.14, -20, -14, 0, 0.25, 90, -1, 0, 0.62, 90, -1, 0, 0.74, 90, -1, 0, 0.9, 24, -4, 0, 1, -10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.14, -12, -2, -26, 0.25, -60, -20, 38, 0.5, -62, -20, 40, 0.74, -58, -20, 36, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.14, -28, 8, -4, 0.25, -24, 16, -6, 0.62, -22, 16, -6, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.14, -2, 24, 7, 0.25, -36, -8, 9, 0.37, -14, 8, 7, 0.5, -36, -8, 9, 0.62, -16, 6, 7, 0.78, -26, 2, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.14, 14, 0, -1, 0.25, 58, 0, -1, 0.37, 34, 0, -1, 0.5, 58, 0, -1, 0.62, 36, 0, -1, 0.78, 46, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.14, -24, -32, -9, 0.25, -14, 12, -11, 0.37, -38, -10, -9, 0.5, -14, 12, -11, 0.62, -36, -10, -9, 0.78, -24, -6, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.14, 38, 0, 1, 0.25, 34, 0, 1, 0.37, 58, 0, 1, 0.5, 34, 0, 1, 0.62, 56, 0, 1, 0.78, 44, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.14, 0, -0.08, -0.06, 0.25, 0, -0.18, 0.24, 0.5, 0, -0.18, 0.86, 0.74, 0, -0.15, 1.36, 0.9, 0, -0.04, 1.54, 1, 0, 0, 1.6] },
});

/* SABRE C1 — a short diagonal from high-right to low-left, thrown by the
 * SHOULDER: measured hips span 42 deg and chest 36, against the glaive C1's
 * 66 and 58, and the left sabre cuts the opposite diagonal on the same frames
 * so the silhouette reads as two blades. Recovery is 8 frames. */
clip('sabre_cross_1', 0.50, {
  blend: 0.06, fps: 15, rm: 0.65, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'effort' }), ev(0.28, 'swoosh', { power: 0.45 }),
    ev(0.32, 'hitboxOn', { arc: 74, reach: 2.05, height: 1.35, power: 0.42, dmg: 1.0 }),
    ev(0.55, 'hitboxOff'), ev(0.62, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.18, 2, -20, -3, 0.32, 3, -8, -1, 0.44, 5, 10, 2, 0.55, 7, 22, 4, 0.7, 5, 14, 3, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.18, 2, -8, -1, 0.44, 3, 5, 1, 0.55, 4, 10, 2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.18, -1, -9, -1, 0.44, 1, 6, 1, 0.55, 2, 12, 2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.18, -6, -16, -3, 0.32, -4, -6, -1, 0.44, -2, 10, 3, 0.55, 0, 20, 5, 0.7, -2, 13, 3, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.18, 6, 14, 0, 0.5, 6, 2, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.18, -3, 16, 0, 0.5, -3, 2, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.18, 2, 6, 10, 0.32, 1, 8, 10, 0.44, 1, 8, 11, 0.55, 1, 8, 11, 0.7, -6, 5, 16, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.18, -18, 22, 5, 0.32, -28, 23, 5, 0.44, -27, 10, 3, 0.55, -12, 7, 3, 0.7, -34, 4, 4, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.18, -17, -23, 6, 0.32, -17, -17, 6, 0.44, -3, -23, 6, 0.55, -2, -29, 6, 0.7, -44, -12, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.18, 30, -11, 0, 0.32, 69, -11, 0, 0.44, 70, -15, 0, 0.55, 75, -15, 0, 0.7, 10, 4, 0, 1, -10, -6, 0],
    // the off-hand sabre runs the opposite diagonal on the same frames
    clavicleL: [0, 2, -6, -10, 0.18, 0, -10, -14, 0.44, 4, -2, -4, 0.55, 4, 0, -2, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.18, -12, -8, 4, 0.32, -26, -12, -6, 0.44, -52, -20, -22, 0.55, -66, -24, -30, 0.7, -46, -18, -18, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.18, -34, 10, -4, 0.32, -20, 8, -4, 0.44, -34, 16, -6, 0.55, -58, 24, -8, 1, -62, 20, -4],
    handL: [0, -14, 6, 0, 0.18, -6, 10, 0, 0.44, -22, 2, 0, 1, -14, 6, 0],
    thighL: [0, -14, 10, 7, 0.18, -18, 12, 7, 0.44, -22, 6, 7, 0.6, -16, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.18, 28, 0, -1, 0.44, 30, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.18, -14, -18, -9, 0.44, -10, -12, -9, 0.6, -16, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.18, 24, 0, 1, 0.44, 22, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.18, 0, 0.01, -0.04, 0.44, 0, -0.02, 0.4, 0.7, 0, -0.01, 0.6, 1, 0, 0, 0.65] },
});

/* SABRE C2 — the answering cut, low-left to high-right, LEFT blade leading. */
clip('sabre_cross_2', 0.50, {
  blend: 0.06, fps: 15, rm: 0.6, tags: 'attack',
  events: [ev(0.12, 'voice', { tag: 'effort' }), ev(0.26, 'swoosh', { power: 0.45 }),
    ev(0.30, 'hitboxOn', { arc: 76, reach: 2.05, height: 1.30, power: 0.42, dmg: 1.05 }),
    ev(0.54, 'hitboxOff'), ev(0.6, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.18, 6, 20, 4, 0.3, 5, 8, 2, 0.42, 3, -12, -2, 0.54, 1, -24, -4, 0.7, 2, -16, -3, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.18, 4, 9, 2, 0.42, 2, -5, -1, 0.54, 1, -10, -2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.18, 1, 10, 2, 0.42, 0, -6, -1, 0.54, -2, -12, -2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.18, -1, 18, 4, 0.3, -2, 7, 2, 0.42, -5, -10, -2, 0.54, -8, -20, -4, 0.7, -6, -13, -3, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.18, 6, 2, 0, 0.5, 6, 16, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.18, -2, 4, 0, 0.5, -4, 18, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.18, 2, 8, 11, 0.3, 2, 8, 11, 0.42, 2, 13, 11, 0.54, 0, 13, 10, 0.7, 10, 4, 14, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.18, 15, 22, 26, 0.3, -1, 22, 20, 0.42, -30, 22, 32, 0.54, -30, 22, 11, 0.7, -8, 12, 0, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.18, -38, -22, 6, 0.3, -38, -22, 6, 0.42, -35, -22, 6, 0.54, -38, -19, 6, 0.7, -20, -18, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.18, 67, -13, 0, 0.3, 70, -13, 0, 0.42, 90, -13, 0, 0.54, 87, -10, 0, 0.7, 14, -12, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.18, 4, -2, -4, 0.42, 0, -10, -14, 0.54, -2, -12, -18, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.18, -60, -24, -28, 0.3, -46, -18, -18, 0.42, -22, -10, 2, 0.54, -10, -6, 8, 0.7, -26, -12, -2, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.18, -56, 22, -8, 0.3, -34, 16, -6, 0.42, -18, 8, -4, 0.54, -14, 6, -4, 1, -62, 20, -4],
    handL: [0, -14, 6, 0, 0.18, -22, 2, 0, 0.42, -6, 10, 0, 1, -14, 6, 0],
    thighL: [0, -14, 10, 7, 0.18, -10, 12, 7, 0.42, -24, 6, 7, 0.6, -18, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.18, 18, 0, -1, 0.42, 32, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.18, -24, -18, -9, 0.42, -8, -12, -9, 0.6, -14, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.18, 34, 0, 1, 0.42, 20, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.18, 0, -0.02, -0.03, 0.42, 0, -0.01, 0.36, 0.7, 0, 0, 0.56, 1, 0, 0, 0.6] },
});

/* SABRE C5 — "Rushing Blades". The hero steps THROUGH the enemy with both
 * sabres scissoring at chest height: the right blade sweeps right-to-left on
 * a flat line while the left cuts back the other way. Used to be C1 slowed
 * from rate 1.60 to 1.10, which is why the fifth hit of the chain looked
 * exactly like the first. */
clip('sabre_rush', 0.50, {
  blend: 0.07, fps: 15, rm: 0.95, tags: 'attack',
  events: [ev(0.12, 'voice', { tag: 'shout' }), ev(0.24, 'swoosh', { power: 0.7 }),
    ev(0.28, 'hitboxOn', { arc: 96, reach: 2.4, height: 1.35, power: 0.6, dmg: 1.3 }),
    ev(0.52, 'hitboxOff'), ev(0.62, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.16, 4, -24, -4, 0.28, 6, -10, -2, 0.4, 7, 12, 2, 0.52, 6, 28, 5, 0.68, 5, 16, 3, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.16, 3, -10, -2, 0.4, 4, 6, 1, 0.52, 4, 13, 2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.16, 0, -11, -2, 0.4, 2, 7, 1, 0.52, 1, 14, 3, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.16, -4, -18, -4, 0.28, -2, -8, -2, 0.4, -1, 12, 3, 0.52, -1, 24, 6, 0.68, -2, 14, 4, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.16, 6, 14, 0, 0.5, 6, 0, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.16, -4, 16, 0, 0.5, -2, 0, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.16, 4, 6, 10, 0.28, 2, 6, 17, 0.4, 2, 6, 17, 0.52, 2, 6, 17, 0.68, -9, 6, 12, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.16, 0, 20, 6, 0.28, -30, 20, -13, 0.4, -30, 17, -36, 0.52, -15, 17, -42, 0.68, 20, 24, -50, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.16, -4, -20, 6, 0.28, -20, -22, 6, 0.4, -20, -28, 6, 0.52, -20, -28, 6, 0.68, -2, 4, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.16, 30, 0, 0, 0.28, 90, 3, 0, 0.4, 90, 0, 0, 0.52, 90, -1, 0, 0.68, 19, 3, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.16, 0, -8, -16, 0.4, 4, -4, -6, 0.52, 5, -2, -4, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.16, -60, -26, -30, 0.28, -50, -20, -22, 0.4, -24, -8, 4, 0.52, -12, -4, 14, 0.68, -30, -14, -2, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.16, -52, 22, -8, 0.28, -32, 14, -6, 0.4, -20, 8, -4, 0.52, -18, 6, -4, 1, -62, 20, -4],
    handL: [0, -14, 6, 0, 0.16, -24, 2, 0, 0.4, -4, 12, 0, 1, -14, 6, 0],
    thighL: [0, -14, 10, 7, 0.16, -8, 14, 7, 0.32, -44, 8, 7, 0.52, -18, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.16, 16, 0, -1, 0.32, 54, 0, -1, 0.52, 22, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.16, -28, -20, -9, 0.32, 6, -12, -9, 0.52, -34, -18, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.16, 40, 0, 1, 0.32, 16, 0, 1, 0.52, 46, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.16, 0, 0, -0.05, 0.32, 0, -0.03, 0.42, 0.52, 0, -0.02, 0.78, 1, 0, 0, 0.95] },
});

/* SPEAR C3 — "Haft Sweep". The one place the spear stops thrusting: the hands
 * stay at the balance point and the WHOLE POLE is driven across at waist
 * height in a single flat pass. Through the whole active window the tip stays
 * between 0.49 and 1.10 m off the ground at a steady 1.77-1.84 m radius — a
 * flat disc at knee-to-waist height, where the glaive C3 it used to borrow is
 * a rising diagonal that lifts the tip from 0.77 to 3.04 m. */
clip('spear_haft_sweep', 0.62, {
  blend: 0.08, fps: 15, rm: 0.95, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'effort' }), ev(0.28, 'swoosh', { power: 0.65 }),
    ev(0.3145, 'hitboxOn', { arc: 100, reach: 3.2, height: 1.10, power: 0.6, dmg: 1.15 }),
    ev(0.524, 'hitboxOff'), ev(0.66, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.18, 12, -34, -5, 0.32, 12, -14, -2, 0.44, 10, 14, 2, 0.56, 8, 34, 4, 0.72, 6, 22, 3, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.18, 7, -14, -2, 0.44, 6, 6, 1, 0.56, 5, 14, 2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.18, 5, -16, -3, 0.44, 4, 7, 2, 0.56, 3, 15, 3, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.18, 8, -24, -5, 0.32, 8, -10, -2, 0.44, 6, 10, 3, 0.56, 5, 24, 5, 0.72, 2, 15, 3, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.18, 6, 16, 0, 0.5, 4, -4, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.18, 0, 20, 2, 0.5, -4, -6, -2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.18, 0, 16, 14, 0.32, -2, 8, 10, 0.56, -2, 8, 10, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.18, 4, 26, 4, 0.32, -40, 18, -14, 0.44, -42, 18, -14, 0.56, -40, 18, -14, 0.72, -24, 20, 0, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.18, -46, -28, 8, 0.32, -6, -26, 6, 0.56, -6, -26, 6, 0.72, -30, -24, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.18, -18, -14, 0, 0.32, 88, -2, 0, 0.44, 90, 0, 0, 0.56, 88, 2, 0, 0.74, 22, -4, 0, 1, -10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.18, -16, -6, -22, 0.32, -50, -20, 28, 0.44, -54, -20, 32, 0.56, -50, -20, 28, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.18, -34, 10, -4, 0.32, -28, 16, -6, 0.56, -26, 16, -6, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.18, -6, 20, 7, 0.44, -34, -2, 7, 0.62, -20, 8, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.18, 14, 0, -1, 0.44, 48, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.18, -30, -26, -9, 0.44, -4, -8, -9, 0.62, -16, -14, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.18, 44, 0, 1, 0.44, 16, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.18, 0, -0.05, -0.05, 0.44, 0, -0.06, 0.52, 0.72, 0, -0.02, 0.86, 1, 0, 0, 0.95] },
});

/* SABRE C3 — "Cross Cut". C1 and C2 counter-phase; this one fires both blades
 * on the SAME frame, right high-to-low-left and left low-to-high-right, so the
 * two arms cross in front of the chest at the contact frame. It is the one
 * pose in the set where the silhouette is unmistakably two weapons. */
clip('sabre_x_cut', 0.56, {
  blend: 0.07, fps: 15, rm: 0.75, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'effort' }), ev(0.24, 'swoosh', { power: 0.6 }),
    ev(0.2768, 'hitboxOn', { arc: 88, reach: 2.2, height: 1.30, power: 0.5, dmg: 1.15 }),
    ev(0.5074, 'hitboxOff'), ev(0.6, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.18, 6, -18, -3, 0.3, 6, -8, -1, 0.42, 6, 8, 1, 0.54, 6, 18, 3, 0.7, 5, 10, 2, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.18, 4, -8, -1, 0.42, 4, 4, 1, 0.54, 4, 8, 1, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.18, 1, -9, -1, 0.42, 1, 4, 1, 0.54, 1, 9, 2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.18, -6, -14, -3, 0.3, -6, -6, -1, 0.42, -4, 6, 2, 0.54, -3, 14, 3, 0.7, -3, 8, 2, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.18, 6, 12, 0, 0.5, 6, 6, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.18, -6, 14, 0, 0.5, 2, 6, 0, 1, -4, 14, 0],
    // right blade: high right -> low left
    clavicleR: [0, 2, 6, 10, 0.18, 4, 8, 8, 0.42, 0, 8, 14, 0.54, 0, 8, 14, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.18, -34, 24, 2, 0.3, -34, 22, 0, 0.42, -26, 12, -2, 0.54, -10, 6, -4, 0.7, -20, 12, 2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.18, -22, -24, 6, 0.3, -14, -18, 6, 0.42, -4, -24, 6, 0.54, -2, -30, 6, 0.7, -40, -18, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.18, 26, -12, 0, 0.3, 66, -12, 0, 0.42, 72, -16, 0, 0.54, 78, -16, 0, 0.72, 8, 2, 0, 1, -10, -6, 0],
    // left blade: low left -> high right, on the SAME frames
    clavicleL: [0, 2, -6, -10, 0.18, -4, -8, -8, 0.42, 0, -8, -14, 0.54, 0, -8, -14, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.18, -8, -6, 10, 0.3, -18, -10, 0, 0.42, -34, -16, -18, 0.54, -50, -22, -30, 0.7, -38, -16, -14, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.18, -18, 8, -4, 0.3, -12, 8, -4, 0.42, -24, 14, -6, 0.54, -44, 22, -8, 1, -62, 20, -4],
    handL: [0, -14, 6, 0, 0.18, -4, 10, 0, 0.42, -26, 2, 0, 1, -14, 6, 0],
    thighL: [0, -14, 10, 7, 0.18, -20, 12, 7, 0.42, -26, 8, 7, 0.6, -18, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.18, 30, 0, -1, 0.42, 36, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.18, -12, -18, -9, 0.42, -8, -14, -9, 0.6, -14, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.18, 22, 0, 1, 0.42, 18, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.18, 0, 0.01, -0.05, 0.42, 0, -0.03, 0.46, 0.7, 0, -0.01, 0.7, 1, 0, 0, 0.75] },
});

/* SPEAR C4 — "Wheeling Haft". A polearm gripped at the middle does not sweep
 * a flat disc, it WHEELS: the spin axis is pitched 26 degrees forward, so the
 * point goes over the shoulder on one side and under the arm on the other:
 * measured tip band 0.53-2.47 m, i.e. 1.94 m of climb and fall inside one
 * revolution, against the glaive C4's near-vertical axis and 1.18 m band.
 * That is the difference between the two 360s. */
clip('spear_haft_spin', 0.94, {
  blend: 0.10, fps: 15, rm: 1.2, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.22, 'swoosh', { power: 0.9 }),
    ev(0.2553, 'hitboxOn', { arc: 180, reach: 3.6, height: 1.20, power: 0.8, dmg: 1.4, launch: true }),
    ev(0.5, 'swoosh', { power: 0.85 }), ev(0.702, 'hitboxOff'), ev(0.85, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.14, 14, -58, -8, 0.26, 26, 40, 4, 0.4, 26, 150, 5, 0.54, 26, 250, 5, 0.68, 24, 330, 4, 0.82, 14, 372, 2, 1, 4, 382, 0],
    spine1: [0, 3, -5, 0, 0.14, 8, -18, -3, 0.26, 6, 10, 2, 0.68, 6, 8, 2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.14, 6, -20, -4, 0.26, 5, 10, 2, 0.68, 5, 8, 2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.14, 10, -30, -6, 0.26, 8, 16, 4, 0.54, 8, 12, 4, 0.82, 4, 8, 2, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.14, 6, 22, 0, 0.3, 0, -14, 0, 0.6, 0, -12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.14, 0, 26, 2, 0.3, -6, -18, -2, 0.6, -6, -16, -2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.14, -4, 20, 20, 0.26, -2, 8, 10, 0.68, -2, 8, 10, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.14, 8, 26, 4, 0.26, -50, 18, -12, 0.4, -52, 18, -12, 0.54, -50, 18, -12, 0.68, -48, 18, -12, 0.84, -30, 20, 2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.14, -44, -30, 8, 0.26, -6, -26, 6, 0.54, -6, -26, 6, 0.68, -10, -26, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.14, -20, -14, 0, 0.26, 88, -3, 0, 0.54, 88, -3, 0, 0.68, 86, -3, 0, 0.86, 24, -4, 0, 1, -10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.14, -12, -2, -26, 0.26, -58, -20, 36, 0.54, -60, -20, 38, 0.68, -56, -20, 34, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.14, -28, 8, -4, 0.26, -26, 16, -6, 0.54, -24, 16, -6, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.14, -4, 24, 7, 0.26, -38, -8, 9, 0.4, -12, 8, 7, 0.54, -38, -8, 9, 0.68, -14, 6, 7, 0.84, -22, 2, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.14, 14, 0, -1, 0.26, 60, 0, -1, 0.4, 22, 0, -1, 0.54, 60, 0, -1, 0.68, 24, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.14, -24, -32, -9, 0.26, -8, 12, -11, 0.4, -38, -10, -9, 0.54, -8, 12, -11, 0.68, -34, -10, -9, 0.84, -18, -6, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.14, 38, 0, 1, 0.26, 18, 0, 1, 0.4, 60, 0, 1, 0.54, 18, 0, 1, 0.68, 58, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.14, 0, -0.04, -0.06, 0.26, 0, -0.10, 0.22, 0.54, 0, -0.10, 0.68, 0.82, 0, -0.03, 1.08, 1, 0, 0, 1.2] },
});

/* SABRE C4 — "Twin Cyclone". The only attack in the library that LEAVES THE
 * GROUND: the hero jumps, tucks, turns 400 degrees with both sabres thrown
 * out at chest height, and lands on the beat the window closes. Root Y goes to
 * +0.42 m and back, and the shins fold to 96 degrees at the apex, which is
 * what makes a short-blade spin read as a different move from a polearm's. */
clip('sabre_cyclone', 0.94, {
  blend: 0.10, fps: 15, rm: 1.1, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.22, 'swoosh', { power: 0.8 }),
    ev(0.2633, 'hitboxOn', { arc: 180, reach: 2.7, height: 1.25, power: 0.7, dmg: 1.3, launch: true }),
    ev(0.5, 'swoosh', { power: 0.8 }), ev(0.718, 'hitboxOff'), ev(0.72, 'land'), ev(0.86, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.16, 16, -48, -6, 0.28, 4, 46, 4, 0.42, 2, 168, 5, 0.56, 2, 286, 5, 0.7, 6, 372, 3, 0.84, 10, 398, 1, 1, 4, 404, 0],
    spine1: [0, 3, -5, 0, 0.16, 9, -14, -2, 0.28, 2, 10, 2, 0.7, 2, 8, 2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.16, 7, -16, -3, 0.28, 0, 11, 3, 0.7, 0, 9, 2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.16, 11, -26, -5, 0.28, -1, 16, 5, 0.56, -2, 14, 5, 0.7, 0, 12, 4, 0.84, 2, 8, 2, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.16, 8, 20, 0, 0.32, 2, -16, 0, 0.62, 2, -14, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.16, 2, 24, 2, 0.32, -8, -20, -2, 0.62, -8, -18, -2, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.16, -2, 18, 18, 0.28, 2, 6, 16, 0.7, 2, 6, 16, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.16, 4, 28, 24, 0.28, -30, 18, -34, 0.42, -32, 18, -36, 0.56, -32, 18, -36, 0.7, -28, 18, -32, 0.84, -20, 20, -6, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.16, -44, -28, 8, 0.28, -20, -28, 6, 0.56, -20, -28, 6, 0.7, -24, -28, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.16, -18, -12, 0, 0.28, 88, -2, 0, 0.56, 88, -2, 0, 0.7, 84, -2, 0, 0.86, 20, -4, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.16, -2, -18, -18, 0.28, 2, -6, -16, 0.7, 2, -6, -16, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.16, -12, -4, -2, 0.28, -34, -18, 34, 0.42, -36, -18, 36, 0.56, -36, -18, 36, 0.7, -32, -18, 32, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.16, -30, 10, -4, 0.28, -22, 22, -6, 0.56, -22, 22, -6, 1, -62, 20, -4],
    handL: [0, -14, 6, 0, 0.28, -80, 2, 0, 0.56, -80, 2, 0, 1, -14, 6, 0],
    thighL: [0, -14, 10, 7, 0.16, -40, 22, 7, 0.3, -66, 6, 8, 0.5, -70, 4, 8, 0.66, -30, 8, 7, 0.76, -30, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.16, 54, 0, -1, 0.3, 90, 0, -1, 0.5, 96, 0, -1, 0.66, 40, 0, -1, 0.76, 46, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.4, 22, 10, 0, 0.7, -10, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.16, -44, -26, -9, 0.3, -58, -12, -9, 0.5, -62, -10, -9, 0.66, -26, -14, -9, 0.76, -28, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.16, 58, 0, 1, 0.3, 86, 0, 1, 0.5, 92, 0, 1, 0.66, 44, 0, 1, 0.76, 50, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.4, 20, -16, 0, 0.7, -12, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.16, 0, -0.14, -0.06, 0.28, 0, 0.26, 0.22, 0.42, 0, 0.42, 0.46, 0.56, 0, 0.34, 0.7, 0.68, 0, 0.02, 0.9, 0.78, 0, -0.12, 1.0, 1, 0, 0, 1.1] },
});

/* =========================================================================
 *  MUSOU
 * =========================================================================
 * The grinder loop and the finisher. The flurry used to borrow the light-chain
 * finisher, which turns the hips ~410 deg over 0.74 s (557 deg/s) while
 * player.js ALSO yawed the entity at 520 deg/s: the hero spun at 1077 deg/s,
 * three revolutions a second, which is a blur and not a move. This clip turns
 * a known 360 deg per cycle so the moveset can declare the remainder — see
 * MUSOU.spin in src/combat/moveset.js. */
clip('musou_grind', 0.72, {
  blend: 0.10, fps: 15, rm: 0, tags: 'attack',
  events: [ev(0.1, 'swoosh', { power: 0.9 }), ev(0.6, 'swoosh', { power: 0.9 })],
  R: on(GUARD, {
    // exactly one revolution per cycle, so re-triggering is seamless
    hips: [0, 6, 0, 2, 0.25, 5, 90, 3, 0.5, 6, 180, 2, 0.75, 5, 270, 3, 1, 6, 360, 2],
    spine1: [0, 4, 4, 1, 0.5, 4, 4, 1, 1, 4, 4, 1],
    spine2: [0, 1, 5, 2, 0.5, 1, 5, 2, 1, 1, 5, 2],
    chest: [0, 2, 10, 5, 0.25, 3, 12, 6, 0.5, 2, 10, 5, 0.75, 3, 12, 6, 1, 2, 10, 5],
    neck: [0, 4, -10, 0, 0.5, 4, -10, 0, 1, 4, -10, 0],
    head: [0, -6, -14, -2, 0.5, -6, -14, -2, 1, -6, -14, -2],
    clavicleR: [0, 2, 8, -10, 1, 2, 8, -10],
    upperArmR: [0, -45, 19, 13, 0.25, -48, 19, 13, 0.5, -45, 19, 13, 0.75, -48, 19, 13, 1, -45, 19, 13],
    foreArmR: [0, -20, -22, 6, 0.5, -18, -22, 6, 1, -20, -22, 6],
    handR: [0, 90, -6, 0, 1, 90, -6, 0],
    clavicleL: [0, 2, -8, 10, 1, 2, -8, 10],
    upperArmL: [0, -64, -22, 42, 0.25, -68, -22, 44, 0.5, -64, -22, 42, 0.75, -68, -22, 44, 1, -64, -22, 42],
    foreArmL: [0, -20, 16, -6, 0.5, -18, 16, -6, 1, -20, 16, -6],
    thighL: [0, -22, 4, 8, 0.25, -30, -8, 9, 0.5, -10, 8, 7, 0.75, -30, -8, 9, 1, -22, 4, 8],
    shinL: [0, 34, 0, -1, 0.25, 52, 0, -1, 0.5, 18, 0, -1, 0.75, 52, 0, -1, 1, 34, 0, -1],
    thighR: [0, -14, -10, -10, 0.25, -6, 12, -11, 0.5, -30, -10, -9, 0.75, -6, 12, -11, 1, -14, -10, -10],
    shinR: [0, 30, 0, 1, 0.25, 16, 0, 1, 0.5, 52, 0, 1, 0.75, 16, 0, 1, 1, 30, 0, 1],
  }),
  T: { root: [0, 0, 0.04, 0, 0.25, 0, 0.06, 0, 0.5, 0, 0.04, 0, 0.75, 0, 0.06, 0, 1, 0, 0.04, 0] },
});

/* MUSOU FINISHER — out of the grind, up onto the toes with the weapon thrown
 * overhead in a rising 360, then the whole body drives it down into the ground
 * on the beat the screen flashes. Two rings go out (see `extra` on the move):
 * the blade's own arc and then the shockwave. */
clip('musou_finish', 1.15, {
  blend: 0.12, fps: 15, rm: 1.8, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.34, 'swoosh', { power: 1.2 }),
    ev(0.48, 'hitboxOn', { arc: 180, reach: 7.5, height: 1.4, power: 1.0, dmg: 3.0, launch: true, gb: true }),
    ev(0.48, 'vfx', { name: 'groundSlam' }), ev(0.58, 'hitboxOff'), ev(0.94, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.14, 12, -60, -8, 0.26, 2, 60, 6, 0.32, -12, 220, 8, 0.4, -18, 330, 4, 0.48, 26, 372, 0, 0.6, 24, 366, 0, 0.76, 12, 358, 0, 1, 4, 346, 0],
    spine1: [0, 3, -5, 0, 0.14, 8, -18, -3, 0.32, -6, 12, 4, 0.48, 14, 2, 0, 0.76, 6, -2, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.14, 6, -20, -4, 0.32, -10, 14, 5, 0.48, 16, 2, 0, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.14, 10, -32, -7, 0.26, 0, 20, 8, 0.32, -22, 26, 10, 0.4, -26, 20, 6, 0.48, 22, 4, 2, 0.6, 20, 2, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.14, 8, 24, 0, 0.32, -10, -12, 0, 0.48, 14, 2, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.14, 2, 28, 2, 0.32, -20, -14, -2, 0.48, 22, 0, 0, 0.68, -6, 6, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.14, -4, 22, 22, 0.32, -14, -8, -18, 0.48, 14, 0, -8, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.14, 6, 30, 6, 0.26, -40, 12, -8, 0.32, -96, -12, 26, 0.4, -104, -16, 30, 0.48, -22, 18, -39, 0.6, -6, 20, -39, 0.78, -18, 18, -12, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.14, -46, -32, 8, 0.26, -20, -14, 6, 0.32, -8, -4, 2, 0.4, -6, -2, 0, 0.48, -24, -20, 6, 0.6, -26, -20, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.14, -22, -16, 0, 0.26, 40, -4, 0, 0.32, 14, -6, 0, 0.4, 10, -6, 0, 0.48, 27, -7, 0, 0.6, 28, -2, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.14, -4, -22, -22, 0.32, -14, 8, 18, 0.48, 14, 0, 8, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.14, -14, -2, -30, 0.26, -44, -22, -20, 0.32, -88, -30, -36, 0.4, -94, -32, -40, 0.48, -6, 6, -6, 0.6, -4, 6, -6, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.14, -30, 8, -4, 0.32, -84, 30, -8, 0.4, -88, 32, -8, 0.48, -22, 6, -2, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.14, -6, 26, 7, 0.26, -32, -6, 8, 0.32, -8, 8, 7, 0.4, -2, -12, 7, 0.48, -70, 12, 7, 0.64, -66, 12, 7, 0.8, -32, 11, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.14, 12, 0, -1, 0.26, 58, 0, -1, 0.32, 20, 0, -1, 0.4, 6, 0, -1, 0.48, 76, 0, -1, 0.64, 72, 0, -1, 0.8, 38, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.4, 20, 8, 0, 0.48, -16, 12, 0, 0.64, -15, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.14, -26, -34, -9, 0.26, -6, 14, -11, 0.32, -34, -12, -9, 0.4, -8, 12, -9, 0.48, 8, -10, -9, 0.64, 6, -10, -9, 0.8, -8, -14, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.14, 40, 0, 1, 0.26, 14, 0, 1, 0.32, 56, 0, 1, 0.4, 10, 0, 1, 0.48, 118, 0, 1, 0.64, 114, 0, 1, 0.8, 54, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.4, 24, -14, 0, 0.48, 34, -14, 0, 0.64, 33, -14, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.14, 0, -0.06, -0.10, 0.26, 0, 0.06, 0.24, 0.32, 0, 0.18, 0.62, 0.4, 0, 0.24, 0.96, 0.48, 0, -0.30, 1.36, 0.64, 0, -0.28, 1.62, 0.8, 0, -0.10, 1.74, 1, 0, 0, 1.8] },
});

/* =========================================================================
 *  EVASION / DEFENCE
 * ========================================================================= */

clip('dash', 0.4, {
  blend: 0.08, rm: 3.0, tags: 'move',
  events: [ev(0.06, 'foot', { side: 'R' }), ev(0.34, 'foot', { side: 'L' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 26, -10, 0, 0.6, 22, -12, 0, 1, 8, -14, 0],
    spine1: [0, 3, -5, 0, 0.3, 12, -4, 0, 1, 4, -5, 0],
    chest: [0, -3, -8, 1, 0.3, 10, -6, 0, 1, -2, -8, 1],
    head: [0, -4, 14, 0, 0.3, -20, 10, 0, 1, -4, 14, 0],
    upperArmL: [0, -34, -16, -8, 0.3, 52, -8, -18, 0.7, -44, -18, -12, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.3, -84, 12, -4, 1, -62, 20, -4],
    upperArmR: [0, -16, 20, 6, 0.3, -46, 16, 16, 0.7, 20, 18, 12, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.3, -96, -18, 6, 1, -78, -22, 6],
    thighL: [0, -6, 10, 6, 0.2, -52, 8, 6, 0.55, 22, 8, 4, 1, -8, 10, 6],
    shinL: [0, 10, 0, -1, 0.2, 40, 0, -1, 0.55, 16, 0, -1, 1, 12, 0, -1],
    thighR: [0, -10, -16, -8, 0.2, 26, -12, -6, 0.6, -46, -12, -8, 1, -12, -16, -8],
    shinR: [0, 16, 0, 1, 0.2, 20, 0, 1, 0.6, 62, 0, 1, 1, 18, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0.02, 0.8, 0.6, 0, 0.04, 2.2, 1, 0, 0, 3.0] },
});

clip('dodge_roll', 0.76, {
  blend: 0.08, rm: 3.4, tags: 'move',
  events: [ev(0.12, 'sfx', { name: 'roll' }), ev(0.62, 'foot', { side: 'R' }), ev(0.7, 'cancel')],
  R: on(GUARD, {
    hips: [0, 10, -14, 0, 0.16, 70, -8, 0, 0.36, 200, -4, 0, 0.56, 330, 0, 0, 0.72, 372, -6, 0, 1, 366, -14, 0],
    spine1: [0, 6, -5, 0, 0.3, 26, -2, 0, 0.62, 6, -4, 0, 1, 3, -5, 0],
    spine2: [0, 4, -6, 1, 0.3, 28, -2, 0, 0.62, 4, -5, 1, 1, -1, -6, 1],
    chest: [0, 6, -8, 1, 0.3, 30, -4, 0, 0.62, 6, -7, 1, 1, -3, -8, 1],
    neck: [0, 10, 8, 0, 0.3, 30, 4, 0, 1, 6, 12, 0],
    head: [0, 12, 10, 0, 0.3, 34, 4, 0, 1, -4, 14, 0],
    upperArmL: [0, -50, -14, -14, 0.3, -96, -8, -22, 0.62, -40, -14, -10, 1, -34, -16, -8],
    foreArmL: [0, -70, 18, -4, 0.3, -118, 10, -4, 1, -62, 20, -4],
    upperArmR: [0, -40, 18, 12, 0.3, -88, 12, 24, 0.62, -30, 18, 10, 1, -16, 20, 6],
    foreArmR: [0, -88, -20, 6, 0.3, -122, -14, 6, 1, -78, -22, 6],
    thighL: [0, -50, 8, 8, 0.28, -104, 6, 8, 0.56, -86, 6, 8, 0.74, -30, 8, 6, 1, -6, 10, 6],
    shinL: [0, 60, 0, -1, 0.28, 118, 0, -1, 0.56, 96, 0, -1, 1, 10, 0, -1],
    thighR: [0, -46, -10, -8, 0.28, -108, -8, -8, 0.56, -76, -8, -8, 0.74, -24, -12, -8, 1, -10, -16, -8],
    shinR: [0, 64, 0, 1, 0.28, 120, 0, 1, 0.56, 88, 0, 1, 1, 16, 0, 1],
    footL: [0, 10, 6, 0, 0.3, 26, 6, 0, 1, -3, 12, 0],
    footR: [0, 10, -6, 0, 0.3, 26, -6, 0, 1, -4, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.18, 0, 0.30, 0.7, 0.36, 0, 0.44, 1.5, 0.56, 0, 0.30, 2.3, 0.72, 0, 0.02, 3.1, 1, 0, 0, 3.4] },
});

clip('block', 0.9, {
  loop: true, blend: 0.12, tags: 'defend',
  R: on(GUARD, {
    hips: [0, 8, -22, 0, 0.5, 9, -21, 0],
    spine1: [0, 6, -8, 0], spine2: [0, 4, -8, 1], chest: [0, 6, -12, 2],
    neck: [0, 8, 14, 0], head: [0, 6, 16, 0],
    clavicleL: [0, 6, -8, -16], upperArmL: [0, -74, -20, -6, 0.5, -76, -20, -6],
    foreArmL: [0, -96, 26, -6], handL: [0, -18, 8, 0],
    clavicleR: [0, 6, 8, 16], upperArmR: [0, -66, 22, 8, 0.5, -68, 22, 8],
    foreArmR: [0, -104, -26, 8], handR: [0, -14, -8, 0],
    thighL: [0, -12, 14, 7], shinL: [0, 20, 0, -1], footL: [0, -4, 16, 0],
    thighR: [0, -18, -20, -9], shinR: [0, 28, 0, 1], footR: [0, -6, -22, 0],
  }),
  T: { root: [0, 0, -0.03, 0, 0.5, 0, -0.025, 0] },
});

clip('block_impact', 0.34, {
  blend: 0.05, tags: 'defend',
  events: [ev(0.02, 'sfx', { name: 'guard' }), ev(0.02, 'vfx', { name: 'guardSpark' })],
  R: on(GUARD, {
    hips: [0, 8, -22, 0, 0.14, 2, -28, 0, 0.5, 10, -20, 0, 1, 8, -22, 0],
    spine1: [0, 6, -8, 0, 0.14, -6, -12, 0, 1, 6, -8, 0],
    spine2: [0, 4, -8, 1, 0.14, -10, -12, 1, 1, 4, -8, 1],
    chest: [0, 6, -12, 2, 0.14, -14, -18, 2, 1, 6, -12, 2],
    head: [0, 6, 16, 0, 0.14, -14, 20, 0, 1, 6, 16, 0],
    upperArmL: [0, -74, -20, -6, 0.14, -48, -26, -14, 1, -74, -20, -6],
    foreArmL: [0, -96, 26, -6, 0.14, -70, 30, -6, 1, -96, 26, -6],
    upperArmR: [0, -66, 22, 8, 0.14, -40, 28, 18, 1, -66, 22, 8],
    foreArmR: [0, -104, -26, 8, 0.14, -76, -32, 8, 1, -104, -26, 8],
    thighL: [0, -12, 14, 7, 0.2, -20, 14, 7, 1, -12, 14, 7],
    shinL: [0, 20, 0, -1, 0.2, 34, 0, -1, 1, 20, 0, -1],
    thighR: [0, -18, -20, -9, 0.2, -26, -20, -9, 1, -18, -20, -9],
    shinR: [0, 28, 0, 1, 0.2, 40, 0, 1, 1, 28, 0, 1],
  }),
  T: { root: [0, 0, -0.03, 0, 0.16, 0, -0.06, -0.16, 1, 0, -0.03, 0] },
});

clip('guard_break', 0.85, {
  blend: 0.06, tags: 'react',
  events: [ev(0.02, 'sfx', { name: 'guardBreak' }), ev(0.02, 'voice', { tag: 'pain' })],
  R: on(GUARD, {
    hips: [0, 8, -22, 0, 0.16, -17, -30, -4, 0.5, -8, -22, -2, 1, 2, -16, 0],
    spine1: [0, 6, -8, 0, 0.16, -9, -12, -2, 1, 3, -5, 0],
    spine2: [0, 4, -8, 1, 0.16, -10, -12, -2, 1, -1, -6, 1],
    chest: [0, 6, -12, 2, 0.16, -13, -18, -4, 0.5, -8, -14, -2, 1, -3, -8, 1],
    neck: [0, 8, 14, 0, 0.16, -11, 18, 0, 1, 6, 12, 0],
    head: [0, 6, 16, 0, 0.16, -15, 20, 0, 0.5, -9, 16, 0, 1, -4, 14, 0],
    clavicleL: [0, 6, -8, -16, 0.16, 6, -12, -34, 1, 2, -6, -10],
    upperArmL: [0, -74, -20, -6, 0.16, -6, -30, -66, 0.5, -30, -24, -40, 1, -34, -16, -8],
    foreArmL: [0, -96, 26, -6, 0.16, -24, 20, -6, 1, -62, 20, -4],
    clavicleR: [0, 6, 8, 16, 0.16, 6, 12, 34, 1, 2, 6, 10],
    upperArmR: [0, -66, 22, 8, 0.16, 4, 32, 62, 0.5, -22, 26, 38, 1, -16, 20, 6],
    foreArmR: [0, -104, -26, 8, 0.16, -30, -22, 8, 1, -78, -22, 6],
    thighL: [0, -12, 14, 7, 0.2, 8, 16, 8, 0.6, -14, 12, 7, 1, -6, 10, 6],
    shinL: [0, 20, 0, -1, 0.2, 30, 0, -1, 1, 10, 0, -1],
    thighR: [0, -18, -20, -9, 0.2, -34, -22, -10, 0.6, -8, -18, -8, 1, -10, -16, -8],
    shinR: [0, 28, 0, 1, 0.2, 54, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, -0.03, 0, 0.18, 0, -0.01, -0.5, 0.6, 0, 0, -0.85, 1, 0, 0, -0.9] },
});

/* =========================================================================
 *  REACTIONS
 * ========================================================================= */

clip('hit_light', 0.4, {
  blend: 0.05, tags: 'react',
  events: [ev(0.01, 'voice', { tag: 'pain' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.16, -13, -22, -4, 0.5, -2, -18, -2, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.16, -8, -10, -3, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.16, -9, -12, -4, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.16, -12, -16, -6, 0.5, -7, -12, -3, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.14, -10, 20, 4, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.14, -15, 24, 6, 0.5, -8, 18, 2, 1, -4, 14, 0],
    upperArmL: [0, -34, -16, -8, 0.16, -12, -22, -28, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.16, -40, 16, -4, 1, -62, 20, -4],
    upperArmR: [0, -16, 20, 6, 0.16, 4, 26, 26, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.16, -54, -18, 6, 1, -78, -22, 6],
    thighL: [0, -6, 10, 6, 0.2, 8, 12, 6, 1, -6, 10, 6],
    shinL: [0, 10, 0, -1, 0.2, 22, 0, -1, 1, 10, 0, -1],
    thighR: [0, -10, -16, -8, 0.2, -26, -18, -8, 1, -10, -16, -8],
    shinR: [0, 16, 0, 1, 0.2, 44, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0.01, -0.34, 1, 0, 0, -0.4] },
});

clip('hit_heavy', 0.72, {
  blend: 0.05, tags: 'react',
  events: [ev(0.01, 'voice', { tag: 'pain' }), ev(0.3, 'foot', { side: 'R' })],
  R: on(LOOSE, {
    hips: [0, 4, -14, 0, 0.18, -22, 26, -12, 0.5, -11, 18, -6, 1, 2, -10, 0],
    spine1: [0, 3, -5, 0, 0.18, -9, 14, -6, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.18, -10, 16, -8, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.18, -14, 22, -12, 0.5, -8, 14, -6, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.16, -12, -6, 8, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.16, -19, -10, 12, 0.5, -10, 6, 4, 1, -4, 14, 0],
    upperArmL: [0, -34, -16, -8, 0.18, 22, -30, -52, 0.5, -6, -24, -30, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.18, -22, 14, -4, 1, -62, 20, -4],
    upperArmR: [0, -16, 20, 6, 0.18, 30, 34, 44, 0.5, 2, 28, 26, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.18, -30, -16, 6, 1, -78, -22, 6],
    thighL: [0, -6, 10, 6, 0.24, 16, 14, 8, 0.6, -18, 10, 6, 1, -6, 10, 6],
    shinL: [0, 10, 0, -1, 0.24, 26, 0, -1, 0.6, 34, 0, -1, 1, 10, 0, -1],
    thighR: [0, -10, -16, -8, 0.24, -36, -20, -10, 0.6, -6, -14, -8, 1, -10, -16, -8],
    shinR: [0, 16, 0, 1, 0.24, 62, 0, 1, 1, 16, 0, 1],
    footL: [0, -3, 12, 0, 0.3, 16, 12, 0, 1, -3, 12, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0.06, -0.8, 0.55, 0, 0, -1.4, 1, 0, 0, -1.5] },
});

clip('launch', 0.46, {
  blend: 0.05, tags: 'react',
  events: [ev(0.01, 'voice', { tag: 'pain' }), ev(0.02, 'vfx', { name: 'launch' })],
  R: on(LOOSE, {
    hips: [0, 4, -14, 0, 0.3, -26, 8, -6, 1, -31, 12, -8],
    spine1: [0, 3, -5, 0, 0.3, -9, 4, -2, 1, -10, 6, -2],
    spine2: [0, -1, -6, 1, 0.3, -10, 4, -2, 1, -11, 6, -2],
    chest: [0, -3, -8, 1, 0.3, -13, 6, -4, 1, -14, 8, -4],
    neck: [0, 6, 12, 0, 0.3, -9, 6, 0, 1, -10, 8, 0],
    head: [0, -4, 14, 0, 0.3, -15, 8, 0, 1, -17, 10, 0],
    upperArmL: [0, -34, -16, -8, 0.3, 54, -20, -42, 1, 66, -22, -50],
    foreArmL: [0, -62, 20, -4, 0.3, -34, 12, -4, 1, -28, 10, -4],
    upperArmR: [0, -16, 20, 6, 0.3, 58, 24, 38, 1, 70, 26, 46],
    foreArmR: [0, -78, -22, 6, 0.3, -36, -14, 6, 1, -30, -12, 6],
    thighL: [0, -6, 10, 6, 0.3, -46, 8, 12, 1, -54, 8, 14],
    shinL: [0, 10, 0, -1, 0.3, 54, 0, -1, 1, 62, 0, -1],
    thighR: [0, -10, -16, -8, 0.3, -30, -10, -12, 1, -34, -10, -14],
    shinR: [0, 16, 0, 1, 0.3, 66, 0, 1, 1, 74, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.35, 0, 0.5, -0.25, 1, 0, 1.1, -0.6] },
});

clip('airborne', 0.9, {
  loop: true, blend: 0.14, tags: 'react',
  R: on(LOOSE, {
    hips: [0, -31, 12, -8, 0.5, -26, 4, -14, 1, -31, 12, -8],
    chest: [0, -14, 8, -4, 0.5, -11, 0, -8, 1, -14, 8, -4],
    head: [0, -17, 10, 0, 0.5, -14, 2, -4, 1, -17, 10, 0],
    upperArmL: [0, 66, -22, -50, 0.5, 74, -18, -58, 1, 66, -22, -50],
    upperArmR: [0, 70, 26, 46, 0.5, 78, 22, 54, 1, 70, 26, 46],
    thighL: [0, -54, 8, 14, 0.5, -44, 8, 16, 1, -54, 8, 14],
    shinL: [0, 62, 0, -1, 0.5, 78, 0, -1, 1, 62, 0, -1],
    thighR: [0, -34, -10, -14, 0.5, -46, -10, -16, 1, -34, -10, -14],
    shinR: [0, 74, 0, 1, 0.5, 60, 0, 1, 1, 74, 0, 1],
  }),
});

clip('land_hard', 0.72, {
  blend: 0.08, tags: 'react',
  events: [ev(0.02, 'land'), ev(0.03, 'vfx', { name: 'landDust' }), ev(0.04, 'foot', { side: 'L' }), ev(0.06, 'foot', { side: 'R' }), ev(0.6, 'cancel')],
  R: on(GUARD, {
    hips: [0, -12, -6, 0, 0.16, 26, -10, 0, 0.42, 15, -12, 0, 1, 4, -14, 0],
    spine1: [0, -6, -4, 0, 0.16, 10, -6, 0, 1, 3, -5, 0],
    spine2: [0, -7, -4, 0, 0.16, 11, -6, 0, 1, -1, -6, 1],
    chest: [0, -9, -6, 0, 0.16, 14, -8, 0, 0.42, 8, -10, 0, 1, -3, -8, 1],
    neck: [0, -5, 8, 0, 0.16, 12, 10, 0, 1, 6, 12, 0],
    head: [0, -8, 10, 0, 0.16, 16, 12, 0, 1, -4, 14, 0],
    upperArmL: [0, 40, -14, -34, 0.16, -66, -20, -30, 0.5, -46, -18, -16, 1, -34, -16, -8],
    foreArmL: [0, -30, 12, -4, 0.16, -84, 22, -6, 1, -62, 20, -4],
    upperArmR: [0, 44, 18, 30, 0.16, -58, 24, 32, 0.5, -38, 22, 18, 1, -16, 20, 6],
    foreArmR: [0, -34, -14, 6, 0.16, -92, -24, 8, 1, -78, -22, 6],
    thighL: [0, -30, 10, 10, 0.16, -66, 12, 12, 0.45, -30, 10, 8, 1, -6, 10, 6],
    shinL: [0, 30, 0, -1, 0.16, 84, 0, -1, 0.45, 40, 0, -1, 1, 10, 0, -1],
    thighR: [0, -26, -10, -10, 0.16, -62, -14, -12, 0.45, -26, -14, -8, 1, -10, -16, -8],
    shinR: [0, 34, 0, 1, 0.16, 88, 0, 1, 0.45, 42, 0, 1, 1, 16, 0, 1],
    footL: [0, 8, 10, 0, 0.16, -14, 12, 0, 1, -3, 12, 0],
    footR: [0, 8, -10, 0, 0.16, -14, -14, 0, 1, -4, -18, 0],
  }),
  T: { root: [0, 0, 0.55, 0, 0.16, 0, -0.24, 0, 0.42, 0, -0.09, 0, 1, 0, 0, 0] },
});

/* Knocked flat on the back. Ends lying on the ground and HOLDS. */
clip('knockdown', 0.95, {
  blend: 0.06, tags: 'react',
  events: [ev(0.01, 'voice', { tag: 'pain' }), ev(0.46, 'sfx', { name: 'bodyfall' }), ev(0.48, 'vfx', { name: 'landDust' }), ev(0.5, 'ragdoll')],
  R: on(LOOSE, {
    hips: [0, -20, 6, -6, 0.28, -62, 8, -8, 0.5, -92, 6, -4, 0.72, -88, 4, -2, 1, -90, 4, 0],
    spine1: [0, -7, 4, -2, 0.5, -8, 2, 0, 1, -4, 2, 0],
    spine2: [0, -8, 4, -2, 0.5, -6, 2, 0, 1, -2, 2, 0],
    chest: [0, -10, 6, -4, 0.5, -4, 2, 0, 1, 2, 2, 0],
    neck: [0, -8, 6, 0, 0.5, 8, 2, 0, 1, 12, 2, 0],
    head: [0, -12, 8, 0, 0.5, 8, 2, 0, 1, 10, 2, 0],
    upperArmL: [0, 40, -16, -34, 0.5, 14, -12, 20, 1, -6, -8, 42],
    foreArmL: [0, -46, 12, -4, 0.5, -30, 8, -4, 1, -20, 6, -4],
    upperArmR: [0, 44, 20, 30, 0.5, 18, 14, -20, 1, -2, 10, -42],
    foreArmR: [0, -42, -12, 6, 0.5, -26, -8, 6, 1, -16, -6, 6],
    thighL: [0, -20, 8, 10, 0.4, -46, 8, 14, 0.7, -18, 8, 12, 1, -8, 8, 10],
    shinL: [0, 24, 0, -1, 0.4, 68, 0, -1, 0.7, 26, 0, -1, 1, 14, 0, -1],
    thighR: [0, -16, -8, -10, 0.4, -38, -8, -14, 0.7, -14, -8, -12, 1, -6, -8, -10],
    shinR: [0, 28, 0, 1, 0.4, 60, 0, 1, 0.7, 22, 0, 1, 1, 12, 0, 1],
    footL: [0, 6, 8, 0, 1, -6, 10, 0], footR: [0, 6, -8, 0, 1, -6, -10, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.22, 0, 0.12, -0.6, 0.46, 0, -0.40, -1.25, 0.62, 0, -0.66, -1.45, 1, 0, -0.70, -1.5] },
});

clip('getup', 1.05, {
  blend: 0.1, tags: 'react',
  events: [ev(0.5, 'foot', { side: 'R' }), ev(0.86, 'foot', { side: 'L' }), ev(0.92, 'cancel')],
  R: on(GUARD, {
    hips: [0, -90, 4, 0, 0.3, -62, -4, 0, 0.58, -14, -10, 0, 0.8, 14, -14, 0, 1, 4, -14, 0],
    spine1: [0, -4, 2, 0, 0.4, 6, -2, 0, 1, 3, -5, 0],
    spine2: [0, -2, 2, 0, 0.4, 10, -4, 0, 1, -1, -6, 1],
    chest: [0, 2, 2, 0, 0.4, 18, -6, 0, 0.7, 10, -8, 0, 1, -3, -8, 1],
    neck: [0, 12, 2, 0, 0.4, 4, 4, 0, 1, 6, 12, 0],
    head: [0, 10, 2, 0, 0.4, -6, 8, 0, 1, -4, 14, 0],
    upperArmL: [0, -6, -8, 42, 0.3, -18, -20, -30, 0.6, -50, -20, -18, 1, -34, -16, -8],
    foreArmL: [0, -20, 6, -4, 0.3, -74, 16, -6, 1, -62, 20, -4],
    upperArmR: [0, -2, 10, -42, 0.3, -12, 24, 28, 0.6, -40, 24, 16, 1, -16, 20, 6],
    foreArmR: [0, -16, -6, 6, 0.3, -70, -18, 6, 1, -78, -22, 6],
    thighL: [0, -8, 8, 10, 0.3, -76, 10, 12, 0.6, -52, 10, 10, 1, -6, 10, 6],
    shinL: [0, 14, 0, -1, 0.3, 104, 0, -1, 0.6, 72, 0, -1, 1, 10, 0, -1],
    thighR: [0, -6, -8, -10, 0.3, -70, -12, -12, 0.6, -20, -14, -10, 1, -10, -16, -8],
    shinR: [0, 12, 0, 1, 0.3, 108, 0, 1, 0.6, 40, 0, 1, 1, 16, 0, 1],
  }),
  T: { root: [0, 0, -0.70, -1.5, 0.32, 0, -0.46, -1.2, 0.62, 0, -0.10, -0.9, 0.85, 0, -0.01, -0.7, 1, 0, 0, -0.65] },
});

/* --- deaths ------------------------------------------------------------- */

/* death_a: takes it in the chest, staggers, folds forward onto the face. */
clip('death_a', 1.3, {
  blend: 0.06, tags: 'death',
  events: [ev(0.01, 'voice', { tag: 'death' }), ev(0.16, 'foot', { side: 'R' }), ev(0.6, 'sfx', { name: 'bodyfall' }), ev(0.62, 'vfx', { name: 'landDust' })],
  R: on(LOOSE, {
    hips: [0, 4, -14, 0, 0.14, -22, -20, -8, 0.34, 10, -14, -4, 0.6, 62, -8, -2, 0.82, 86, -6, 0, 1, 88, -6, 0],
    spine1: [0, 3, -5, 0, 0.2, -8, -8, -2, 0.6, 8, -4, 0, 1, -2, -4, 0],
    spine2: [0, -1, -6, 1, 0.2, -9, -8, -2, 0.6, 8, -4, 0, 1, -2, -4, 0],
    chest: [0, -3, -8, 1, 0.2, -12, -12, -4, 0.6, 10, -6, 0, 1, -3, -6, 0],
    neck: [0, 6, 12, 0, 0.2, -10, 16, 0, 0.6, 2, 6, 0, 1, -8, 22, 0],
    head: [0, -4, 14, 0, 0.2, -16, 18, 0, 0.6, 6, 8, 0, 1, -12, 26, 0],
    upperArmL: [0, -34, -16, -8, 0.2, 26, -26, -46, 0.55, 20, -18, 24, 1, 6, -12, 38],
    foreArmL: [0, -62, 20, -4, 0.2, -20, 14, -4, 0.6, 4, 12, -4, 1, 10, 10, -4],
    upperArmR: [0, -16, 20, 6, 0.2, 34, 30, 40, 0.55, 24, 22, -24, 1, 10, 16, -38],
    foreArmR: [0, -78, -22, 6, 0.2, -24, -16, 6, 0.6, 2, -12, 6, 1, 8, -10, 6],
    thighL: [0, -6, 10, 6, 0.2, 12, 12, 8, 0.5, -34, 10, 10, 0.8, -16, 8, 10, 1, -12, 8, 10],
    shinL: [0, 10, 0, -1, 0.2, 24, 0, -1, 0.5, 62, 0, -1, 1, 30, 0, -1],
    thighR: [0, -10, -16, -8, 0.2, -34, -20, -10, 0.5, -26, -12, -10, 1, -14, -10, -10],
    shinR: [0, 16, 0, 1, 0.2, 58, 0, 1, 0.5, 48, 0, 1, 1, 26, 0, 1],
    footL: [0, -3, 12, 0, 0.6, 10, 10, 0, 1, 12, 10, 0],
    footR: [0, -4, -18, 0, 0.6, 10, -10, 0, 1, 12, -10, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0.03, -0.35, 0.45, 0, -0.06, -0.2, 0.66, 0, -0.46, 0.15, 0.86, 0, -0.70, 0.3, 1, 0, -0.72, 0.32] },
});

/* death_b: spun round by the blow and dropped onto the back. */
clip('death_b', 1.4, {
  blend: 0.06, tags: 'death',
  events: [ev(0.01, 'voice', { tag: 'death' }), ev(0.22, 'foot', { side: 'L' }), ev(0.66, 'sfx', { name: 'bodyfall' }), ev(0.68, 'vfx', { name: 'landDust' })],
  R: on(LOOSE, {
    hips: [0, 4, -14, 0, 0.16, -18, 34, -14, 0.4, -44, 52, -10, 0.66, -78, 60, -4, 0.9, -88, 62, 0, 1, -88, 62, 0],
    spine1: [0, 3, -5, 0, 0.3, -7, 12, -4, 1, -2, 6, 0],
    spine2: [0, -1, -6, 1, 0.3, -8, 14, -4, 1, 0, 6, 0],
    chest: [0, -3, -8, 1, 0.3, -10, 20, -6, 1, 4, 8, 0],
    neck: [0, 6, 12, 0, 0.3, -9, -8, 4, 1, 12, -4, 0],
    head: [0, -4, 14, 0, 0.3, -15, -12, 6, 1, 10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.25, 44, -24, -54, 0.6, 14, -16, 12, 1, -6, -8, 24],
    foreArmL: [0, -62, 20, -4, 0.25, -18, 12, -4, 1, -24, 6, -4],
    upperArmR: [0, -16, 20, 6, 0.25, 48, 28, 48, 0.6, 18, 20, -12, 1, -2, 12, -26],
    foreArmR: [0, -78, -22, 6, 0.25, -22, -14, 6, 1, -22, -6, 6],
    thighL: [0, -6, 10, 6, 0.3, -30, 12, 12, 0.6, -40, 10, 14, 1, -10, 8, 10],
    shinL: [0, 10, 0, -1, 0.3, 44, 0, -1, 0.6, 62, 0, -1, 1, 16, 0, -1],
    thighR: [0, -10, -16, -8, 0.3, -14, -14, -12, 0.6, -30, -10, -14, 1, -6, -8, -10],
    shinR: [0, 16, 0, 1, 0.3, 52, 0, 1, 0.6, 40, 0, 1, 1, 12, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0.05, -0.5, 0.45, 0, 0.02, -1.0, 0.7, 0, -0.46, -1.35, 0.92, 0, -0.70, -1.5, 1, 0, -0.71, -1.52] },
});

/* death_launch: flung backwards through the air, lands and skids. */
clip('death_launch', 1.55, {
  blend: 0.05, tags: 'death',
  events: [ev(0.01, 'voice', { tag: 'death' }), ev(0.02, 'vfx', { name: 'launch' }), ev(0.86, 'sfx', { name: 'bodyfall' }), ev(0.88, 'vfx', { name: 'landDust' })],
  R: on(LOOSE, {
    hips: [0, 4, -14, 0, 0.16, -46, 10, -10, 0.42, -66, 20, -12, 0.7, -84, 16, -6, 0.9, -92, 12, -2, 1, -92, 12, 0],
    spine1: [0, 3, -5, 0, 0.3, -9, 6, -4, 1, -4, 4, 0],
    spine2: [0, -1, -6, 1, 0.3, -10, 6, -4, 1, -2, 4, 0],
    chest: [0, -3, -8, 1, 0.3, -13, 8, -6, 1, 2, 4, 0],
    neck: [0, 6, 12, 0, 0.3, -11, 6, 0, 1, 12, 2, 0],
    head: [0, -4, 14, 0, 0.3, -18, 8, 0, 1, 10, 2, 0],
    upperArmL: [0, -34, -16, -8, 0.3, 72, -24, -56, 0.7, 40, -18, -10, 1, -4, -8, 46],
    foreArmL: [0, -62, 20, -4, 0.3, -20, 12, -4, 1, -20, 6, -4],
    upperArmR: [0, -16, 20, 6, 0.3, 76, 28, 50, 0.7, 44, 22, 8, 1, 0, 12, -46],
    foreArmR: [0, -78, -22, 6, 0.3, -24, -14, 6, 1, -18, -6, 6],
    thighL: [0, -6, 10, 6, 0.3, -58, 10, 16, 0.7, -46, 10, 14, 1, -8, 8, 10],
    shinL: [0, 10, 0, -1, 0.3, 74, 0, -1, 0.7, 58, 0, -1, 1, 14, 0, -1],
    thighR: [0, -10, -16, -8, 0.3, -44, -10, -16, 0.7, -34, -10, -14, 1, -4, -8, -10],
    shinR: [0, 16, 0, 1, 0.3, 82, 0, 1, 0.7, 50, 0, 1, 1, 10, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0.85, -1.4, 0.44, 0, 1.25, -2.7, 0.7, 0, 0.6, -4.0, 0.88, 0, -0.66, -4.7, 1, 0, -0.71, -4.9] },
});

/* =========================================================================
 *  FLAVOUR
 * ========================================================================= */

clip('taunt', 1.6, {
  blend: 0.16, tags: 'flavour',
  events: [ev(0.3, 'voice', { tag: 'shout' }), ev(0.34, 'swoosh', { power: 0.4 })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.3, 6, -30, -2, 0.55, 2, 12, 4, 0.8, 4, -6, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.3, -6, -12, 0, 0.55, 6, 6, 2, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.3, -10, -14, 0, 0.55, 8, 8, 2, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.3, -16, -20, -2, 0.55, 12, 12, 4, 0.8, -6, -4, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.3, -6, 18, 0, 0.55, 10, -4, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.3, -18, 22, 0, 0.55, 8, -6, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.3, -10, 10, 30, 0.55, 6, 0, 2, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.3, 26, 22, 40, 0.5, -98, -6, -14, 0.7, -76, 4, -4, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.3, -112, -18, 6, 0.5, -20, -8, 2, 0.72, -60, -14, 4, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.5, 14, 6, 0, 1, -10, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.35, -84, -22, -30, 0.6, -56, -20, -18, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.35, -104, 26, -6, 0.6, -70, 22, -4, 1, -62, 20, -4],
    thighL: [0, -6, 10, 6, 0.5, -14, 12, 6, 1, -6, 10, 6],
    thighR: [0, -10, -16, -8, 0.5, -4, -18, -8, 1, -10, -16, -8],
  }),
  T: { root: [0, 0, 0, 0, 0.32, 0, 0.03, -0.06, 0.56, 0, -0.03, 0.1, 1, 0, 0, 0] },
});

clip('celebrate', 1.9, {
  blend: 0.2, tags: 'flavour',
  events: [ev(0.24, 'voice', { tag: 'shout' })],
  R: on(EASE, {
    hips: [0, 3, -5, 0, 0.3, -8, -2, 0, 0.6, 2, 2, 0, 1, 3, -5, 0],
    spine1: [0, 3, -3, 1, 0.3, -8, 0, 0, 1, 3, -3, 1],
    spine2: [0, -1, -3, 1, 0.3, -12, 0, 0, 1, -1, -3, 1],
    chest: [0, -3, -5, 1, 0.3, -18, 0, 0, 0.6, -10, 0, 0, 1, -3, -5, 1],
    neck: [0, 5, 5, 0, 0.3, -8, 0, 0, 1, 5, 5, 0],
    head: [0, -3, 6, 0, 0.3, -22, 0, 0, 0.6, -12, 0, 0, 1, -3, 6, 0],
    clavicleR: [0, 0, 3, 7, 0.3, -26, 4, 44, 0.55, -22, 4, 40, 1, 0, 3, 7],
    upperArmR: [0, -10, 14, 13, 0.3, -98, 10, 30, 0.55, -92, 10, 28, 0.8, -50, 12, 16, 1, -10, 14, 13],
    foreArmR: [0, -48, -16, 6, 0.3, -12, -6, 4, 0.55, -26, -8, 4, 1, -48, -16, 6],
    clavicleL: [0, 0, -3, -7, 0.3, -26, -4, -44, 0.55, -22, -4, -40, 1, 0, -3, -7],
    upperArmL: [0, -14, -12, -13, 0.3, -94, -10, -32, 0.55, -88, -10, -30, 0.8, -48, -12, -16, 1, -14, -12, -13],
    foreArmL: [0, -40, 14, -6, 0.3, -18, 8, -4, 0.55, -34, 10, -4, 1, -40, 14, -6],
    thighL: [0, -3, 5, 5, 0.3, -14, 6, 6, 1, -3, 5, 5],
    shinL: [0, 5, 0, -1, 0.3, 20, 0, -1, 1, 5, 0, -1],
    thighR: [0, -4, -8, -6, 0.3, -14, -8, -6, 1, -4, -8, -6],
    shinR: [0, 7, 0, 1, 0.3, 20, 0, 1, 1, 7, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.3, 0, -0.06, 0, 0.46, 0, 0.1, 0, 0.62, 0, -0.02, 0, 1, 0, 0, 0] },
});

/* Seated on a mount: legs splayed round the barrel, torso riding the gait. */
clip('mounted', 1.1, {
  loop: true, blend: 0.24, tags: 'pose',
  R: on(GUARD, {
    hips: [0, 6, -8, 0, 0.5, 2, -8, 0],
    spine1: [0, 4, -4, 0, 0.5, 6, -4, 0],
    spine2: [0, -2, -4, 0, 0.5, 0, -4, 0],
    chest: [0, -4, -6, 0, 0.5, -1, -6, 0],
    neck: [0, 6, 8, 0], head: [0, -4, 10, 0, 0.5, -6, 10, 0],
    upperArmL: [0, -46, -18, -10, 0.5, -50, -18, -10],
    foreArmL: [0, -74, 24, -6], handL: [0, -14, 8, 0],
    upperArmR: [0, -26, 22, 10, 0.5, -30, 22, 10],
    foreArmR: [0, -84, -24, 8], handR: [0, -10, -8, 0],
    thighL: [0, -74, 16, 34, 0.5, -70, 16, 34],
    shinL: [0, 62, 0, -6], footL: [0, 6, 14, 0],
    thighR: [0, -74, -16, -34, 0.5, -70, -16, -34],
    shinR: [0, 62, 0, 6], footR: [0, 6, -14, 0],
  }),
  T: { root: [0, 0, 0.36, 0, 0.5, 0, 0.40, 0] },
});


/* =========================================================================
 *  CAMBUK NAGA — the chain whip (DEV_PLAN Step 8, weapon 2 of 3)
 * -------------------------------------------------------------------------
 *  THE ARCHETYPE IS "THE HEAD NEVER COMES HOME". Measured across the four
 *  weapons already here, every one brings its business end within 0.09 m of
 *  the wielder's own axis somewhere in its chain — a blade is a lever and a
 *  lever pivots at the hand. Nothing keeps its damage out there.
 *
 *  So every clip below is authored on one rule: the arm stays EXTENDED and the
 *  body turns under it. No wind-up that pulls the hand across the chest, no
 *  recovery that brings it back to the hip — the elbow barely folds, and what
 *  moves is the shoulder and the hips. That is what a two-metre chain does,
 *  and it is why this weapon has an inner dead zone where every other one is
 *  at its most dangerous.
 *
 *  It also means the hips do most of the work, so `hipsYaw` runs high — the
 *  opposite corner from the gada, which was built on pitch with almost no yaw
 *  at all.
 * ========================================================================= */

/* C1 — Outer Circle. A flat horizontal orbit at full extension. */
clip('cambuk_c1', 0.58, {
  blend: 0.10, rm: 0.4, tags: 'attack',
  events: [ev(0.30, 'swoosh', { power: 0.9 }),
    ev(0.36, 'hitboxOn', { arc: 150, reach: 3.6, height: 1.2, power: 0.85, dmg: 1.5 }),
    ev(0.54, 'hitboxOff'), ev(0.70, 'cancel'), ev(0.16, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 6, 46, -2, 0.44, 8, -70, 2, 0.58, 6, -40, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 4, 20, 0, 0.44, 6, -30, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 2, 18, 1, 0.44, 4, -28, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, 0, 26, 1, 0.44, 2, -36, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 6, -12, 0, 0.44, 6, 22, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, -2, -14, 0, 0.44, -2, 24, 0, 1, -4, 14, 0],
    /* The arm stays out. upperArmR near -70 with the forearm almost straight
     * is what holds the head at radius; folding the elbow here would bring the
     * chain inboard and lose the whole weapon. */
    clavicleR: [0, 2, 6, 10, 0.2, 4, 10, 26, 0.44, 2, 4, -10, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, -64, 30, 38, 0.44, -70, 8, -18, 0.58, -40, 16, 0, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -14, -22, 6, 0.44, -10, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, 4, -6, 0, 0.44, 10, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.2, 4, -10, -22, 0.44, 2, -4, 12, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, -20, -26, -32, 0.44, -26, -8, 22, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -48, 20, -4, 0.44, -44, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -24, 13, 7, 0.44, -30, 7, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 34, 0, -1, 0.44, 42, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -28, -16, -9, 0.44, -22, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 40, 0, 1, 0.44, 34, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, -0.03, 0.12, 0.44, 0, -0.02, 0.30, 1, 0, 0, 0.4] },
});

/* C2 — Reverse Circle. The same orbit the other way, so the chain is never
 * asked to stop and restart — it keeps its momentum across the link. */
clip('cambuk_c2', 0.56, {
  blend: 0.10, rm: 0.4, tags: 'attack',
  events: [ev(0.28, 'swoosh', { power: 0.95 }),
    ev(0.34, 'hitboxOn', { arc: 150, reach: 3.6, height: 1.4, power: 0.9, dmg: 1.55 }),
    ev(0.52, 'hitboxOff'), ev(0.68, 'cancel'), ev(0.14, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 4, -66, 2, 0.44, 8, 44, -2, 0.56, 6, 10, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 4, -28, 0, 0.44, 4, 20, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 0, -26, 1, 0.44, 2, 18, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, -2, -34, 1, 0.44, 0, 24, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 6, 22, 0, 0.44, 6, -10, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, -2, 24, 0, 0.44, -2, -12, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 2, 4, -12, 0.44, 4, 10, 28, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, -68, 8, -20, 0.44, -62, 30, 40, 0.56, -38, 18, 2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -12, -22, 6, 0.44, -16, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, 12, -6, 0, 0.44, 2, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.2, 2, -4, 14, 0.44, 4, -10, -24, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, -28, -8, 24, 0.44, -22, -26, -30, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -44, 20, -4, 0.44, -46, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -28, 7, 7, 0.44, -22, 13, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 40, 0, -1, 0.44, 32, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -24, -16, -9, 0.44, -30, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 34, 0, 1, 0.44, 42, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, -0.03, 0.12, 0.44, 0, -0.02, 0.30, 1, 0, 0, 0.4] },
});

/* C3 — Low Scythe. The orbit dropped to shin height without folding the arm:
 * the hips tilt, the chain stays out. */
clip('cambuk_c3', 0.62, {
  blend: 0.10, rm: 0.5, tags: 'attack',
  events: [ev(0.30, 'swoosh', { power: 1.0 }),
    ev(0.36, 'hitboxOn', { arc: 160, reach: 3.7, height: 0.5, power: 0.95, dmg: 1.6, launch: true }),
    ev(0.56, 'hitboxOff'), ev(0.74, 'cancel'), ev(0.16, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 22, 44, -3, 0.46, 26, -66, 3, 0.62, 16, -34, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 14, 18, 0, 0.46, 16, -26, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 12, 16, 1, 0.46, 14, -24, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, 16, 22, 1, 0.46, 18, -32, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 4, -10, 0, 0.46, 4, 20, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, 10, -12, 0, 0.46, 10, 22, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 6, 10, 24, 0.46, 4, 4, -12, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, -34, 30, 42, 0.46, -40, 8, -22, 0.62, -24, 16, -2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -16, -22, 6, 0.46, -12, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, 6, -6, 0, 0.46, 12, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.2, 6, -10, -24, 0.46, 4, -4, 14, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, -18, -26, -34, 0.46, -24, -8, 24, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -46, 20, -4, 0.46, -42, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -40, 12, 7, 0.46, -48, 8, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 56, 0, -1, 0.46, 66, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -32, -16, -9, 0.46, -40, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 48, 0, 1, 0.46, 58, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, -0.12, 0.16, 0.46, 0, -0.15, 0.38, 1, 0, 0, 0.5] },
});

/* C4 — Overhead Wheel. The orbit tipped vertical: the head comes over the top
 * and down in front, still at full chain. */
clip('cambuk_c4', 0.70, {
  blend: 0.11, rm: 0.6, tags: 'attack',
  events: [ev(0.34, 'swoosh', { power: 1.1 }),
    ev(0.40, 'hitboxOn', { arc: 80, reach: 3.8, height: 1.6, power: 1.0, dmg: 1.8 }),
    ev(0.60, 'hitboxOff'), ev(0.82, 'cancel'), ev(0.18, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.24, -14, -10, -2, 0.48, 20, -16, 1, 0.7, 12, -15, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.24, -10, -5, 0, 0.48, 14, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.24, -12, -6, 1, 0.48, 16, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.24, -16, -8, 1, 0.48, 20, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.24, 14, 12, 0, 0.48, -12, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.24, 16, 14, 0, 0.48, -16, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.24, 18, 6, 20, 0.48, -6, 6, -2, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.24, -136, 20, 4, 0.48, -20, 20, 16, 0.7, -22, 20, 10, 1, -16, 20, 6],
    /* Even overhead the elbow stays open: -16 at the peak, where a blade would
     * fold to -70 and bring the tip past the ear. */
    foreArmR: [0, -78, -22, 6, 0.24, -16, -22, 6, 0.48, -12, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.24, -20, -6, 0, 0.48, 14, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.24, 12, -6, -18, 0.48, -6, -6, 4, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.24, -60, -16, -18, 0.48, -30, -16, -6, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.24, -40, 20, -4, 0.48, -46, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.24, -10, 10, 7, 0.48, -34, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.24, 16, 0, -1, 0.48, 48, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.24, -26, -16, -9, 0.48, -14, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.24, 38, 0, 1, 0.48, 22, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.24, 0, 0.02, 0.10, 0.48, 0, -0.04, 0.40, 1, 0, 0, 0.6] },
});

/* C5 — Double Orbit. Two circuits in one move, the second wider than the
 * first, and the hips carry both. */
clip('cambuk_c5', 0.96, {
  blend: 0.11, rm: 0.9, tags: 'attack',
  events: [ev(0.24, 'swoosh', { power: 1.0 }),
    ev(0.30, 'hitboxOn', { arc: 180, reach: 3.8, height: 1.3, power: 0.95, dmg: 1.5 }),
    ev(0.62, 'swoosh', { power: 1.15 }), ev(0.82, 'hitboxOff'),
    ev(1.04, 'cancel'), ev(0.14, 'voice', { tag: 'shout' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.18, 8, 60, -2, 0.36, 10, -110, 2, 0.56, 10, -260, 2, 0.78, 8, -400, 2, 0.9, 6, -430, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.18, 6, 16, 0, 0.56, 8, -14, 0, 0.9, 6, -8, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.18, 2, 14, 1, 0.56, 4, -12, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.18, 2, 20, 1, 0.56, 4, -18, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.18, 6, 4, 0, 0.56, 6, 8, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.18, 4, 6, 0, 0.56, 4, 10, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.18, 4, 10, 30, 0.56, 4, 10, 30, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.18, -62, 30, 44, 0.36, -66, 30, 44, 0.78, -64, 30, 42, 0.9, -36, 24, 20, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.18, -10, -22, 6, 0.78, -12, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.18, 8, -6, 0, 0.78, 8, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.18, 4, -10, -26, 0.56, 4, -10, -26, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.18, -16, -26, -34, 0.36, -20, -26, -34, 0.78, -18, -26, -34, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.18, -42, 20, -4, 0.78, -40, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.18, -30, 10, 7, 0.36, -12, 10, 7, 0.56, -32, 10, 7, 0.78, -14, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.18, 46, 0, -1, 0.36, 18, 0, -1, 0.56, 48, 0, -1, 0.78, 20, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.18, -14, -16, -9, 0.36, -34, -16, -9, 0.56, -16, -16, -9, 0.78, -36, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.18, 20, 0, 1, 0.36, 50, 0, 1, 0.56, 22, 0, 1, 0.78, 52, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.18, 0, -0.04, 0.14, 0.36, 0, -0.05, 0.34, 0.56, 0, -0.04, 0.58, 0.78, 0, -0.03, 0.80, 1, 0, 0, 0.9] },
});

/* C6 — Coil and Crack. The chain gathers in tight and then snaps out to full
 * length in one frame, which is the only moment this weapon is ever close. */
clip('cambuk_c6', 1.04, {
  blend: 0.12, rm: 1.4, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.44, 'swoosh', { power: 1.35 }),
    ev(0.50, 'hitboxOn', { arc: 60, reach: 4.4, height: 1.4, power: 1.1, dmg: 2.4, launch: true }),
    ev(0.66, 'hitboxOff'), ev(0.94, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.3, 10, 54, -2, 0.5, 4, -46, 2, 0.72, 4, -26, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.3, 8, 24, 0, 0.5, 2, -20, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.3, 4, 22, 1, 0.5, 0, -18, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.3, 6, 30, 1, 0.5, -2, -26, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.3, 6, -14, 0, 0.5, 8, 18, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.3, -2, -16, 0, 0.5, 0, 20, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.3, 6, 12, 18, 0.5, 2, 2, -6, 1, 2, 6, 10],
    /* THE ONE EXCEPTION, and it is the move's whole idea: the elbow folds to
     * -86 at 0.30 to gather the chain, then opens to -6 at 0.50 to throw it.
     * Every other clip in this set keeps it open. */
    upperArmR: [0, -16, 20, 6, 0.3, -30, 26, 20, 0.5, -76, 10, -10, 0.72, -44, 16, 0, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.3, -86, -22, 6, 0.5, -6, -22, 6, 0.72, -30, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.3, -24, -6, 0, 0.5, 16, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.3, 6, -12, -18, 0.5, 2, -2, 8, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.3, -40, -22, -22, 0.5, -30, -10, 12, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.3, -70, 20, -4, 0.5, -38, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.3, -8, 10, 7, 0.5, -46, 10, 7, 0.72, -26, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.3, 14, 0, -1, 0.5, 62, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.5, 12, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.3, -32, -16, -9, 0.5, -12, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.3, 46, 0, 1, 0.5, 18, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.3, 0, -0.02, 0.10, 0.5, 0, -0.04, 0.86, 0.72, 0, -0.02, 1.22, 1, 0, 0, 1.4] },
});

/* CH1 — Rising Coil. The launcher: the orbit tips from flat to vertical and
 * carries whatever it caught up with it. */
clip('cambuk_ch1', 0.84, {
  blend: 0.11, rm: 0.6, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.36, 'swoosh', { power: 1.2 }),
    ev(0.42, 'hitboxOn', { arc: 110, reach: 3.9, height: 1.7, power: 1.05, dmg: 2.0, launch: true }),
    ev(0.62, 'hitboxOff'), ev(0.92, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.26, 26, 40, -3, 0.54, -18, -50, 3, 0.74, -10, -30, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.26, 16, 16, 0, 0.54, -14, -20, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.26, 14, 14, 1, 0.54, -16, -18, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.26, 20, 20, 1, 0.54, -22, -26, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.26, 18, -8, 0, 0.54, -18, 18, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.26, 22, -10, 0, 0.54, -24, 20, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.26, 8, 10, 26, 0.54, -12, 4, 6, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.26, -26, 30, 44, 0.54, -118, 10, 8, 0.74, -70, 16, 4, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.26, -14, -22, 6, 0.54, -18, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.26, 8, -6, 0, 0.54, -26, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.26, 8, -10, -26, 0.54, -12, -4, -6, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.26, -20, -26, -34, 0.54, -96, -10, -10, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.26, -44, 20, -4, 0.54, -34, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.26, -42, 10, 7, 0.54, -4, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.26, 58, 0, -1, 0.54, 6, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.26, -38, -16, -9, 0.54, -6, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.26, 54, 0, 1, 0.54, 10, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.26, 0, -0.10, 0.14, 0.54, 0, 0.08, 0.42, 1, 0, 0, 0.6] },
});

/* CH2 — Haft Check. The stun, and the only move that admits the dead zone:
 * a short jab with the HANDLE for something that got inside the chain. */
clip('cambuk_ch2', 0.48, {
  blend: 0.09, rm: 0.6, tags: 'attack',
  events: [ev(0.24, 'swoosh', { power: 0.6 }),
    ev(0.30, 'hitboxOn', { arc: 40, reach: 1.5, height: 1.2, power: 0.8, dmg: 1.1 }),
    ev(0.42, 'hitboxOff'), ev(0.58, 'cancel'), ev(0.12, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 2, -26, -1, 0.36, 6, 6, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 4, -12, 0, 0.36, 6, 6, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 0, -12, 1, 0.36, 2, 6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, -2, -18, 1, 0.36, 0, 8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 8, 14, 0, 0.36, 6, 2, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, -2, 16, 0, 0.36, -4, 2, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 6, 6, 14, 0.36, -2, 6, 2, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, -30, 22, 12, 0.36, 6, 18, -2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -92, -22, 6, 0.36, -40, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, -16, -6, 0, 0.36, 8, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.2, 6, -6, -14, 0.36, -2, -6, -2, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, -48, -16, -12, 0.36, -8, -16, 2, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -84, 20, -4, 0.36, -30, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -8, 10, 7, 0.36, -30, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 14, 0, -1, 0.36, 40, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -26, -16, -9, 0.36, -12, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 36, 0, 1, 0.36, 20, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0, -0.10, 0.36, 0, 0, 0.44, 1, 0, 0, 0.6] },
});

/* CH3 — Ring of Coils. A full orbit held open for a long active window: this
 * weapon's answer to a crowd is to stand still and let them walk into it. */
clip('cambuk_ch3', 1.30, {
  blend: 0.12, rm: 0.8, tags: 'attack',
  events: [ev(0.12, 'voice', { tag: 'shout' }), ev(0.26, 'swoosh', { power: 1.2 }),
    ev(0.30, 'hitboxOn', { arc: 180, reach: 4.2, height: 1.2, power: 1.0, dmg: 1.7 }),
    ev(0.66, 'swoosh', { power: 1.25 }), ev(0.90, 'hitboxOff'), ev(1.14, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.14, 8, 70, -2, 0.34, 10, -120, 2, 0.54, 10, -300, 2, 0.74, 10, -480, 2, 0.9, 8, -540, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.14, 6, 14, 0, 0.54, 8, -12, 0, 0.9, 6, -8, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.14, 2, 12, 1, 0.54, 4, -10, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.14, 2, 18, 1, 0.54, 4, -16, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.14, 6, 4, 0, 0.54, 6, 8, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.14, 4, 6, 0, 0.54, 4, 10, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.14, 4, 10, 32, 0.54, 4, 10, 32, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.14, -66, 32, 46, 0.34, -70, 32, 46, 0.74, -68, 32, 44, 0.9, -38, 24, 22, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.14, -8, -22, 6, 0.74, -10, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.14, 10, -6, 0, 0.74, 10, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.14, 4, -10, -28, 0.54, 4, -10, -28, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.14, -14, -28, -36, 0.34, -18, -28, -36, 0.74, -16, -28, -36, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.14, -40, 20, -4, 0.74, -38, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.14, -30, 10, 7, 0.34, -12, 10, 7, 0.54, -32, 10, 7, 0.74, -14, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.14, 46, 0, -1, 0.34, 18, 0, -1, 0.54, 48, 0, -1, 0.74, 20, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.14, -14, -16, -9, 0.34, -34, -16, -9, 0.54, -16, -16, -9, 0.74, -36, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.14, 20, 0, 1, 0.34, 50, 0, 1, 0.54, 22, 0, 1, 0.74, 52, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.14, 0, -0.04, 0.10, 0.34, 0, -0.05, 0.26, 0.54, 0, -0.04, 0.48, 0.74, 0, -0.03, 0.68, 1, 0, 0, 0.8] },
});

/* CH4 — Naga Descends. The overhead wheel taken all the way over: the head
 * goes up, past vertical, and comes down on one point from four metres. */
clip('cambuk_ch4', 1.10, {
  blend: 0.12, rm: 1.0, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.44, 'swoosh', { power: 1.3 }),
    ev(0.52, 'hitboxOn', { arc: 90, reach: 4.2, height: 0.9, power: 1.1, dmg: 2.3, launch: true, gb: true }),
    ev(0.54, 'vfx', { name: 'groundSlam' }), ev(0.70, 'hitboxOff'), ev(1.00, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.28, -22, -12, -2, 0.46, -18, -13, -2, 0.54, 30, -14, 1, 0.7, 26, -14, 0, 0.88, 12, -14, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.28, -14, -5, 0, 0.54, 18, -5, 0, 0.7, 16, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.28, -16, -6, 1, 0.54, 20, -6, 1, 0.7, 18, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.28, -22, -8, 1, 0.54, 26, -8, 1, 0.7, 23, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.28, 18, 12, 0, 0.54, -16, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.28, 22, 14, 0, 0.54, -20, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.28, 22, 6, 18, 0.54, -8, 6, -2, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.28, -150, 20, 2, 0.46, -144, 20, 2, 0.54, -16, 20, 18, 0.7, -20, 20, 12, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.28, -12, -22, 6, 0.54, -8, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.28, -26, -6, 0, 0.54, 18, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.28, 16, -6, -18, 0.54, -6, -6, 4, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.28, -100, -16, -14, 0.54, -34, -16, -8, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.28, -34, 20, -4, 0.54, -44, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.28, -12, 10, 7, 0.54, -52, 10, 7, 0.7, -44, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.28, 18, 0, -1, 0.54, 70, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.54, 14, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.28, -28, -16, -9, 0.54, -18, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.28, 42, 0, 1, 0.54, 28, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.28, 0, 0.06, 0.14, 0.54, 0, -0.08, 0.64, 0.7, 0, -0.06, 0.82, 1, 0, 0, 1.0] },
});

/* CH5 — Serpent Runs. Travel with the head trailing wide, so the chain sweeps
 * a corridor rather than a point. */
clip('cambuk_ch5', 1.16, {
  blend: 0.12, rm: 3.2, tags: 'attack',
  events: [ev(0.10, 'voice', { tag: 'shout' }), ev(0.20, 'foot', { side: 'R' }),
    ev(0.26, 'hitboxOn', { arc: 120, reach: 4.0, height: 1.1, power: 0.9, dmg: 1.5 }),
    ev(0.44, 'foot', { side: 'L' }), ev(0.64, 'swoosh', { power: 1.2 }),
    ev(0.86, 'hitboxOff'), ev(1.04, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 16, 34, -2, 0.5, 18, -56, 2, 0.74, 12, -150, 2, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 10, 14, 0, 0.5, 12, -22, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 8, 12, 1, 0.5, 10, -20, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, 12, 18, 1, 0.5, 14, -28, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 6, -6, 0, 0.5, 6, 16, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, 2, -8, 0, 0.5, 2, 18, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 4, 10, 28, 0.5, 4, 8, 24, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, -58, 30, 42, 0.5, -62, 26, 38, 0.74, -54, 28, 36, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -12, -22, 6, 0.74, -14, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, 8, -6, 0, 0.74, 8, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.2, 4, -10, -24, 0.5, 4, -8, -20, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, -22, -26, -30, 0.5, -26, -22, -26, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -40, 20, -4, 0.5, -42, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, 14, 10, 7, 0.36, -52, 10, 7, 0.5, 12, 10, 7, 0.74, -46, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 8, 0, -1, 0.36, 66, 0, -1, 0.5, 10, 0, -1, 0.74, 60, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.36, -18, 12, 0, 0.5, 12, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.2, -54, -16, -9, 0.36, 12, -16, -9, 0.5, -48, -16, -9, 0.74, 10, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 68, 0, 1, 0.36, 10, 0, 1, 0.5, 64, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.2, -20, -18, 0, 0.36, 14, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, -0.05, 0.46, 0.5, 0, -0.06, 1.54, 0.74, 0, -0.04, 2.40, 1, 0, 0, 3.2] },
});

/* MOUNTAIN FALLS ON WATER — the gada's Battle Art and musou finisher.
 *
 * Every weapon in the game ended its musou on `musou_finish`, a 513-degree
 * spin. For three of them that is right. For this one it contradicts the whole
 * instrument: the gada is built on PITCH, not yaw — hipsYaw 4 to 26 across
 * seven of its eleven moves where the glaive's cheapest is 122 — and finishing
 * it with the biggest spin in the set said the opposite of everything the
 * chain had spent eleven clips establishing.
 *
 * So the finisher is what the weapon is, at its largest: both hands take the
 * head as high as the body reaches, the whole mass comes down on one spot, and
 * the ring goes out from where it lands rather than from where the hero turns.
 * Almost no hip yaw at all, and the longest hold at the bottom of any clip in
 * the library — the pause after the impact is the point of it.
 */
clip('gada_musou', 1.30, {
  blend: 0.13, fps: 15, rm: 1.2, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.40, 'swoosh', { power: 1.4 }),
    ev(0.50, 'hitboxOn', { arc: 180, reach: 7.0, height: 1.0, power: 1.0, dmg: 3.2, launch: true, gb: true }),
    ev(0.50, 'vfx', { name: 'groundSlam' }), ev(0.52, 'sfx', { name: 'impact_slam' }),
    ev(0.64, 'hitboxOff'), ev(1.06, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.3, -26, -15, -1, 0.44, -22, -15, -1, 0.5, 30, -14, 0, 0.66, 28, -14, 0, 0.86, 14, -14, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.3, -15, -5, 0, 0.5, 19, -5, 0, 0.66, 17, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.3, -17, -6, 1, 0.5, 21, -6, 1, 0.66, 19, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.3, -24, -8, 1, 0.5, 28, -8, 1, 0.66, 25, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.3, 22, 12, 0, 0.5, -22, 12, 0, 0.66, -18, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.3, 26, 14, 0, 0.5, -28, 14, 0, 0.66, -24, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.3, 30, 6, 24, 0.5, -14, 6, -10, 0.66, -12, 6, -8, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.3, -172, 20, -26, 0.44, -166, 20, -26, 0.5, 60, 20, 28, 0.66, 54, 20, 26, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.3, -8, -22, 6, 0.5, -2, -22, 6, 0.66, -8, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.3, -54, -6, 0, 0.5, 46, -6, 0, 0.66, 40, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.3, 30, -6, -24, 0.5, -14, -6, 10, 0.66, -12, -6, 8, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.3, -174, -16, 28, 0.44, -168, -16, 28, 0.5, 56, -16, -26, 0.66, 50, -16, -24, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.3, -6, 20, -4, 0.5, -2, 20, -4, 0.66, -6, 20, -4, 1, -62, 20, -4],
    handL: [0, -14, 6, 0, 0.3, -50, 6, 0, 0.5, 44, 6, 0, 1, -14, 6, 0],
    thighL: [0, -14, 10, 7, 0.3, -24, 10, 7, 0.5, -92, 9, 7, 0.66, -86, 9, 7, 0.86, -30, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.3, 30, 0, -1, 0.5, 118, 0, -1, 0.66, 112, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.5, 24, 11, 0, 0.66, 22, 11, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.3, -28, -16, -9, 0.5, -94, -15, -9, 0.66, -88, -15, -9, 0.86, -32, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.3, 36, 0, 1, 0.5, 120, 0, 1, 0.66, 114, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.5, 26, -17, 0, 0.66, 24, -17, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.3, 0, 0.12, 0.18, 0.5, 0, -0.14, 0.82, 0.66, 0, -0.13, 1.00, 0.86, 0, -0.04, 1.16, 1, 0, 0, 1.2] },
});

/* Scissor Finish — the twin sabres' chain finisher, and the last shared clip
 * in the library.
 *
 * Until this existed, SABRES c6 and GLAIVE c5 both ran `attack_heavy`. The
 * distinctness tool passed them, correctly: different sockets and reaches make
 * the two tip paths genuinely different in space. But it is ONE BODY MOTION
 * appearing in two weapons' chains, and a player reads the body, not the
 * socket — so the sixth press of the sabre chain looked like the fifth press
 * of the glaive's.
 *
 * The fix is a motion only a two-bladed set can make: both arms cross the
 * body wide and then scissor SHUT through the middle, left over right, with
 * the hips counter-rotating. A polearm cannot do it because a polearm is one
 * lever held in two hands, which is exactly why it separates the two chains
 * where a retimed shared clip could not.
 */
clip('sabre_c6_scissor', 0.72, {
  blend: 0.11, rm: 1.5, tags: 'attack',
  events: [ev(0.18, 'voice', { tag: 'shout' }), ev(0.34, 'swoosh', { power: 1.2 }),
    ev(0.40, 'hitboxOn', { arc: 130, reach: 2.8, height: 1.2, power: 1.1, dmg: 2.5, launch: true, gb: true }),
    ev(0.46, 'swoosh', { power: 1.0 }), ev(0.58, 'hitboxOff'), ev(0.82, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.24, 6, 40, -2, 0.46, 8, -44, 2, 0.72, 6, -22, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.24, 4, 18, 0, 0.46, 6, -20, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.24, 0, 16, 1, 0.46, 2, -18, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.24, -2, 24, 1, 0.46, 0, -26, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.24, 8, -10, 0, 0.46, 8, 18, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.24, -2, -12, 0, 0.46, -2, 20, 0, 1, -4, 14, 0],
    /* Both arms open WIDE at 0.24 and shut across the centre line at 0.46 —
     * the scissor. The rz terms are what open them; the glaive's heavy has no
     * equivalent because both its hands are on one shaft. */
    clavicleR: [0, 2, 6, 10, 0.24, 4, 8, 30, 0.46, 2, 4, -18, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.24, -38, 26, 52, 0.46, -30, 10, -34, 0.62, -22, 16, -12, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.24, -30, -22, 6, 0.46, -54, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.24, -18, -6, 0, 0.46, 14, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.24, 4, -8, -30, 0.46, 2, -4, 18, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.24, -44, -26, -52, 0.46, -34, -10, 34, 0.62, -28, -16, 12, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.24, -26, 20, -4, 0.46, -50, 20, -4, 1, -62, 20, -4],
    handL: [0, -14, 6, 0, 0.24, -22, 6, 0, 0.46, 10, 6, 0, 1, -14, 6, 0],
    thighL: [0, -14, 10, 7, 0.24, -30, 12, 7, 0.46, -40, 8, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.24, 44, 0, -1, 0.46, 56, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.46, 10, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.24, -40, -16, -9, 0.46, -26, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.24, 54, 0, 1, 0.46, 38, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.24, 0, -0.05, 0.34, 0.46, 0, -0.06, 1.02, 0.62, 0, -0.03, 1.32, 1, 0, 0, 1.5] },
});

/* =========================================================================
 *  GADA — the heavy blunt chain (DEV_PLAN Step 8, weapon 1 of 3)
 * -------------------------------------------------------------------------
 *  THE ARCHETYPE IS AN AXIS, not a flavour. Measured with tools/clip-shape.mjs,
 *  the three weapons already here all live in the same place: their swept boxes
 *  are Z-dominant (forward reach), their tips stay above 1.0 m for nearly every
 *  move, and the big ones get big by spinning the hips — 455 deg for the
 *  glaive's Whirling Moon, 487 for the spear's Wheeling Haft, 798 for Dragon
 *  Tail. Nothing in the library swings LOW, and nothing gets its power from
 *  pitch instead of yaw.
 *
 *  So the gada is authored on the axis nobody occupies:
 *
 *    - VERTICAL. Power comes from hips/chest PITCH — lift the head over the
 *      shoulder and drop it — so hipsYaw stays small on everything except the
 *      one move that is deliberately a spin (ch4).
 *    - LOW. Three of these put the head at or below knee height inside the
 *      active window. c2 sweeps at shin height for its whole arc, which no
 *      other move in the game does at all.
 *    - SHORT. The head is 1.05 m from the grip against the glaive's 1.78, so
 *      the paths come out shorter even where the body works harder. That is
 *      the trade the weapon is: you give up a metre of reach for a head that
 *      does not care what it hits.
 *
 *  SLOW IS THE OTHER HALF. Every windup here is longer than its opposite
 *  number on the glaive and every recovery is much longer. A mace that
 *  recovered like a sabre would be strictly better than one, and the moveset
 *  would have nothing to say.
 * ========================================================================= */

/* C1 — Overhead Drop. The plainest statement of the weapon: lift it past the
 * shoulder and bring it down on the crown of a man's head. */
clip('gada_c1', 0.62, {
  blend: 0.10, rm: 0.5, tags: 'attack',
  events: [ev(0.34, 'swoosh', { power: 1.0 }),
    ev(0.40, 'hitboxOn', { arc: 46, reach: 2.0, height: 1.0, power: 1.0, dmg: 2.0, gb: true }),
    ev(0.54, 'hitboxOff'), ev(0.46, 'sfx', { name: 'impact_slam' }),
    ev(0.72, 'cancel'), ev(0.18, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.22, -11, -8, -2, 0.44, 18, -12, 1, 0.62, 13, -13, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.22, -8, -3, 0, 0.44, 14, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.22, -10, -4, 1, 0.44, 13, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.22, -15, -5, 1, 0.44, 17, -8, 1, 0.62, 11, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.22, 16, 10, 0, 0.44, -14, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.22, 18, 12, 0, 0.44, -20, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.22, 16, 6, 16, 0.44, -8, 6, -4, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.22, -124, 18, -14, 0.44, 30, 20, 18, 0.62, 6, 20, 12, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.22, -26, -22, 6, 0.44, -10, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.22, -36, -6, 0, 0.44, 26, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.22, 16, -6, -16, 0.44, -8, -6, 4, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.22, -128, -14, 16, 0.44, 26, -16, -16, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.22, -22, 20, -4, 0.44, -8, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.22, -6, 10, 7, 0.44, -32, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.22, 14, 0, -1, 0.44, 46, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.22, -26, -16, -9, 0.44, -10, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.22, 38, 0, 1, 0.44, 18, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.22, 0, 0.04, -0.06, 0.44, 0, -0.03, 0.38, 1, 0, 0, 0.5] },
});

/* C2 — Shin Sweep. The move no other weapon has: the head travels a full
 * horizontal arc at ANKLE height, so it is a trip rather than a cut, and its
 * tip band is the lowest in the library. */
clip('gada_c2', 0.58, {
  blend: 0.10, rm: 0.4, tags: 'attack',
  events: [ev(0.28, 'swoosh', { power: 0.9 }),
    ev(0.34, 'hitboxOn', { arc: 120, reach: 2.2, height: 0.3, power: 0.9, dmg: 1.7, launch: true }),
    ev(0.52, 'hitboxOff'), ev(0.68, 'cancel'), ev(0.16, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 16, 30, -2, 0.4, 20, -48, 2, 0.58, 15, -34, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 11, 14, 0, 0.4, 14, -22, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 8, 12, 1, 0.4, 11, -20, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, 10, 16, 1, 0.4, 14, -26, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 6, -6, 0, 0.4, 2, 16, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, 12, -8, 0, 0.4, 10, 18, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 6, 10, 18, 0.4, 2, 2, -12, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, 24, 34, 26, 0.4, 44, 6, -22, 0.58, 20, 14, -6, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -34, -22, 6, 0.4, -16, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, 8, -6, 0, 0.4, 22, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.2, 6, -10, -18, 0.4, 2, -2, 12, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, 16, -30, -26, 0.4, 40, -8, 20, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -30, 20, -4, 0.4, -14, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -40, 12, 7, 0.4, -52, 8, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 58, 0, -1, 0.4, 72, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -30, -16, -9, 0.4, -44, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 48, 0, 1, 0.4, 66, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, -0.14, 0.10, 0.4, 0, -0.18, 0.30, 1, 0, 0, 0.4] },
});

/* C3 — Rising Crown. Scooped off the ground and driven up past the jaw: the
 * inverse of C1, and the only move here whose tip climbs through its window. */
clip('gada_c3', 0.66, {
  blend: 0.10, rm: 0.6, tags: 'attack',
  events: [ev(0.30, 'swoosh', { power: 1.0 }),
    ev(0.36, 'hitboxOn', { arc: 60, reach: 2.1, height: 1.2, power: 1.0, dmg: 2.0, launch: true }),
    ev(0.54, 'hitboxOff'), ev(0.76, 'cancel'), ev(0.16, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.22, 21, -18, -2, 0.46, -13, -10, 2, 0.66, -6, -12, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.22, 14, -6, 0, 0.46, -9, -4, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.22, 11, -8, 1, 0.46, -12, -5, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.22, 14, -10, 1, 0.46, -16, -6, 1, 0.66, -10, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.22, 20, 12, 0, 0.46, -20, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.22, 24, 14, 0, 0.46, -26, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.22, 14, 6, -6, 0.46, -14, 6, 18, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.22, 46, 20, 10, 0.46, -104, 20, -10, 0.66, -56, 20, 0, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.22, -18, -22, 6, 0.46, -44, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.22, 24, -6, 0, 0.46, -34, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.22, 14, -6, 6, 0.46, -14, -6, -18, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.22, 40, -16, -12, 0.46, -108, -16, 12, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.22, -16, 20, -4, 0.46, -40, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.22, -44, 10, 7, 0.46, -6, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.22, 62, 0, -1, 0.46, 10, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.22, -40, -16, -9, 0.46, -8, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.22, 58, 0, 1, 0.46, 12, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.22, 0, -0.12, 0.14, 0.46, 0, 0.06, 0.44, 1, 0, 0, 0.6] },
});

/* C4 — Waist Turn. A flat swing through the middle of a crowd with the whole
 * torso behind it. The chain's one wide move, and still no spin. */
clip('gada_c4', 0.74, {
  blend: 0.11, rm: 0.7, tags: 'attack',
  events: [ev(0.32, 'swoosh', { power: 1.1 }),
    ev(0.38, 'hitboxOn', { arc: 150, reach: 2.3, height: 1.0, power: 1.05, dmg: 2.1, gb: true }),
    ev(0.60, 'hitboxOff'), ev(0.84, 'cancel'), ev(0.2, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.22, 6, 58, -3, 0.5, 8, -76, 3, 0.74, 6, -40, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.22, 4, 26, 0, 0.5, 6, -34, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.22, 2, 24, 1, 0.5, 4, -32, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.22, 2, 32, 1, 0.5, 6, -42, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.22, 6, -14, 0, 0.5, 6, 26, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.22, -2, -16, 0, 0.5, -2, 28, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.22, 4, 10, 22, 0.5, 2, 2, -14, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.22, -34, 34, 34, 0.5, -20, 6, -26, 0.74, -18, 14, -6, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.22, -46, -22, 6, 0.5, -34, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.22, -4, -6, 0, 0.5, 10, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.22, 4, -10, -22, 0.5, 2, -2, 14, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.22, -44, -30, -34, 0.5, -26, -8, 24, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.22, -42, 20, -4, 0.5, -30, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.22, -22, 14, 7, 0.5, -30, 6, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.22, 36, 0, -1, 0.5, 44, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.22, -30, -16, -9, 0.5, -22, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.22, 42, 0, 1, 0.5, 34, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.22, 0, -0.05, 0.16, 0.5, 0, -0.04, 0.48, 1, 0, 0, 0.7] },
});

/* C5 — Stepping Smash. C1 again with a full stride behind it and the head
 * carried further over the shoulder, so the drop is longer and lands harder. */
clip('gada_c5', 0.90, {
  blend: 0.12, rm: 1.2, tags: 'attack',
  events: [ev(0.42, 'swoosh', { power: 1.2 }), ev(0.24, 'foot', { side: 'L' }),
    ev(0.48, 'hitboxOn', { arc: 56, reach: 2.4, height: 0.9, power: 1.15, dmg: 2.4, gb: true }),
    ev(0.64, 'hitboxOff'), ev(0.52, 'vfx', { name: 'groundSlam' }),
    ev(0.54, 'sfx', { name: 'impact_slam' }), ev(0.98, 'cancel'), ev(0.24, 'voice', { tag: 'shout' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.28, -16, -6, -3, 0.5, 25, -14, 2, 0.68, 19, -14, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.28, -9, -2, 0, 0.5, 17, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.28, -12, -3, 1, 0.5, 16, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.28, -18, -4, 1, 0.5, 20, -8, 1, 0.68, 14, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.28, 20, 10, 0, 0.5, -20, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.28, 24, 12, 0, 0.5, -28, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.28, 22, 6, 18, 0.5, -10, 6, -6, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.28, -142, 18, -18, 0.5, 40, 20, 20, 0.68, 16, 20, 12, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.28, -20, -22, 6, 0.5, -6, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.28, -44, -6, 0, 0.5, 34, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.28, 22, -6, -18, 0.5, -10, -6, 6, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.28, -146, -14, 20, 0.5, 34, -16, -18, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.28, -16, 20, -4, 0.5, -4, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.28, -56, 10, 7, 0.5, -24, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.28, 66, 0, -1, 0.5, 30, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.28, -22, 12, 0, 0.5, 8, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.28, -14, -16, -9, 0.5, -40, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.28, 22, 0, 1, 0.5, 52, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.28, 0, 0.05, 0.22, 0.5, 0, -0.06, 0.94, 0.68, 0, -0.03, 1.14, 1, 0, 0, 1.2] },
});

/* C6 — Earth Shatter. The chain finisher: both hands over the head, a drop
 * that ends with the head planted in the dirt, and the slowest recovery of any
 * light-chain move in the game. */
clip('gada_c6', 1.16, {
  blend: 0.13, rm: 1.0, tags: 'attack',
  events: [ev(0.20, 'voice', { tag: 'shout' }), ev(0.46, 'swoosh', { power: 1.3 }),
    ev(0.52, 'hitboxOn', { arc: 180, reach: 3.4, height: 0.6, power: 1.0, dmg: 2.8, launch: true, gb: true }),
    ev(0.56, 'vfx', { name: 'groundSlam' }), ev(0.70, 'hitboxOff'),
    ev(0.58, 'sfx', { name: 'impact_slam' }), ev(1.02, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.3, -23, -10, -2, 0.46, -20, -12, -2, 0.56, 32, -14, 1, 0.72, 28, -14, 0, 0.88, 14, -14, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.3, -13, -5, 0, 0.56, 19, -5, 0, 0.72, 16, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.3, -16, -6, 1, 0.56, 19, -6, 1, 0.72, 16, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.3, -23, -8, 1, 0.56, 26, -8, 1, 0.72, 22, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.3, 24, 12, 0, 0.56, -26, 12, 0, 0.72, -22, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.3, 28, 14, 0, 0.56, -34, 14, 0, 0.72, -28, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.3, 26, 6, 20, 0.56, -12, 6, -8, 0.72, -10, 6, -6, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.3, -158, 20, -22, 0.46, -152, 20, -22, 0.56, 52, 20, 24, 0.72, 46, 20, 22, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.3, -14, -22, 6, 0.56, -4, -22, 6, 0.72, -10, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.3, -48, -6, 0, 0.56, 40, -6, 0, 0.72, 34, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.3, 26, -6, -20, 0.56, -12, -6, 8, 0.72, -10, -6, 6, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.3, -160, -16, 24, 0.46, -154, -16, 24, 0.56, 48, -16, -22, 0.72, 42, -16, -20, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.3, -12, 20, -4, 0.56, -4, 20, -4, 0.72, -8, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.3, -20, 10, 7, 0.56, -84, 9, 7, 0.72, -78, 9, 7, 0.88, -32, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.3, 26, 0, -1, 0.56, 106, 0, -1, 0.72, 100, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.56, 20, 11, 0, 0.72, 18, 11, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.3, -24, -16, -9, 0.56, -86, -15, -9, 0.72, -80, -15, -9, 0.88, -34, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.3, 32, 0, 1, 0.56, 108, 0, 1, 0.72, 102, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.56, 22, -17, 0, 0.72, 20, -17, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.3, 0, 0.06, 0.16, 0.56, 0, -0.11, 0.78, 0.72, 0, -0.10, 0.92, 0.88, 0, -0.03, 1.0, 1, 0, 0, 1.0] },
});

/* CH1 — Skyward Lift. The launcher. A scoop that starts behind the heel and
 * finishes over the head, carrying whatever it caught with it. */
clip('gada_ch1', 0.86, {
  blend: 0.11, rm: 0.7, tags: 'attack',
  events: [ev(0.18, 'voice', { tag: 'shout' }), ev(0.38, 'swoosh', { power: 1.2 }),
    ev(0.44, 'hitboxOn', { arc: 70, reach: 2.2, height: 1.4, power: 1.1, dmg: 2.3, launch: true }),
    ev(0.62, 'hitboxOff'), ev(0.94, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.26, 25, -20, -2, 0.54, -20, -8, 3, 0.74, -11, -12, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.26, 16, -6, 0, 0.54, -13, -3, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.26, 13, -8, 1, 0.54, -16, -4, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.26, 18, -12, 1, 0.54, -23, -5, 1, 0.74, -15, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.26, 24, 12, 0, 0.54, -28, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.26, 30, 14, 0, 0.54, -36, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.26, 18, 6, -8, 0.54, -18, 6, 22, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.26, 58, 20, 12, 0.54, -146, 20, -14, 0.74, -84, 20, 2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.26, -12, -22, 6, 0.54, -52, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.26, 30, -6, 0, 0.54, -44, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.26, 18, -6, 8, 0.54, -18, -6, -22, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.26, 52, -16, -14, 0.54, -150, -16, 16, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.26, -10, 20, -4, 0.54, -48, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.26, -52, 10, 7, 0.54, 4, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.26, 70, 0, -1, 0.54, 4, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.26, -48, -16, -9, 0.54, 2, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.26, 66, 0, 1, 0.54, 6, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.26, 0, -0.16, 0.12, 0.54, 0, 0.12, 0.48, 0.74, 0, 0.04, 0.64, 1, 0, 0, 0.7] },
});

/* CH2 — Haft Jab. The short one. A butt-end punch with the bottom of the
 * shaft: almost no arc, almost no travel, and the only gada move whose path is
 * under three metres — which is exactly what a stun should cost. */
clip('gada_ch2', 0.52, {
  blend: 0.09, rm: 0.5, tags: 'attack',
  events: [ev(0.26, 'swoosh', { power: 0.7 }),
    ev(0.32, 'hitboxOn', { arc: 34, reach: 1.6, height: 1.2, power: 0.85, dmg: 1.2 }),
    ev(0.44, 'hitboxOff'), ev(0.62, 'cancel'), ev(0.14, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 2, -30, -1, 0.38, 6, 4, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 4, -14, 0, 0.38, 6, 4, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 0, -14, 1, 0.38, 2, 4, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, -2, -20, 1, 0.38, 0, 6, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 8, 16, 0, 0.38, 6, 2, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, -2, 18, 0, 0.38, -4, 2, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 6, 6, 16, 0.38, -2, 6, 2, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, -34, 22, 14, 0.38, 8, 18, -2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -96, -22, 6, 0.38, -34, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, -18, -6, 0, 0.38, 6, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.2, 6, -6, -16, 0.38, -2, -6, -2, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, -52, -16, -14, 0.38, -6, -16, 2, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -88, 20, -4, 0.38, -28, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -8, 10, 7, 0.38, -28, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 14, 0, -1, 0.38, 38, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -24, -16, -9, 0.38, -12, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 34, 0, 1, 0.38, 20, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0, -0.08, 0.38, 0, 0, 0.36, 1, 0, 0, 0.5] },
});

/* CH3 — Quake. Both hands, a drop to one knee, and the head driven into the
 * ground so hard the ring goes out past anything else in the set. */
clip('gada_ch3', 1.10, {
  blend: 0.12, rm: 0.9, tags: 'attack',
  events: [ev(0.16, 'voice', { tag: 'shout' }), ev(0.44, 'swoosh', { power: 1.35 }),
    ev(0.50, 'hitboxOn', { arc: 180, reach: 5.4, height: 0.5, power: 1.0, dmg: 2.9, launch: true, gb: true }),
    ev(0.54, 'vfx', { name: 'groundSlam' }), ev(0.68, 'hitboxOff'),
    ev(0.56, 'sfx', { name: 'impact_slam' }), ev(0.94, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.28, -25, -12, -2, 0.44, -22, -13, -2, 0.54, 34, -14, 1, 0.7, 30, -14, 0, 0.88, 15, -14, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.28, -14, -5, 0, 0.54, 20, -5, 0, 0.7, 17, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.28, -16, -6, 1, 0.54, 19, -6, 1, 0.7, 17, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.28, -25, -8, 1, 0.54, 27, -8, 1, 0.7, 24, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.28, 26, 12, 0, 0.54, -30, 12, 0, 0.7, -24, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.28, 30, 14, 0, 0.54, -36, 14, 0, 0.7, -30, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.28, 28, 6, 22, 0.54, -14, 6, -10, 0.7, -12, 6, -8, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.28, -164, 20, -24, 0.44, -158, 20, -24, 0.54, 58, 20, 26, 0.7, 52, 20, 24, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.28, -10, -22, 6, 0.54, -2, -22, 6, 0.7, -8, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.28, -52, -6, 0, 0.54, 44, -6, 0, 0.7, 38, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.28, 28, -6, -22, 0.54, -14, -6, 10, 0.7, -12, -6, 8, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.28, -166, -16, 26, 0.44, -160, -16, 26, 0.54, 54, -16, -24, 0.7, 48, -16, -22, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.28, -8, 20, -4, 0.54, -2, 20, -4, 0.7, -6, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.28, -22, 10, 7, 0.54, -96, 9, 7, 0.7, -90, 9, 7, 0.88, -34, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.28, 28, 0, -1, 0.54, 122, 0, -1, 0.7, 116, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.54, 24, 11, 0, 0.7, 22, 11, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.28, -26, -16, -9, 0.54, -98, -15, -9, 0.7, -92, -15, -9, 0.88, -36, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.28, 34, 0, 1, 0.54, 124, 0, 1, 0.7, 118, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.54, 26, -17, 0, 0.7, 24, -17, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.28, 0, 0.07, 0.14, 0.54, 0, -0.13, 0.70, 0.7, 0, -0.12, 0.84, 0.88, 0, -0.04, 0.9, 1, 0, 0, 0.9] },
});

/* CH4 — Grinding Wheel. The one spin, and it spins LOW: two full turns with
 * the head at hip height, so it out-travels everything else here while staying
 * under the guard a standing man holds. */
clip('gada_ch4', 1.24, {
  blend: 0.12, rm: 1.6, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'shout' }), ev(0.30, 'swoosh', { power: 1.1 }),
    ev(0.34, 'hitboxOn', { arc: 180, reach: 2.8, height: 0.8, power: 1.0, dmg: 1.9, gb: true }),
    ev(0.60, 'swoosh', { power: 1.2 }), ev(0.86, 'hitboxOff'), ev(1.08, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.16, 18, 40, -2, 0.36, 22, -130, 2, 0.56, 22, -300, 2, 0.76, 20, -470, 2, 0.9, 14, -530, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.16, 12, 10, 0, 0.56, 14, -12, 0, 0.9, 10, -8, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.16, 10, 8, 1, 0.56, 12, -10, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.16, 14, 14, 1, 0.56, 16, -16, 1, 0.9, 10, -10, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.16, 8, 6, 0, 0.56, 8, 8, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.16, 6, 8, 0, 0.56, 6, 10, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.16, 4, 8, 20, 0.56, 4, 8, 20, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.16, 10, 30, 30, 0.36, 14, 30, 30, 0.76, 14, 30, 30, 0.9, 4, 24, 18, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.16, -30, -22, 6, 0.76, -28, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.16, 12, -6, 0, 0.76, 12, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.16, 4, -8, -20, 0.56, 4, -8, -20, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.16, 2, -26, -30, 0.36, 6, -26, -30, 0.76, 6, -26, -30, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.16, -26, 20, -4, 0.76, -24, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.16, -34, 10, 7, 0.36, -10, 10, 7, 0.56, -36, 10, 7, 0.76, -12, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.16, 50, 0, -1, 0.36, 16, 0, -1, 0.56, 52, 0, -1, 0.76, 18, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.16, -12, -16, -9, 0.36, -38, -16, -9, 0.56, -14, -16, -9, 0.76, -40, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.16, 18, 0, 1, 0.36, 54, 0, 1, 0.56, 20, 0, 1, 0.76, 56, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.16, 0, -0.10, 0.18, 0.36, 0, -0.11, 0.56, 0.56, 0, -0.10, 0.96, 0.76, 0, -0.10, 1.34, 0.9, 0, -0.04, 1.54, 1, 0, 0, 1.6] },
});

/* CH5 — Bull Rush. Carries the head low and forward at a run and then throws
 * it up through whatever it reached, so it is the set's travel move and its
 * only one that ends higher than it started. */
clip('gada_ch5', 1.20, {
  blend: 0.12, rm: 3.0, tags: 'attack',
  events: [ev(0.12, 'voice', { tag: 'shout' }), ev(0.22, 'foot', { side: 'R' }),
    ev(0.30, 'hitboxOn', { arc: 70, reach: 2.4, height: 0.8, power: 0.9, dmg: 1.6 }),
    ev(0.44, 'foot', { side: 'L' }), ev(0.62, 'swoosh', { power: 1.3 }),
    ev(0.66, 'hitboxOn', { arc: 90, reach: 2.6, height: 1.5, power: 1.15, dmg: 2.5, launch: true, gb: true }),
    ev(0.84, 'hitboxOff'), ev(1.06, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 18, -16, -2, 0.5, 20, -18, -2, 0.68, -17, -6, 3, 0.86, -8, -12, 1, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 12, -5, 0, 0.5, 14, -5, 0, 0.68, -11, -3, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 10, -6, 1, 0.5, 11, -6, 1, 0.68, -14, -4, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, 12, -8, 1, 0.5, 14, -8, 1, 0.68, -20, -5, 1, 0.86, -12, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 14, 12, 0, 0.68, -22, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, 10, 14, 0, 0.68, -30, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 8, 6, 4, 0.68, -16, 6, 20, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, 34, 20, 8, 0.5, 38, 20, 8, 0.68, -132, 20, -12, 0.86, -70, 20, 2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -20, -22, 6, 0.68, -48, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, 18, -6, 0, 0.68, -40, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.2, 8, -6, -4, 0.68, -16, -6, -20, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, 28, -16, -10, 0.5, 32, -16, -10, 0.68, -136, -16, 14, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -18, 20, -4, 0.68, -44, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, 16, 10, 7, 0.36, -54, 10, 7, 0.5, 14, 10, 7, 0.68, -40, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 8, 0, -1, 0.36, 68, 0, -1, 0.5, 10, 0, -1, 0.68, 54, 0, -1, 1, 22, 0, -1],
    footL: [0, -6, 12, 0, 0.36, -20, 12, 0, 0.5, 14, 12, 0, 1, -6, 12, 0],
    thighR: [0, -19, -16, -9, 0.2, -56, -16, -9, 0.36, 14, -16, -9, 0.5, -50, -16, -9, 0.68, -6, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 70, 0, 1, 0.36, 10, 0, 1, 0.5, 66, 0, 1, 0.68, 12, 0, 1, 1, 29, 0, 1],
    footR: [0, -8, -18, 0, 0.2, -22, -18, 0, 0.36, 16, -18, 0, 1, -8, -18, 0],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, -0.08, 0.42, 0.5, 0, -0.09, 1.44, 0.68, 0, 0.06, 2.18, 0.86, 0, 0.02, 2.74, 1, 0, 0, 3.0] },
});

/* =========================================================================
 *  KAPAK GARUDA — paired war axes
 * -------------------------------------------------------------------------
 *  The weapon is short (0.88 m, bit ending at 0.56) and heavy, and it is
 *  carried in both fists. That dictates the animation more than the silhouette
 *  does: a paired weapon's rhythm is ALTERNATION. The chain hands the work from
 *  right to left and back, so the hero is never fully unwound between blows —
 *  one arm recovers while the other is already committing. That is what
 *  "a different rhythm" has to mean in the bones, not just in the frame counts.
 *
 *  Every track here is built on GUARD and kept close to it. The spine is a
 *  CHAIN: hips + spine1 + spine2 + chest pitches compound, and the gada's
 *  first pass put a clip at 148 degrees of cumulative lean that photographed
 *  as a somersault. These stay inside roughly +/-20 of the stance per track.
 *  Elbows keep foreArm.rx negative and knees keep shin.rx positive, which is
 *  the rig's own flexion convention and what pose-check reads.
 * ========================================================================= */

/* C1 — Right Rend. A short downward diagonal off the right fist. Fast, because
 * a weapon with no reach cannot also be slow to start. */
clip('kapak_c1', 0.44, {
  blend: 0.08, rm: 0.5, tags: 'attack',
  events: [ev(0.22, 'swoosh', { power: 0.85 }),
    ev(0.27, 'hitboxOn', { arc: 70, reach: 1.5, height: 1.1, power: 0.9, dmg: 1.5 }),
    ev(0.41, 'hitboxOff'), ev(0.30, 'sfx', { name: 'impact_slash' }),
    ev(0.58, 'cancel'), ev(0.12, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 6, -22, -2, 0.44, 8, -4, 2, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 5, -12, 0, 0.44, 7, 3, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 2, -13, 1, 0.44, 6, 2, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, -1, -16, 1, 0.44, 8, 4, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 8, 16, 0, 0.44, 4, 4, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, -2, 18, 0, 0.44, -8, 4, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 10, 6, 18, 0.44, -4, 6, 2, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, -84, 26, -10, 0.44, 22, 14, 20, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -52, -22, 6, 0.44, -26, -22, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.2, -28, -6, 0, 0.44, 16, -6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.2, -2, -6, -6, 0.44, 6, -6, -14, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, -26, -14, -12, 0.44, -42, -18, -4, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -70, 20, -4, 0.44, -56, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -10, 10, 7, 0.44, -20, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 18, 0, -1, 0.44, 30, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -24, -16, -9, 0.44, -14, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 34, 0, 1, 0.44, 22, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0.02, -0.03, 0.44, 0, 0, 0.30, 1, 0, 0, 0.40] },
});

/* C2 — Left Rend. The mirror, and FASTER: the right arm is still unwinding, so
 * the left one starts from a cocked position it never had to take. */
clip('kapak_c2', 0.40, {
  blend: 0.08, rm: 0.5, tags: 'attack',
  events: [ev(0.18, 'swoosh', { power: 0.85 }),
    ev(0.23, 'hitboxOn', { arc: 70, reach: 1.5, height: 1.1, power: 0.9, dmg: 1.5 }),
    ev(0.38, 'hitboxOff'), ev(0.26, 'sfx', { name: 'impact_slash' }),
    ev(0.56, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.18, 6, -4, 2, 0.4, 8, -20, -2, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.18, 5, 4, 0, 0.4, 7, -12, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.18, 2, 3, 1, 0.4, 6, -13, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.18, -1, 6, 1, 0.4, 8, -14, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.18, 8, 2, 0, 0.4, 4, 16, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.18, -2, 2, 0, 0.4, -8, 18, 0, 1, -4, 14, 0],
    clavicleL: [0, 2, -6, -10, 0.18, 10, -6, -18, 0.4, -4, -6, -2, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.18, -88, -24, 12, 0.4, 16, -12, -22, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.18, -48, 20, -4, 0.4, -24, 20, -4, 1, -62, 20, -4],
    handL: [0, -14, 6, 0, 0.18, -30, 6, 0, 0.4, 14, 6, 0, 1, -14, 6, 0],
    clavicleR: [0, 2, 6, 10, 0.18, -2, 6, 6, 0.4, 6, 6, 14, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.18, -12, 18, 10, 0.4, -30, 22, 2, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.18, -84, -22, 6, 0.4, -70, -22, 6, 1, -78, -22, 6],
    thighL: [0, -14, 10, 7, 0.18, -20, 10, 7, 0.4, -10, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.18, 30, 0, -1, 0.4, 18, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.18, -14, -16, -9, 0.4, -24, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.18, 22, 0, 1, 0.4, 34, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.18, 0, 0.02, -0.02, 0.4, 0, 0, 0.28, 1, 0, 0, 0.36] },
});

/* C3 — Cross Rip. Both bits pass through each other at chest height, arms
 * opening from crossed. The only move in the chain that is genuinely two
 * weapons at once rather than one after the other. */
clip('kapak_c3', 0.52, {
  blend: 0.09, rm: 0.5, tags: 'attack',
  events: [ev(0.24, 'swoosh', { power: 1.0 }),
    ev(0.30, 'hitboxOn', { arc: 96, reach: 1.6, height: 1.2, power: 1.05, dmg: 1.8 }),
    ev(0.46, 'hitboxOff'), ev(0.33, 'sfx', { name: 'impact_slash' }),
    ev(0.66, 'cancel'), ev(0.14, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.22, 2, -18, 0, 0.5, 9, -8, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.22, 1, -9, 0, 0.5, 8, -1, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.22, -3, -10, 1, 0.5, 6, -2, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.22, -6, -12, 1, 0.5, 9, -3, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.22, 9, 13, 0, 0.5, 3, 9, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.22, 0, 15, 0, 0.5, -9, 11, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.22, 14, 6, 6, 0.5, -6, 6, 20, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.22, -40, 44, 30, 0.5, -20, -6, -16, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.22, -96, -22, 6, 0.5, -34, -22, 6, 1, -78, -22, 6],
    clavicleL: [0, 2, -6, -10, 0.22, 14, -6, -6, 0.5, -6, -6, -20, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.22, -44, -40, -30, 0.5, -24, 6, 18, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.22, -92, 20, -4, 0.5, -32, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.22, -18, 10, 7, 0.5, -12, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.22, 28, 0, -1, 0.5, 20, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.22, -23, -16, -9, 0.5, -16, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.22, 33, 0, 1, 0.5, 25, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.22, 0, 0.01, 0.06, 0.5, 0, 0, 0.34, 1, 0, 0, 0.42] },
});

/* C4 — Beak Hook. The BACK-SPIKE, not the bit: the right axe turns over and
 * the beak catches, so this one drags a man in rather than pushing him away.
 * The reason the head has a beak at all. */
clip('kapak_c4', 0.58, {
  blend: 0.10, rm: 0.6, tags: 'attack',
  events: [ev(0.30, 'swoosh', { power: 1.0 }),
    ev(0.36, 'hitboxOn', { arc: 54, reach: 1.7, height: 1.2, power: 1.1, dmg: 1.9 }),
    ev(0.52, 'hitboxOff'), ev(0.39, 'sfx', { name: 'impact_slam' }),
    ev(0.74, 'cancel'), ev(0.16, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.28, 10, -24, -3, 0.56, 6, 2, 3, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.28, 9, -13, 0, 0.56, 5, 5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.28, 6, -14, 1, 0.56, 3, 4, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.28, 3, -17, 1, 0.56, 6, 6, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.28, 10, 17, 0, 0.56, 5, 3, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.28, 2, 19, 0, 0.56, -10, 3, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.28, 16, 6, 20, 0.56, -8, 6, 0, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.28, -102, 34, -6, 0.56, 8, 6, 26, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.28, -44, -30, 6, 0.56, -62, -14, 6, 1, -78, -22, 6],
    handR: [0, -10, -6, 0, 0.28, -34, -20, 0, 0.56, 22, 6, 0, 1, -10, -6, 0],
    clavicleL: [0, 2, -6, -10, 0.28, -2, -6, -4, 0.56, 8, -6, -16, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.28, -22, -12, -14, 0.56, -46, -20, -2, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.28, -74, 20, -4, 0.56, -52, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.28, -8, 10, 7, 0.56, -22, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.28, 16, 0, -1, 0.56, 32, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.28, -27, -16, -9, 0.56, -12, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.28, 37, 0, 1, 0.56, 20, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.28, 0, 0.03, 0.10, 0.56, 0, 0, 0.36, 1, 0, 0, 0.46] },
});

/* C5 — Twin Drum. Four blows in one move, alternating, no pause between them.
 * The longest active window in the chain and the reason the weapon feels
 * relentless rather than punchy. */
clip('kapak_c5', 0.76, {
  blend: 0.10, rm: 0.6, tags: 'attack',
  events: [ev(0.18, 'swoosh', { power: 0.8 }),
    ev(0.23, 'hitboxOn', { arc: 80, reach: 1.5, height: 1.1, power: 0.85, dmg: 1.2 }),
    ev(0.62, 'hitboxOff'), ev(0.34, 'swoosh', { power: 0.8 }),
    ev(0.50, 'swoosh', { power: 0.9 }), ev(0.28, 'sfx', { name: 'impact_slash' }),
    ev(0.90, 'cancel'), ev(0.10, 'voice', { tag: 'effort' })],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 6, -20, -2, 0.38, 6, -4, 2, 0.56, 6, -20, -2, 0.74, 6, -4, 2, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 5, -12, 0, 0.38, 5, 3, 0, 0.56, 5, -12, 0, 0.74, 5, 3, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 2, -12, 1, 0.38, 2, 2, 1, 0.56, 2, -12, 1, 0.74, 2, 2, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.2, 0, -15, 1, 0.38, 0, 4, 1, 0.56, 0, -15, 1, 0.74, 0, 4, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 8, 15, 0, 0.38, 8, 5, 0, 0.56, 8, 15, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.2, -2, 17, 0, 0.38, -2, 5, 0, 0.56, -2, 17, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 12, 6, 18, 0.38, -4, 6, 2, 0.56, 12, 6, 18, 0.74, -4, 6, 2, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.2, -78, 26, -6, 0.38, 14, 14, 22, 0.56, -74, 26, -6, 0.74, 12, 14, 22, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.2, -50, -22, 6, 0.38, -30, -22, 6, 0.56, -50, -22, 6, 1, -78, -22, 6],
    clavicleL: [0, 2, -6, -10, 0.2, -4, -6, -2, 0.38, 12, -6, -18, 0.56, -4, -6, -2, 0.74, 12, -6, -18, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.2, 10, -14, -22, 0.38, -80, -22, 10, 0.56, 12, -14, -22, 0.74, -78, -22, 10, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.2, -32, 20, -4, 0.38, -52, 20, -4, 0.56, -32, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -10, 10, 7, 0.56, -18, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 18, 0, -1, 0.56, 28, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -23, -16, -9, 0.56, -15, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 33, 0, 1, 0.56, 24, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0.01, 0.10, 0.56, 0, 0.01, 0.30, 1, 0, 0, 0.48] },
});

/* C6 — Garuda Stoop. A short hop and both axes driven down together. The
 * chain's finisher and the FIRST UNBLOCKABLE MOVE IN THE GAME: a raised guard
 * does not stop it, which is the trade for a weapon that has to be this close
 * to use anything at all. */
clip('kapak_c6', 0.90, {
  blend: 0.12, rm: 0.8, tags: 'attack',
  events: [ev(0.10, 'voice', { tag: 'shout' }), ev(0.38, 'swoosh', { power: 1.4 }),
    ev(0.46, 'hitboxOn', { arc: 86, reach: 1.7, height: 1.4, power: 1.5, dmg: 2.8, gb: true }),
    ev(0.64, 'hitboxOff'), ev(0.49, 'sfx', { name: 'impact_slam' }),
    ev(0.52, 'foot', { side: 'R' }), ev(0.80, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.24, -12, -14, 0, 0.44, -16, -14, 0, 0.64, 16, -14, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.24, -9, -5, 0, 0.44, -12, -5, 0, 0.64, 13, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.24, -12, -6, 1, 0.44, -14, -6, 1, 0.64, 12, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.24, -14, -8, 1, 0.44, -17, -8, 1, 0.64, 15, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.24, 14, 12, 0, 0.44, 16, 12, 0, 0.64, -10, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.24, 12, 14, 0, 0.44, 14, 14, 0, 0.64, -16, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.24, 18, 6, 22, 0.44, 20, 6, 24, 0.64, -8, 6, -2, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.24, -128, 22, -12, 0.44, -142, 22, -14, 0.64, 26, 18, 22, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.24, -30, -22, 6, 0.44, -18, -22, 6, 0.64, -34, -22, 6, 1, -78, -22, 6],
    clavicleL: [0, 2, -6, -10, 0.24, 18, -6, -22, 0.44, 20, -6, -24, 0.64, -8, -6, 2, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.24, -130, -20, 14, 0.44, -144, -20, 16, 0.64, 24, -16, -22, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.24, -28, 20, -4, 0.44, -16, 20, -4, 0.64, -32, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.24, -34, 10, 7, 0.44, -40, 10, 7, 0.64, -6, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.24, 48, 0, -1, 0.44, 54, 0, -1, 0.64, 14, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.24, -36, -16, -9, 0.44, -42, -16, -9, 0.64, -10, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.24, 52, 0, 1, 0.44, 58, 0, 1, 0.64, 18, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.24, 0, 0.16, 0.14, 0.44, 0, 0.26, 0.40, 0.64, 0, 0, 0.66, 1, 0, 0, 0.78] },
});

/* CH1 — Rising Talon. Both bits driven upward from a crouch. The launcher. */
clip('kapak_ch1', 0.72, {
  blend: 0.10, rm: 0.5, tags: 'attack',
  events: [ev(0.14, 'voice', { tag: 'effort' }), ev(0.30, 'swoosh', { power: 1.1 }),
    ev(0.36, 'hitboxOn', { arc: 72, reach: 1.6, height: 2.4, power: 1.2, dmg: 1.9, launch: true }),
    ev(0.52, 'hitboxOff'), ev(0.39, 'sfx', { name: 'launcher' }), ev(0.86, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.26, 20, -14, 0, 0.5, -14, -14, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.26, 15, -5, 0, 0.5, -11, -5, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.26, 13, -6, 1, 0.5, -13, -6, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.26, 16, -8, 1, 0.5, -15, -8, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.26, -8, 12, 0, 0.5, 15, 12, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.26, -16, 14, 0, 0.5, 16, 14, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.26, -6, 6, 4, 0.5, 16, 6, 22, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.26, 30, 16, 24, 0.5, -122, 24, -10, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.26, -36, -22, 6, 0.5, -24, -22, 6, 1, -78, -22, 6],
    clavicleL: [0, 2, -6, -10, 0.26, -6, -6, -4, 0.5, 16, -6, -22, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.26, 28, -14, -26, 0.5, -124, -20, 12, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.26, -34, 20, -4, 0.5, -22, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.26, -40, 10, 7, 0.5, -6, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.26, 56, 0, -1, 0.5, 12, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.26, -44, -16, -9, 0.5, -10, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.26, 60, 0, 1, 0.5, 16, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.26, 0, -0.10, 0.06, 0.5, 0, 0.04, 0.28, 1, 0, 0, 0.42] },
});

/* CH2 — Shield Split. Slow, telegraphed, and UNBLOCKABLE: the answer to a man
 * who has decided to stand behind his guard. Everything else in the game can
 * be blocked, so this is the move the whole weapon is built to set up. */
clip('kapak_ch2', 1.00, {
  blend: 0.12, rm: 0.4, tags: 'attack',
  events: [ev(0.08, 'voice', { tag: 'shout' }), ev(0.46, 'swoosh', { power: 1.5 }),
    ev(0.54, 'hitboxOn', { arc: 48, reach: 1.7, height: 1.5, power: 1.7, dmg: 3.2, gb: true }),
    ev(0.70, 'hitboxOff'), ev(0.57, 'sfx', { name: 'guard_break' }), ev(0.92, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.3, -14, -20, -2, 0.52, -18, -20, -2, 0.7, 18, -6, 2, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.3, -10, -11, 0, 0.52, -13, -11, 0, 0.7, 14, -1, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.3, -13, -12, 1, 0.52, -15, -12, 1, 0.7, 13, -2, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.3, -15, -14, 1, 0.52, -18, -14, 1, 0.7, 16, -3, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.3, 15, 14, 0, 0.52, 17, 14, 0, 0.7, -12, 10, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.3, 14, 16, 0, 0.52, 16, 16, 0, 0.7, -18, 12, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.3, 20, 6, 24, 0.52, 22, 6, 26, 0.7, -10, 6, -4, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.3, -134, 20, -14, 0.52, -148, 20, -16, 0.7, 30, 16, 24, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.3, -26, -22, 6, 0.52, -14, -22, 6, 0.7, -38, -22, 6, 1, -78, -22, 6],
    clavicleL: [0, 2, -6, -10, 0.3, 20, -6, -24, 0.52, 22, -6, -26, 0.7, -10, -6, 4, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.3, -136, -18, 16, 0.52, -150, -18, 18, 0.7, 28, -14, -24, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.3, -24, 20, -4, 0.52, -12, 20, -4, 0.7, -36, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.3, -30, 10, 7, 0.52, -36, 10, 7, 0.7, -8, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.3, 44, 0, -1, 0.52, 50, 0, -1, 0.7, 16, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.3, -32, -16, -9, 0.52, -38, -16, -9, 0.7, -12, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.3, 48, 0, 1, 0.52, 54, 0, 1, 0.7, 20, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.3, 0, 0.08, 0.04, 0.52, 0, 0.12, 0.12, 0.7, 0, 0, 0.32, 1, 0, 0, 0.38] },
});

/* CH3 — Whirl of Two. A full turn with both bits out. A 360 that reaches
 * barely past arm's length, which is what a 360 costs on this weapon. */
clip('kapak_ch3', 1.06, {
  blend: 0.12, rm: 1.2, tags: 'attack',
  events: [ev(0.10, 'voice', { tag: 'shout' }), ev(0.26, 'swoosh', { power: 1.2 }),
    ev(0.32, 'hitboxOn', { arc: 180, reach: 1.8, height: 1.3, power: 1.1, dmg: 1.7 }),
    ev(0.82, 'hitboxOff'), ev(0.50, 'swoosh', { power: 1.2 }),
    ev(0.36, 'sfx', { name: 'swing_huge' }), ev(0.98, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.3, 8, -16, 0, 0.6, 8, -16, 0, 0.86, 6, -12, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.3, 6, -6, 0, 0.6, 6, -6, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.3, 3, -7, 1, 0.6, 3, -7, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.3, 2, -9, 1, 0.6, 2, -9, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.3, 7, 13, 0, 0.6, 7, 13, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.3, -3, 15, 0, 0.6, -3, 15, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.3, 4, 6, 24, 0.6, 4, 6, 24, 0.86, 3, 6, 16, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.3, -18, 34, 48, 0.6, -18, 34, 48, 0.86, -17, 26, 26, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.3, -20, -22, 6, 0.6, -20, -22, 6, 1, -78, -22, 6],
    clavicleL: [0, 2, -6, -10, 0.3, 4, -6, -24, 0.6, 4, -6, -24, 0.86, 3, -6, -16, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.3, -20, -32, -48, 0.6, -20, -32, -48, 0.86, -27, -24, -26, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.3, -18, 20, -4, 0.6, -18, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.3, -16, 10, 7, 0.6, -12, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.3, 26, 0, -1, 0.6, 20, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.3, -17, -16, -9, 0.6, -21, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.3, 25, 0, 1, 0.6, 31, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.3, 0, 0, 0.30, 0.6, 0, 0, 0.66, 1, 0, 0, 1.10] },
});

/* CH4 — Beak Drop. Both back-spikes overhead and straight down. A wall slam:
 * it puts a man on the floor rather than across the field. */
clip('kapak_ch4', 1.04, {
  blend: 0.12, rm: 0.9, tags: 'attack',
  events: [ev(0.10, 'voice', { tag: 'shout' }), ev(0.44, 'swoosh', { power: 1.4 }),
    ev(0.52, 'hitboxOn', { arc: 64, reach: 1.6, height: 1.6, power: 1.5, dmg: 2.6 }),
    ev(0.68, 'hitboxOff'), ev(0.55, 'sfx', { name: 'impact_slam' }),
    ev(0.58, 'foot', { side: 'L' }), ev(0.94, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.3, -16, -16, 0, 0.5, -18, -16, 0, 0.68, 19, -10, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.3, -12, -6, 0, 0.5, -14, -6, 0, 0.68, 15, -3, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.3, -14, -7, 1, 0.5, -16, -7, 1, 0.68, 14, -4, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.3, -16, -9, 1, 0.5, -19, -9, 1, 0.68, 17, -5, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.3, 16, 13, 0, 0.5, 18, 13, 0, 0.68, -13, 11, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.3, 15, 15, 0, 0.5, 17, 15, 0, 0.68, -19, 13, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.3, 19, 6, 20, 0.5, 21, 6, 22, 0.68, -9, 6, -2, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.3, -140, 18, -8, 0.5, -152, 18, -10, 0.68, 32, 14, 20, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.3, -20, -22, 6, 0.5, -10, -22, 6, 0.68, -40, -22, 6, 1, -78, -22, 6],
    clavicleL: [0, 2, -6, -10, 0.3, 19, -6, -20, 0.5, 21, -6, -22, 0.68, -9, -6, 2, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.3, -142, -16, 10, 0.5, -154, -16, 12, 0.68, 30, -12, -20, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.3, -18, 20, -4, 0.5, -8, 20, -4, 0.68, -38, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.3, -26, 10, 7, 0.5, -32, 10, 7, 0.68, -6, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.3, 40, 0, -1, 0.5, 46, 0, -1, 0.68, 14, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.3, -28, -16, -9, 0.5, -34, -16, -9, 0.68, -11, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.3, 44, 0, 1, 0.5, 50, 0, 1, 0.68, 19, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.3, 0, 0.12, 0.10, 0.5, 0, 0.18, 0.22, 0.68, 0, 0, 0.52, 1, 0, 0, 0.62] },
});

/* CH5 — Inside Storm. Six blows without leaving the spot, the longest active
 * window on the weapon. It does not travel: there is nothing to chase, because
 * anything worth hitting is already within arm's reach. */
clip('kapak_ch5', 1.22, {
  blend: 0.12, rm: 0.5, tags: 'attack',
  events: [ev(0.08, 'voice', { tag: 'shout' }), ev(0.20, 'swoosh', { power: 1.0 }),
    ev(0.26, 'hitboxOn', { arc: 130, reach: 1.7, height: 1.3, power: 1.0, dmg: 1.4 }),
    ev(0.94, 'hitboxOff'), ev(0.42, 'swoosh', { power: 1.0 }),
    ev(0.64, 'swoosh', { power: 1.0 }), ev(0.82, 'swoosh', { power: 1.1 }),
    ev(0.30, 'sfx', { name: 'impact_slash' }), ev(1.12, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.22, 6, -22, -2, 0.4, 6, -4, 2, 0.58, 6, -22, -2, 0.76, 6, -4, 2, 0.92, 6, -16, 0, 1, 4, -14, 0],
    spine1: [0, 3, -5, 0, 0.22, 5, -13, 0, 0.4, 5, 4, 0, 0.58, 5, -13, 0, 0.76, 5, 4, 0, 1, 3, -5, 0],
    spine2: [0, -1, -6, 1, 0.22, 2, -13, 1, 0.4, 2, 3, 1, 0.58, 2, -13, 1, 0.76, 2, 3, 1, 1, -1, -6, 1],
    chest: [0, -3, -8, 1, 0.22, 0, -16, 1, 0.4, 0, 5, 1, 0.58, 0, -16, 1, 0.76, 0, 5, 1, 1, -3, -8, 1],
    neck: [0, 6, 12, 0, 0.22, 8, 16, 0, 0.4, 8, 4, 0, 0.58, 8, 16, 0, 1, 6, 12, 0],
    head: [0, -4, 14, 0, 0.22, -2, 18, 0, 0.4, -2, 4, 0, 0.58, -2, 18, 0, 1, -4, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.22, 14, 6, 20, 0.4, -4, 6, 0, 0.58, 14, 6, 20, 0.76, -4, 6, 0, 1, 2, 6, 10],
    upperArmR: [0, -16, 20, 6, 0.22, -86, 28, -8, 0.4, 18, 12, 24, 0.58, -84, 28, -8, 0.76, 16, 12, 24, 1, -16, 20, 6],
    foreArmR: [0, -78, -22, 6, 0.22, -46, -22, 6, 0.4, -26, -22, 6, 0.58, -46, -22, 6, 1, -78, -22, 6],
    clavicleL: [0, 2, -6, -10, 0.22, -4, -6, 0, 0.4, 14, -6, -20, 0.58, -4, -6, 0, 0.76, 14, -6, -20, 1, 2, -6, -10],
    upperArmL: [0, -34, -16, -8, 0.22, 14, -12, -24, 0.4, -88, -24, 8, 0.58, 16, -12, -24, 0.76, -86, -24, 8, 1, -34, -16, -8],
    foreArmL: [0, -62, 20, -4, 0.22, -28, 20, -4, 0.4, -44, 20, -4, 0.58, -28, 20, -4, 1, -62, 20, -4],
    thighL: [0, -14, 10, 7, 0.22, -10, 10, 7, 0.58, -18, 10, 7, 1, -14, 10, 7],
    shinL: [0, 22, 0, -1, 0.22, 18, 0, -1, 0.58, 28, 0, -1, 1, 22, 0, -1],
    thighR: [0, -19, -16, -9, 0.22, -23, -16, -9, 0.58, -15, -16, -9, 1, -19, -16, -9],
    shinR: [0, 29, 0, 1, 0.22, 33, 0, 1, 0.58, 24, 0, 1, 1, 29, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.22, 0, 0.01, 0.06, 0.58, 0, 0.01, 0.16, 1, 0, 0, 0.30] },
});

/* MUSOU — Garuda Descends. A storm of alternating blows that ends on both
 * beaks driven down together. Unblockable, like the rest of the weapon's
 * finishers, and the only one that is also radial. */
clip('kapak_musou', 1.34, {
  blend: 0.14, rm: 1.0, tags: 'attack',
  events: [ev(0.06, 'voice', { tag: 'musou' }), ev(0.18, 'swoosh', { power: 1.3 }),
    ev(0.24, 'hitboxOn', { arc: 180, reach: 1.9, height: 1.5, power: 1.3, dmg: 1.8 }),
    ev(0.96, 'hitboxOff'), ev(0.44, 'swoosh', { power: 1.3 }),
    ev(0.70, 'swoosh', { power: 1.4 }),
    ev(1.02, 'hitboxOn', { arc: 180, reach: 2.4, height: 2.0, power: 2.0, dmg: 3.6, launch: true, gb: true }),
    ev(1.16, 'hitboxOff'), ev(1.04, 'sfx', { name: 'impact_musou' }), ev(1.26, 'cancel')],
  R: on(GUARD, {
    hips: [0, 4, -14, 0, 0.2, 7, -20, -2, 0.42, 7, -6, 2, 0.64, 7, -20, -2, 0.86, -15, -14, 0, 1, 10, -14, 0],
    spine1: [0, 3, -5, 0, 0.2, 6, -12, 0, 0.42, 6, 3, 0, 0.64, 6, -12, 0, 0.86, -12, -5, 0, 1, 8, -5, 0],
    spine2: [0, -1, -6, 1, 0.2, 3, -12, 1, 0.42, 3, 2, 1, 0.64, 3, -12, 1, 0.86, -14, -6, 1, 1, 6, -6, 1],
    chest: [0, -3, -8, 1, 0.2, 1, -15, 1, 0.42, 1, 4, 1, 0.64, 1, -15, 1, 0.86, -16, -8, 1, 1, 8, -8, 1],
    neck: [0, 6, 12, 0, 0.2, 8, 15, 0, 0.42, 8, 5, 0, 0.86, 16, 12, 0, 1, 2, 12, 0],
    head: [0, -4, 14, 0, 0.2, -2, 17, 0, 0.42, -2, 5, 0, 0.86, 15, 14, 0, 1, -12, 14, 0],
    clavicleR: [0, 2, 6, 10, 0.2, 14, 6, 20, 0.42, -4, 6, 0, 0.64, 14, 6, 20, 0.86, 20, 6, 24, 1, -6, 6, 0],
    upperArmR: [0, -16, 20, 6, 0.2, -88, 28, -8, 0.42, 18, 12, 24, 0.64, -86, 28, -8, 0.86, -146, 20, -14, 1, 30, 16, 22],
    foreArmR: [0, -78, -22, 6, 0.2, -44, -22, 6, 0.42, -26, -22, 6, 0.64, -44, -22, 6, 0.86, -16, -22, 6, 1, -40, -22, 6],
    clavicleL: [0, 2, -6, -10, 0.2, -4, -6, 0, 0.42, 14, -6, -20, 0.64, -4, -6, 0, 0.86, 20, -6, -24, 1, -6, -6, 0],
    upperArmL: [0, -34, -16, -8, 0.2, 14, -12, -24, 0.42, -90, -24, 8, 0.64, 16, -12, -24, 0.86, -148, -18, 16, 1, 28, -14, -22],
    foreArmL: [0, -62, 20, -4, 0.2, -26, 20, -4, 0.42, -42, 20, -4, 0.64, -26, 20, -4, 0.86, -14, 20, -4, 1, -38, 20, -4],
    thighL: [0, -14, 10, 7, 0.2, -10, 10, 7, 0.64, -18, 10, 7, 0.86, -34, 10, 7, 1, -12, 10, 7],
    shinL: [0, 22, 0, -1, 0.2, 18, 0, -1, 0.64, 28, 0, -1, 0.86, 48, 0, -1, 1, 18, 0, -1],
    thighR: [0, -19, -16, -9, 0.2, -23, -16, -9, 0.64, -15, -16, -9, 0.86, -36, -16, -9, 1, -15, -16, -9],
    shinR: [0, 29, 0, 1, 0.2, 33, 0, 1, 0.64, 24, 0, 1, 0.86, 52, 0, 1, 1, 24, 0, 1],
  }),
  T: { root: [0, 0, 0, 0, 0.2, 0, 0.01, 0.16, 0.64, 0, 0.01, 0.62, 0.86, 0, 0.14, 0.86, 1, 0, 0, 1.00] },
});

/* =========================================================================
 *  COMPILE
 * ========================================================================= */

function build(def) {
  const rot = new Array(BONE_COUNT).fill(null);
  const pos = new Array(BONE_COUNT).fill(null);
  const loop = !!def.loop;
  for (const b in (def.R || {})) {
    const i = BONE_INDEX[b];
    if (i === undefined) throw new Error('clip ' + def.name + ': unknown bone ' + b);
    rot[i] = compile(def.R[b], D, loop);
  }
  for (const b in (def.T || {})) {
    const i = BONE_INDEX[b];
    if (i === undefined) throw new Error('clip ' + def.name + ': unknown bone ' + b);
    pos[i] = compile(def.T[b], 1, loop);
  }
  const events = (def.events || []).map(e => Object.assign({}, e, { t: e.t * def.dur })).sort((a, b) => a.t - b.t);
  let rm = def.rm ?? 0;
  if (rm === 0 && pos[0]) { const p = pos[0]; rm = p.v[(p.n - 1) * 3 + 2] - p.v[2]; }
  return {
    name: def.name, dur: def.dur, loop, blend: def.blend ?? 0.16,
    speed: def.speed || 0, rm, tags: def.tags || '', fps: def.fps || 0,
    // gait metadata (locomotion only): how long the foot stays planted and how
    // far it travels while it is. animation.js blends the ladder on these.
    contact: def.contact || 0, excursion: def.excursion || 0,
    rot, pos, events: Object.freeze(events),
    hasPos: pos.some(Boolean),
  };
}

export const CLIPS = DEFS.map(build);
export const CLIP_NAMES = CLIPS.map(c => c.name);
export const CLIP_INDEX = {};
CLIP_NAMES.forEach((n, i) => { CLIP_INDEX[n] = i; });
export const CLIP_COUNT = CLIPS.length;

/* --------------------------------------------------------------- calibrate
 * gait.js authors the ankle path, but the keys it emits are re-interpolated by
 * the runtime spline and the pelvis carries a little twist and roll the planar
 * solver ignores, so the excursion that actually reaches the screen is a per
 * cent or two off the requested one. Measure the real thing — through the
 * exact sampling path the game uses, on the canonical 1.80 m rig — and publish
 * THAT as `clip.excursion`. animation.js divides by it to get the playback
 * rate, so this is the number that decides whether the foot slides.
 */
{
  const eul = new Float32Array(BONE_COUNT * 3), pos = new Float32Array(BONE_COUNT * 3);
  const q = makeQ(), world = makeM();
  const ankleZ = (clip, t, bone) => {
    sampleClip(clip, t, eul, pos);
    eulerToQuat(eul, q);
    poseToMatrices(q, pos, RIG, world, null);
    return world[bone * 12 + 11];
  };
  const L = BONE_INDEX.footL, R = BONE_INDEX.footR;
  for (const c of CLIPS) {
    if (!(c.contact > 0)) continue;
    // left stance runs [0, contact], right [0.5, 0.5+contact]; average the two
    // so neither leg carries the whole of the mirror's residual
    const eL = ankleZ(c, 0, L) - ankleZ(c, c.contact, L);
    const eR = ankleZ(c, 0.5, R) - ankleZ(c, 0.5 + c.contact, R);
    c.excursion = +((eL + eR) * 0.5).toFixed(4);
    c.speed = c.excursion / c.contact / c.dur;
  }
}

/** Handy id constants so hot code never does a string lookup. */
export const C = {};
for (const n of CLIP_NAMES) C[n.replace(/_(\w)/g, (m, a) => a.toUpperCase())] = CLIP_INDEX[n];

/** The locomotion ladder, slowest first. Driven by entities.speed. */
export const LOCO = ['idle', 'walk', 'run', 'sprint'].map(n => CLIP_INDEX[n]);
/** Standing stances a unit may be given instead of LOCO[0]. Same contract as
 *  `idle`: looping, ground-planted, blends into `walk`. */
export const IDLES = ['idle', 'idle_b', 'idle_c'].map(n => CLIP_INDEX[n]);
export const LOCO_SPEED = LOCO.map(i => CLIPS[i].speed);

/**
 * Sample a clip into `outEul` (radians, 3 per bone) and `outPos`
 * (metres, 3 per bone; zero where the clip has no position track).
 */
export function sampleClip(clip, tNorm, outEul, outPos) {
  const rot = clip.rot, pos = clip.pos;
  for (let b = 0; b < BONE_COUNT; b++) {
    const o = b * 3;
    const r = rot[b];
    if (r) sampleChannel(r, tNorm, outEul, o);
    else { outEul[o] = 0; outEul[o + 1] = 0; outEul[o + 2] = 0; }
    const p = pos[b];
    if (p) sampleChannel(p, tNorm, outPos, o);
    else { outPos[o] = 0; outPos[o + 1] = 0; outPos[o + 2] = 0; }
  }
  shoulderClearance(outEul);
}
